/**
 * The running game: owns terrain, player, camera, scenery, actors, progression
 * and the per-frame translation from game state into renderer draw calls and
 * shading parameters.
 */

import { Camera } from './camera';
import { Player, type Contact } from './player';
import { Terrain } from './terrain';
import {
  LAYERS, laneAtZ, laneZ, layerDefocus, layerLift, layerVisibility, toField,
} from './mesher';
import { Actors, type ActorCtx, type Enemy, type Pickup, type Station } from './entities';
import { Octopus } from './octopus';
import { Sonar } from './sonar';
import { BaitBall } from './shoal';
import { propsForChunk, type Prop } from './props';
import {
  BANDS, BREACH, CAMERA, CARGO, ENEMY, GRAPPLE, LIGHT, LOOT, NITRO, PICKUP_DEF, FISH_FLEX, SCORE,
  SHOAL_TINTS, SONAR, SUB, WEAPONS, WORLD, type UpgradeId,
  DEFAULT_SUN, SUN_PRESETS, sunUnderwater, type SunPreset, type SunState,
} from './config';
import {
  BAIT_BALLS, EVENT_HINTS, FINALE, HINTS, SPAWN, STATIONS,
  POCKETS, gateNear, profileY, regionAt, type Mood, type Pocket,
} from './level';
import {
  carve, craterState, density, featuresFor, floorAt, resetCraters, restoreCraters, seabedY,
} from './field';
import { driveTow, newTowLine, solveTow, type TowHooks } from './cargo';
import { buildSubLamp, buildSubModel, LAMP_PIVOT } from '../render/subModel';
import {
  buildSub2Parts, mat4Mul, sub2Local, sub2PitchJoint, sub2Stretch, type Sub2PartMesh,
} from '../render/subModel2';
import { CHASSIS, PATHFINDER, type Chassis, type ChassisId } from './chassis';
import { CampaignRules } from './modes';
import { survivalHurt, survivalKill } from './survival';
import { blankProfile, writeProfile, type Profile } from './profile';
import type { ModeRules } from './mode';
import { emptyLevels, maxLevel, statsFor, type Levels, type Stats } from './upgrades';
import {
  clearSave, isHullModule, loadSettings, writeSave, SAVE_VERSION, type SaveData,
} from './save';
import { clamp, compose, damp, dampAngle, hyp, invert, mat4, rotateOffset, type Mat4 } from '../core/math';

/** Cargo shape -> the mesh slot it is drawn from. */
const CARGO_MESH = {
  crate: 'crate', drum: 'drum', core: 'core', slab: 'slab', pod: 'pod',
} as const;

import type { Input } from '../core/input';
import { SKY_SUN_REF } from '../render/renderer';
import { MINERAL_TYPES, mineralMeshes, mineralSlot } from '../render/mineralModel';
import { MINERAL_MESHES } from '../render/mineralMesh';
import {
  HOLD_CAPACITY, ORE, RESERVE_PER_SHARD, depositName, secondsPerShard,
} from './ore';
import type { Renderer, FrameParams } from '../render/renderer';
import type { Audio } from '../audio/audio';
import type { Quality } from '../core/quality';
import {
  buildBullet, buildCableLink, buildCanister, buildCoral, buildCrate, buildDebris, buildAegisShell, buildDray, buildHook,
  buildDreadnought, buildDrone,
  buildLed, buildDrum, buildCore, buildSlab, buildPod, cargoShape,
  buildBackdrop,
  buildHunter, buildKelp, buildMine, buildMonolith, buildJellyfish, jellySpecies,
  buildProbe,
  JELLY_SPECIES, buildBloom, BLOOM_SPECIES, buildOctoHead, buildPickup, buildProp,
  buildRock, buildRuin, buildSeeker, buildSentinel, buildGate, buildGateShard, buildGateVeil, buildStation, buildSub,
  buildCrown, buildTentacleSeg, buildTorpedo, buildTurret,
  buildVent, buildWreck, buildLurker, buildFishSpecies, FISH_SPECIES,
} from '../render/models';
import { MAT, meshHalfExtents } from '../render/mesh';
import { HAB_GROUP_SUFFIX, type HabKitMeshes } from '../render/habKitMesh';
import { HAB_MODULES } from '../render/habitat';
import { TEST_BASES } from './habitats';

export interface Toast {
  text: string;
  sub: string;
  time: number;
  /** Accent colour, so a reward and a warning do not look the same. */
  color: string;
}

const T = WEAPONS.torpedo;
const CH = WEAPONS.charge;

/** How fast the sub has to be going under nitro before ramming counts. */
const RAM_SPEED = 24;
/**
 * Rock thinner than this breaks when the hull leans on it.
 *
 * A spur one mesh cell across — 0.62 m — is solid to the analytic field and
 * very nearly invisible on screen, so a player pinned by one has nothing to
 * aim at and, in a pocket, no room to build the ramming speed that used to be
 * the only way through. Nitro reaches further, which makes it the tool for
 * getting out of somewhere rather than only for going fast.
 */
/**
 * How tall each ore cluster is as modelled, by mesh name.
 *
 * Read once so a node's point light can be put at the crystal's own height
 * rather than at its origin — the origin is at the base, which the placement
 * deliberately buries, and a light inside a boulder lights only the inside of
 * a boulder.
 */
const ORE_HEIGHT = new Map(MINERAL_MESHES.map((m) => [m.name, m.size[1]]));

/**
 * A mineral's authored linear colour, as something a 2D canvas can stroke.
 *
 * Gamma first, because the manifest's numbers are linear and the HUD is not —
 * copper's 0.10 blue channel is 0.35 on screen, and skipping the transform
 * turns every ore into a more saturated version of itself. Then lifted toward
 * white, because these are surface albedos and a mark on a dark display has to
 * be legible before it is accurate.
 */
function oreHudRgb(c: readonly [number, number, number]): [number, number, number] {
  const ch = (v: number) => {
    const srgb = Math.pow(Math.max(0, Math.min(1, v)), 1 / 2.2);
    return 0.35 + srgb * 0.65;
  };
  return [ch(c[0]), ch(c[1]), ch(c[2])];
}

function oreHudColour(c: readonly [number, number, number]): string {
  const ch = (v: number) => {
    const srgb = Math.pow(Math.max(0, Math.min(1, v)), 1 / 2.2);
    return Math.round(255 * (0.35 + srgb * 0.65));
  };
  return `rgb(${ch(c[0])}, ${ch(c[1])}, ${ch(c[2])})`;
}
/** How close the boat has to be to a gate for it to take. */
/** The aegis powerup's own colour, which no module may borrow. */
const AEGIS_TINT: [number, number, number] = [0.34, 0.74, 1.0];

/**
 * THE MATERIAL CHART, and the reason it exists.
 *
 * Every lighting change in this renderer has been judged against a rock that is
 * also a normal map that is also a detail layer that is also fogged by depth.
 * When the screen-space normal was corrected, four separate effects moved at
 * once and the only report available was "the rocks lost some glow" — true, and
 * impossible to act on, because a rock is not a measurement.
 *
 * A row of spheres is. Each one is a single material with nothing else on it,
 * they cover every normal direction at once, and they ride with the boat so the
 * same eight balls can be walked from the surface to the abyss and through every
 * light in the game without the scene underneath them changing.
 *
 * MAT is a per-vertex tag, so a material is a mesh; `tint`, `emis` and the
 * per-instance specular amount are what separates two probes on the same one.
 */
/**
 * THE HABITAT'S STRUCTURAL STEEL, AND WHY IT IS NOT A PURE CONDUCTOR.
 *
 * This was metallic 1.0 — real stainless, and the reason the collars and
 * junctions stopped reacting to light at all. A conductor has NO diffuse term
 * by definition:
 *
 *     diffAlbedo = albedo * (1.0 - mMetalV)      // zero when metallic is 1
 *
 * so it is lit purely by what it reflects. At the surface that is fine. At
 * 78 m the environment probe is pinned at its 0.16 floor, which leaves a
 * conductor about a tenth of its F0 and nothing else — black, whatever the
 * searchlight does, because the lamp reaches it as diffuse and diffuse is what
 * was removed.
 *
 * 0.72 is the honest number for this object rather than a retreat. Submerged
 * structural steel is oxidised, painted and biofouled; a mirror finish belongs
 * on a machined fitting, not on a truss that has been in seawater. The 28
 * percent of diffuse that comes back is what lets the lamp and the ambient
 * dome touch it at all.
 *
 * Roughness up with it: 0.30 was a polish this surface does not have.
 */
const HAB_STEEL: [number, number, number, number] = [0.72, 0.38, 0.04, 1];

const PROBE_MESHES: { slot: string; mat: number }[] = [
  { slot: 'probePaint', mat: MAT.paint },
  { slot: 'probeMetal', mat: MAT.metal },
  { slot: 'probeRubber', mat: MAT.rubber },
  { slot: 'probeOrganic', mat: MAT.organic },
  { slot: 'probeLeaf', mat: MAT.leaf },
  { slot: 'probeGlass', mat: MAT.crystal },
];

interface Probe {
  slot: string;
  label: string;
  tint: [number, number, number];
  emis: [number, number, number];
  /**
   * Per-instance specular amount — `emis.w`, which the shader reads as spec.
   *
   * 1.00 on every probe, and that is the point rather than an oversight: the
   * `pbr` triple below IS this material's whole description, so scaling its
   * highlight by a second number would double-count what `metallic` and
   * `roughness` already say. The shader used to force 1 here whenever a triple
   * was present; now it honours the field, so the field has to say 1.
   */
  spec: number;
  /** params2.y, the bioluminescence weight. */
  bio: number;
  /**
   * Metallic, roughness, dielectric F0. The chart is the first thing in the
   * game to drive these directly rather than inherit them from a slot, which is
   * the entire reason it can now show a mirror next to a brushed conductor when
   * both are the same material tag.
   */
  pbr?: [number, number, number];
}

/** Left to right, and the on-screen legend follows this order. */
const PROBES: Probe[] = [
  { slot: 'probePaint', label: 'PAINT', tint: [0.72, 0.13, 0.11],
    emis: [0, 0, 0], spec: 1.00, bio: 0,
    pbr: [0, 0.34, 0.05] },
  { slot: 'probeRubber', label: 'RUBBER', tint: [0.10, 0.11, 0.12],
    emis: [0, 0, 0], spec: 1.00, bio: 0,
    pbr: [0, 0.92, 0.03] },
  { slot: 'probeMetal', label: 'STEEL', tint: [0.55, 0.57, 0.60],
    emis: [0, 0, 0], spec: 1.00, bio: 0,
    pbr: [1, 0.42, 0.04] },
  // Same tag as the steel. What makes it chrome is the specular amount, which
  // is the one reflective property that IS per-instance.
  { slot: 'probeMetal', label: 'CHROME', tint: [0.95, 0.96, 1.00],
    emis: [0, 0, 0], spec: 1.00, bio: 0,
    pbr: [1, 0.05, 0.04] },
  // THE CHART WAS SHOWING A LAMP AND CALLING IT GLASS.
  //
  // This probe was `MAT.glow`, which is the emissive tag — a full Lambert wash
  // over a bright albedo, which is precisely the "broad flat interior" that
  // glass does not have, and it rendered as a porcelain ball. Meanwhile every
  // actual window in the game is `MAT.crystal`, whose gem terms cut the diffuse
  // to 4 percent and give it an interior, dispersion and a cleaved edge.
  //
  // So the chart was not showing a bad glass shader. It was showing a material
  // no window uses, on the one screen whose entire job is to answer "what does
  // this material look like" — which made it worse than no probe at all.
  { slot: 'probeGlass', label: 'GLASS', tint: [0.62, 0.86, 0.92],
    emis: [0.02, 0.05, 0.06], spec: 1.00, bio: 0,
    pbr: [0, 0.06, 0.08] },
  { slot: 'probeOrganic', label: 'ROCK', tint: [0.34, 0.31, 0.26],
    emis: [0, 0, 0], spec: 1.00, bio: 0,
    pbr: [0, 0.78, 0.04] },
  { slot: 'probeLeaf', label: 'LEAF', tint: [0.28, 0.52, 0.22],
    emis: [0, 0, 0], spec: 1.00, bio: 0,
    pbr: [0, 0.60, 0.03] },
  { slot: 'probeOrganic', label: 'BIO', tint: [0.12, 0.30, 0.32],
    emis: [0.20, 1.00, 0.85], spec: 1.00, bio: 1,
    pbr: [0, 0.45, 0.03] },
];

const GATE_RADIUS = 7.5;
/** How far a gate stands off the seabed it is planted in. */
const GATE_LIFT = 5.0;
/** Seconds before a gate will take the boat again — both ends share it. */
const GATE_COOLDOWN = 2.2;

const CRUSH_THIN = 2.6;
const CRUSH_NITRO = 5.0;
/** Nudge needed to break thin rock without nitro. Barely more than drifting. */
const CRUSH_SPEED = 2.2;

export class World {
  readonly terrain: Terrain;
  readonly player = new Player();
  readonly camera = new Camera();
  actors = new Actors();
  readonly octopus = new Octopus();
  readonly sonar = new Sonar();
  /** Decorative shoals. Hundreds of fish each, closed-form, no AI. */
  baitBalls: BaitBall[] = BAIT_BALLS.map((b) => new BaitBall({
    id: b.id, x: b.x, y: profileY(b.x) + b.lift,
    radius: b.radius, height: b.height, count: b.count, spin: b.spin, sheen: b.sheen,
    mode: b.mode, glow: b.glow, range: b.range,
  }));

  time = 0;
  playTime = 0;
  damageFlash = 0;
  rewardFlash = 0;
  region = regionAt(0);
  /** 0 = calm exploration, 1 = boss. Drives the music mix (PRD §29). */
  intensity = 0;
  mood: Mood = 'calm';

  // Weapons + targeting (PRD §12/§13)
  target: Enemy | null = null;
  targetIsBoss = false;
  lockProgress = 0;
  private cannonCooldown = 0;
  private torpedoCooldown = 0;
  private chargeCooldown = 0;

  toast: Toast | null = null;
  private toastQueue: Toast[] = [];
  private bossBanner = 0;
  private bossBannerText = '';

  // --- progression ---------------------------------------------------------
  /**
   * The account. Ownership — boats, kit, banked credits — lives here rather
   * than on the run, so a hull earned in one mode is flyable in another. See
   * `profile.ts`; nothing below stores a second copy of any of it.
   */
  profile: Profile;

  /**
   * Structural modules per hull. Hull, shield and engine belong to the boat
   * they are fitted to, because they *are* the boat. A view onto the profile's
   * fleet, not a copy of it.
   */
  get fleet(): Map<ChassisId, Levels> {
    const m = new Map<ChassisId, Levels>();
    for (const f of this.profile.fleet) m.set(f.id as ChassisId, f.levels);
    return m;
  }

  /**
   * Owned kit — cannon, torpedo, sonar, lamp. Refits across the whole fleet, so
   * buying a second boat never means re-earning your gun. That split is the
   * reason a second hull is a different tool rather than a demotion.
   */
  get equipment(): Levels { return this.profile.equipment; }
  get hulls(): Set<ChassisId> { return new Set(this.profile.hulls as ChassisId[]); }

  /** Structural levels for one hull, creating the record if it is a new boat. */
  private structureOf(id: ChassisId): Levels {
    let f = this.profile.fleet.find((q) => q.id === id);
    if (!f) { f = { id, levels: emptyLevels() }; this.profile.fleet.push(f); }
    return f.levels;
  }

  /**
   * What the active boat is actually running: its own structure, plus whatever
   * kit is owned. Composed rather than stored, so there is exactly one truth.
   */
  get levels(): Levels {
    return this.levelsFor(this.player.chassis.id);
  }

  /**
   * What a *given* hull is running. Explicitly parameterised, because the
   * getter above reads the ACTIVE boat and a hull swap has to compute the
   * incoming boat's stats before it becomes active — reading `levels` there
   * hands the new hull the old one's structural modules, which arrives as a
   * stock Dray somehow having 225 hull points.
   */
  levelsFor(id: ChassisId): Levels {
    const own = this.profile.fleet.find((f) => f.id === id)?.levels ?? emptyLevels();
    return {
      // Bolted to the hull, and therefore owned per hull: plating, the field
      // emitters, the drive and its burner are all part of the boat.
      hull: own.hull, shield: own.shield, engine: own.engine,
      nitro: own.nitro, ablative: own.ablative, warding: own.warding,
      winch: own.winch,
      // Carried between hulls: a gun is a gun.
      cannon: this.equipment.cannon, torpedo: this.equipment.torpedo,
      sonar: this.equipment.sonar, lamp: this.equipment.lamp,
    };
  }

  /** Where a module's level is kept: on this boat, or in the kit locker. */
  private ledger(id: UpgradeId): Levels {
    // Through `structureOf`, never through the `fleet` getter — that getter
    // builds a fresh Map every call, so `fleet.set(...)` would write into a
    // throwaway and a bought hull plate would silently not exist.
    // `isHullModule` rather than a list written out here. This test and the one
    // in `levelsFor` are the two halves of the same decision, and when they were
    // written separately they disagreed for three modules — see HULL_MODULES.
    if (isHullModule(id)) return this.structureOf(this.player.chassis.id);
    return this.profile.equipment;
  }
  salvage = 0;
  seenPois = new Set<string>();
  activatedStations = new Set<string>();
  defeatedBosses = new Set<string>();
  takenPickups = new Set<string>();
  clearedEnemies = new Set<string>();
  /** Onboarding lines already shown. Persisted, so they never repeat. */
  seenHints = new Set<string>();
  /** Set by the shell so hints name the control the player actually has. */
  touchControls = false;
  lastStation = '';
  reachedX = 0;
  victory = false;
  /** Set for a beat after a death, while the wreck sinks. */
  deathTimer = 0;
  /** Station the player is currently able to dock with. */
  dockable: Station | null = null;
  /** Set when a station activates itself, so the shell can open the refit. */
  pendingRefit = false;

  /**
   * The rules this world runs under. Everything mode-specific in the update
   * loop goes through `rules.tick`, and nothing else in `game/` asks what mode
   * it is — a mode that declines a feature declines it by supplying no content
   * for it, not by being tested for.
   */
  rules: ModeRules;

  /** The grapple. One line, one load — see `game/cargo.ts`. */
  tow = newTowLine();
  /** Nearest free cargo within reach, or null. HUD only. */
  nearCargo: Pickup | null = null;

  /**
   * Rolling per-phase CPU cost in milliseconds, for the F3 readout.
   *
   * Smoothed hard on purpose. `performance.now` has a 0.1 ms quantum and every
   * one of these phases runs well under that on a good frame, so an
   * instantaneous reading is quantisation noise — docs/NEXT.md records that
   * several earlier "optimisations" here were measuring nothing at all. A long
   * EMA over hundreds of frames is the only honest way to read a sub-quantum
   * cost, and the numbers only mean anything after standing still for a second.
   */
  /**
   * `render` covers the whole `renderer.render` call and was missing entirely:
   * the shell closed the `draw` timer before it and opened `hud` after it, so
   * every millisecond of instance upload, command encoding and queue submit
   * fell in a gap no counter watched. That made the F3 CPU total structurally
   * unable to show a regression inside the renderer — and it is also where the
   * main thread blocks when the GPU is behind, which is the one reading that
   * separates "we are slow" from "we are waiting".
   */
  readonly cpu = { update: 0, frame: 0, draw: 0, render: 0, hud: 0, lamp: 0 };

  /** Per-frame draw accounting, for the F3 readout. */
  get drawStats() { return this.renderer.published; }

  /** Measured GPU time for the last reported frame, in ms. 0 if unavailable. */
  get gpuMs() { return this.renderer.gpuMs; }

  /** Measured GPU milliseconds per pass. Empty unless per-pass timing is on. */
  get gpuPass() { return this.renderer.gpuPass; }

  /** Whether the device offers timestamps at all, and whether any arrived. */
  get gpuTiming() {
    return {
      supported: this.renderer.tsSupported,
      timing: this.renderer.gpuPassTiming,
      samples: this.renderer.tsSamples,
    };
  }

  /**
   * Everything with a pool, live against allocated.
   *
   * On the F3 readout because "it degrades the longer I sit still" has now been
   * asked three times and answered by guesswork twice. Anything that grows
   * while the boat is parked shows up here as a live count climbing against a
   * total that does not — and if none of them move, the problem is not ours,
   * which is just as useful an answer.
   */
  /**
   * Why the nearest thing the player is looking at cannot be interacted with.
   *
   * "There are objects I cannot pick up" is a report with half a dozen possible
   * causes that all look identical from outside: it is in another lane, it is
   * cargo and wants the line rather than a fly-through, it is unrevealed and
   * waiting on a ping, it is heavier than the winch, or the boat is simply not
   * close enough yet. Guessing between those from a screenshot is how the last
   * three of these took two rounds each.
   *
   * So the game says it. Reads the nearest candidate of each kind and reports
   * the FIRST condition that fails, in the order the collector checks them.
   */
  /**
   * What the refit's turntable thinks it is doing, for when it is doing
   * nothing. Null rect, zero scale and a position off the frustum all look
   * identical from the outside — an empty viewport.
   */
  /** Forwarded from the renderer: is the sky an image or is it procedural. */
  get skyStats(): string { return this.renderer.skyStats; }

  get showcaseStats(): string {
    const s = this.showcase;
    if (!s) return 'SHOWCASE  none (no rect published)';
    const D = World.SHOW_DIST;
    const visH = 2 * D * Math.tan(CAMERA.fov / 2);
    const visW = visH * this.aspect;
    const scale = Math.min(
      (visW * s.wf * 0.88) / SUB.length,
      (visH * s.hf * 0.80) / 3.2,
    );
    return `SHOWCASE  uv ${s.u.toFixed(2)},${s.v.toFixed(2)}  wh ${s.wf.toFixed(2)},${s.hf.toFixed(2)}`
      + `  scale ${scale.toFixed(3)}  parts ${this.subParts ? this.subParts.length : 0}`
      + `  mesh ${this.player.chassis.mesh}`;
  }

  get reachStats(): string {
    const p = this.player;
    const lane = p.lane;
    let best: Pickup | null = null;
    let bestD = Infinity;
    for (const q of this.actors.pickups) {
      if (!q.alive) continue;
      const d = Math.hypot(q.x - p.x, q.y - p.y);
      if (d < bestD) { bestD = d; best = q; }
    }
    let why = 'nothing near';
    if (best) {
      const d = bestD.toFixed(1);
      why = best.lane !== lane
        ? `lane ${best.lane} vs you ${lane}`
        : !best.revealed
          ? 'unrevealed — needs a ping'
          : best.mass > 0
            ? (best.mass > p.stats.liftCap
              ? `cargo ${best.mass.toFixed(1)}t over lift ${p.stats.liftCap.toFixed(1)}t`
              : `cargo ${best.mass.toFixed(1)}t — use the line`)
            : bestD > LOOT.pickupRadius
              ? `${d} m, need ${LOOT.pickupRadius}`
              : 'collectable now';
      why = `${best.label} ${why}`;
    }
    return `LANE ${lane}  ·  ${why}`;
  }

  get poolStats() {
    const a = this.actors;
    let en = 0, pk = 0, pr = 0, fish = 0;
    for (const e of a.enemies) if (e.alive) en++;
    for (const q of a.pickups) if (q.alive) pk++;
    for (const q of a.projectiles) if (q.alive) pr++;
    for (const b of this.baitBalls) if (b.live) fish += b.fish.length;
    return {
      en, enTotal: a.enemies.length,
      pk, pkTotal: a.pickups.length,
      pr, prTotal: a.projectiles.length,
      fish, balls: this.baitBalls.length,
      // Three numbers per crater. Allocating here is fine: `poolStats` is only
      // read by the F3 readout, which is off unless someone is looking at it.
      craters: Math.round(craterState().length / 3),
    };
  }

  /** Live one-shot audio voices and their high-water mark, for the F3 readout. */
  get voiceStats() { return { live: this.audio.voices, peak: this.audio.voicePeak }; }

  /**
   * Which parallax lane the sub is heading for, and where it is between them.
   *
   * A lane is not scenery to be looked at — `cellMul` is a mesh resolution and
   * `warp` is a real horizontal offset, so lane 1 is the seabed 431.7 m along,
   * drawn coarsely. Flying into it is therefore a genuine change of place, not
   * an effect, which is why it moves the sub rather than the world.
   */
  laneTarget = 0;
  /** Eased z of the sub. The lane it is IN follows from this, via `laneAtZ`. */
  private subZ = laneZ(0);

  /** Send the sub to another lane. Returns the lane it is now heading for. */
  travelTo(lane: number): number {
    this.laneTarget = Math.max(0, Math.min(LAYERS.length - 1, lane));
    return this.laneTarget;
  }

  /**
   * How far the rock extends behind a contact, capped at `max`.
   *
   * Marched against the analytic field rather than guessed from the mesh,
   * because the two disagree exactly where this matters: a spur one cell wide
   * is solid to collision and close to invisible on screen, and being pinned by
   * something you cannot see is the worst version of being stuck. Returns `max`
   * when the rock keeps going, which reads as "this is a wall, not a spur".
   */
  private rockThickness(c: Contact, max: number): number {
    const L = LAYERS[Math.max(0, this.player.lane)]!;
    const feats = featuresFor(c.x + L.warp - max - 1, c.x + L.warp + max + 1);
    for (let t = 0.3; t <= max; t += 0.3) {
      // Against the outward normal is into the rock.
      const x = c.x - c.nx * t, y = c.y - c.ny * t;
      const f = toField(this.player.lane, x, y, this.fieldPt);
      if (density(f.x, f.y, feats) <= 0) return t;
    }
    return max;
  }

  /** Carve at a point given in DRAWN coordinates, in whatever lane holds it. */
  private carveHere(x: number, y: number, r: number) {
    const f = toField(Math.max(0, this.player.lane), x, y, this.fieldPt);
    const span = carve(f.x, f.y, r);
    if (!span) return null;
    // `carve` returns a span in FIELD x; the chunks to rebuild are addressed in
    // drawn x, so the warp has to come back off before it is asked for.
    const L = LAYERS[Math.max(0, this.player.lane)]!;
    return { x0: span.x0 - L.warp, x1: span.x1 - L.warp };
  }

  /** Arrive at the target lane immediately, with no swim across. */
  snapLane() {
    this.subZ = laneZ(this.laneTarget);
    this.player.z = this.subZ;
    this.player.lane = laneAtZ(this.subZ);
    this.camera.focusZ = this.subZ;
    // Effects follow the boat: bubbles off the hull, sparks off the rock it
    // scrapes and silt off the floor it is over all belong in its water.
    this.renderer.particles.baseZ = this.subZ;
    this.terrain.setOccupied(this.laneTarget);
  }

  /** The gate the sub is standing in, for the HUD prompt. */
  nearGate: Pocket | null = null;
  private gateHold = 0;
  /** Rooms already found, so the discovery bonus is paid once. */
  seenPockets = new Set<string>();

  /** Put the sub back in the near lane at once, with no swim. For respawns. */
  resetLane() {
    this.laneTarget = 0;
    this.terrain.setOccupied(0);
    this.subZ = laneZ(0);
    this.player.z = 0;
    this.player.lane = 0;
    this.camera.focusZ = 0;
  }

  /** Next lane along, wrapping. A debug shortcut; the game uses gates. */
  travelNext(): number {
    return this.travelTo((this.laneTarget + 1) % LAYERS.length);
  }

  /**
   * Fly through a gate and you are somewhere else.
   *
   * Both ends of a room are the same gate at the same x — one standing on the
   * main lane, one standing in the room — so going in and coming back out are
   * the same act and there is nothing new to learn for the return trip. The
   * cooldown is what stops the two ends handing the boat back and forth while
   * it sits inside them.
   */
  private gates(dt: number) {
    this.gateHold = Math.max(0, this.gateHold - dt);
    const p = this.player;
    this.nearGate = null;
    if (p.dead) return;
    const g = gateNear(p.x, GATE_RADIUS);
    if (!g) return;

    // The gate only exists in the two lanes it joins.
    const here = p.lane;
    if (here !== 0 && here !== g.lane) return;
    // And only where it actually stands: on the seabed, not anywhere in the
    // column above it.
    const L = LAYERS[Math.max(0, here)]!;
    const gy = floorAt(g.gateX + L.warp) + layerLift(g.gateX, L) + GATE_LIFT;
    if (Math.abs(p.y - gy) > GATE_RADIUS) return;

    this.nearGate = g;
    if (this.gateHold > 0) return;
    this.gateHold = GATE_COOLDOWN;
    const to = here === 0 ? g.lane : 0;
    this.travelTo(to);
    this.snapLane();
    // COME OUT OF THE GATE, not out of thin air beside it.
    //
    // A lane's drawn seabed sits `yOffset` above the near one — 26 m on lane 1,
    // 54 m on lane 2 — so the same gate stands at a different DEPTH at each
    // end. Carrying the old y across put the boat that far off the far gate,
    // which reads as being spat out into open water rather than through a door.
    const dl = LAYERS[Math.max(0, to)]!;
    p.y = floorAt(g.gateX + dl.warp) + layerLift(g.gateX, dl) + GATE_LIFT;
    p.x = g.gateX;
    // Momentum through a door is momentum out of the other side; the vertical
    // component is not, because the depth it was climbing toward is gone.
    p.vy *= 0.25;
    this.audio.dock(true);
    this.camera.addShake(0.22);
    this.renderer.particles.sparks(p.x, p.y, 0, 1, 30, 1.3);
    if (to === 0) {
      this.showToast('RETURNED', g.name, '#8fd9ff');
    } else {
      this.showToast(g.name, 'A ROOM BEHIND THE WORLD', '#c9a6ff');
      if (!this.seenPockets.has(g.id)) {
        this.seenPockets.add(g.id);
        p.score += SCORE.discovery * 3;
      }
    }
  }

  /** Draw both ends of every gate near the camera. */
  private drawGates(onScreen: (x: number, y: number, pad?: number) => boolean) {
    this.useLane(-1);
    const r = this.renderer;
    const camZ = this.camera.eye().z;
    const lane = Math.max(0, this.player.lane);
    for (const g of POCKETS) {
      // A gate is visible from the main lane and from inside its own room.
      if (lane !== 0 && lane !== g.lane) continue;
      const L = LAYERS[lane]!;
      if (layerVisibility(camZ, L.zFront) <= 0) continue;
      const y = floorAt(g.gateX + L.warp) + layerLift(g.gateX, L) + GATE_LIFT;
      if (!onScreen(g.gateX, y, 40)) continue;
      const z = laneZ(lane) - 2;
      // Two beats at different rates, so the thing never settles into a loop
      // you can count. The phase is seeded off x so no two gates pulse together.
      const ph = g.gateX * 0.017;
      const slow = 0.5 + Math.sin(this.time * 1.1 + ph) * 0.5;
      const fast = 0.5 + Math.sin(this.time * 2.9 + ph * 2.1) * 0.5;
      const glow = 0.45 + slow * 0.4 + fast * 0.15;

      // Emissive well above 1 is what the bloom chain picks up — the station
      // runs its ring to 5.6 and that is why it reads as a light rather than
      // as a pale object. This was peaking at 2.0 in one channel and looked
      // like painted plastic beside it.
      compose(this.m, g.gateX, y, z, 0, 0, 0, 1);
      this.set(
        0.30, 0.26, 0.44, 1,
        (1.4 + fast * 1.6) * glow, (0.7 + fast * 0.9) * glow, (3.4 + fast * 2.2) * glow, 0,
      );
      r.queue('gate', this.m, this.tint, this.emis);

      // The veil, spun. Violet is this game's one unused hue and it is spent
      // here on purpose: the colour IS the tell that this is not more scenery.
      compose(this.m, g.gateX, y, z + 0.3, 0, 0, this.time * 0.42 + ph, 1);
      this.set(
        0.60, 0.38, 1.0, 1,
        2.2 + fast * 2.0, 1.0 + fast * 1.1, 5.0 + fast * 2.4, 0,
      );
      r.queue('gateveil', this.m, this.tint, this.emis);
      // A second copy, counter-rotating and smaller, for the interference.
      compose(this.m, g.gateX, y, z + 0.55, 0, 0, -this.time * 0.27 - ph, 0.72);
      this.set(
        0.76, 0.54, 1.0, 1,
        1.5 + slow * 1.6, 0.7 + slow * 0.8, 3.4 + slow * 2.0, 0,
      );
      r.queue('gateveil', this.m, this.tint, this.emis);

      // Shards, orbiting on their own clocks.
      for (let k = 0; k < 5; k++) {
        const a = this.time * (0.31 + k * 0.07) + ph + (k / 5) * Math.PI * 2;
        const rad = 5.4 + Math.sin(this.time * 0.6 + k * 2.1) * 0.9;
        const sx = g.gateX + Math.cos(a) * rad;
        const sy = y + Math.sin(a) * rad * 0.82;
        compose(this.m, sx, sy, z + Math.sin(a * 1.7) * 1.6, a * 1.3, a * 0.7, a, 1);
        this.set(0.66, 0.46, 1.0, 1, 2.4 + fast * 1.4, 1.1 + fast * 0.7, 5.2 + fast * 1.8, 0);
        r.queue('gateshard', this.m, this.tint, this.emis);
      }

      // Core and rim, so the water around it carries the colour too.
      r.addLight(g.gateX, y, laneZ(lane), 38, 0.56, 0.34, 1.0, 1.5 + glow * 1.5);
      r.addLight(g.gateX, y, laneZ(lane) + 6, 16, 0.86, 0.66, 1.0, 0.9 + fast * 1.1);
    }
  }

  /**
   * Swim toward the target lane. Deliberately slow: the gaps are 36 and 42 m of
   * open water and crossing one should read as a journey, not a cut.
   */
  private updateLane(dt: number) {
    const want = laneZ(this.laneTarget);
    this.subZ = damp(this.subZ, want, 1.35, dt);
    if (Math.abs(this.subZ - want) < 0.05) this.subZ = want;
    this.player.z = this.subZ;
    this.player.lane = laneAtZ(this.subZ);
    this.camera.focusZ = this.subZ;
    // Promote on DEPARTURE, not on arrival: the rebuild is a worker round trip
    // per chunk, and asking for it the moment the sub sets off means the ground
    // is ready by the time it gets there rather than popping once it has.
    this.terrain.setOccupied(this.laneTarget);
  }

  /** Fold one sample into a phase average. */
  noteCpu(phase: keyof World['cpu'], ms: number) {
    this.cpu[phase] += (ms - this.cpu[phase]) * 0.02;
  }

  /**
   * What the grapple sounds and looks like. `cargo.ts` reports events and knows
   * nothing about audio, particles or toasts; this is where they land.
   */
  private towHooks: TowHooks = {
    onCatch: (pk) => {
      this.audio.impact(6);
      this.showToast('LINE ATTACHED', `${pk.label}  ·  ${pk.mass.toFixed(1)} t`, '#8fd9ff');
      this.eventHint('cargo');
      this.telemetry('hooked', { id: pk.id, mass: pk.mass });
    },
    onTooHeavy: (pk, cap) => {
      this.audio.impact(2);
      this.showToast(
        'TOO HEAVY FOR THE GEAR',
        `${pk.label}  ·  ${pk.mass.toFixed(1)} t  ·  RATED ${cap.toFixed(1)} t`,
        '#ffb454',
      );
    },
    onRelease: () => {
      this.audio.dock(false);
    },
    onSnap: (x, y) => {
      // A parted cable is a bang, a shower of sparks and a shove. Losing a load
      // should read as an event, not as a state flag changing.
      const p = this.player;
      this.audio.rockBreak(hyp(x - p.x, y - p.y));
      this.renderer.particles.sparks(x, y, 0, 1, 18, 1.4);
      this.camera.addShake(0.5);
      this.showToast('LINE PARTED', 'CARGO ADRIFT', '#ff8a5c');
      this.telemetry('snapped', { x2: x, y2: y });
    },
  };

  private m: Mat4 = mat4();
  /**
   * ORE IN THE HOLD, one count per seam, indexed by `ORE`.
   *
   * Shards rather than clusters: a cluster is the thing in the world, a shard
   * is the thing that comes back on board, and the ratio between them is per
   * mineral. Salvage is still the currency — this is the manifest, and it is
   * what a refinery, a contract or another player would be trading against.
   *
   * A plain array indexed by `ORE` rather than a map keyed by name, because
   * `Prop.variant` is already that index and the save is already promised to
   * be stable against it.
   */
  readonly oreHeld: number[] = new Array(ORE.length).fill(0);
  /**
   * HUD colours for the hold, built once. The readout draws every frame and a
   * gamma conversion per mineral per frame is work for a number that cannot
   * change.
   */
  readonly oreColours: string[] = MINERAL_TYPES.map((t) => oreHudColour(t.colour));

  /**
   * Deposits already broken, by site key. Persisted: a seam you mined out does
   * not grow back when the chunk cache is evicted and regenerated.
   *
   * Keyed by rounded position rather than by an index, because the generator
   * is deterministic but the ORDER within a chunk is not something a save
   * should depend on — inserting one roll upstream would renumber every seam
   * after it and hand a player somebody else's mined-out list.
   */
  private minedOre = new Set<string>();
  /**
   * Damage taken by deposits that are still standing. Transient by design: a
   * half-mined node healing while you fly away and come back is a smaller
   * problem than a save file that grows a row every time somebody shoots a
   * rock and stops.
   */
  private oreHits = new Map<string, number>();
  /**
   * Shards already taken out of seams that are still standing. PERSISTED, and
   * that is the change: a seam is a reserve you come back to, not a thing you
   * finish or lose.
   *
   * `oreHits` was transient on the argument that a save should not grow a row
   * every time somebody shoots a rock and stops. That was right for a hit
   * counter and wrong for a reserve — the whole point of a capacity is that
   * you leave with the seam half worked, and it has to be half worked when you
   * return or the trip back was pointless. One row per partially-worked
   * deposit, which is bounded by how many the player has actually visited.
   */
  private oreProgress = new Map<string, number>();

  /** Shards aboard, against `HOLD_CAPACITY`. */
  get holdUsed(): number {
    let n = 0;
    for (const q of this.oreHeld) n += q;
    return n;
  }
  get holdFull(): boolean { return this.holdUsed >= HOLD_CAPACITY; }

  /**
   * Land the hold at a station: every shard becomes salvage and the manifest
   * empties.
   *
   * ORE IS WORTH NOTHING UNTIL IT IS LANDED, which is what makes the trip back
   * part of the mining rather than an errand afterwards. Paying at the seam —
   * which is what it did — meant the hold was a souvenir and the capacity
   * meant nothing.
   */
  /**
   * Put every seam back, and forget what has been taken out of the ones still
   * standing.
   *
   * For a save made under numbers that no longer apply. The reserve went from
   * a cluster's yield to six times it, so a world mined out at the old rate is
   * a world of holes that would never have been holes — and there is no honest
   * migration for that, only a reset.
   */
  resetOre() {
    const gone = this.minedOre.size;
    this.minedOre.clear();
    this.oreProgress.clear();
    this.oreHits.clear();
    this.mining = null;
    this.showToast('SEAMS RESTORED', `${gone} DEPOSIT${gone === 1 ? '' : 'S'} BACK`, '#8fd9ff');
  }

  /**
   * THE BOAT IS NOT BEING DRIVEN. Drop every held control and stop every
   * looping sound.
   *
   * `update` is the only thing that turns a held key into a state and a state
   * into a sound — `audio.nitro(p.nitroOn)` runs inside it — so any moment the
   * world is not updating is a moment those two are frozen at whatever they
   * were. Open the shop with SHIFT down and the burner is still lit, still
   * audible, and still lit when you come back out.
   *
   * IDEMPOTENT, AND CALLED EVERY FRAME A SCREEN IS UP rather than once when it
   * opens. An edge can be missed — a screen opened from a path nobody thought
   * of, a mode switch, a death during a pause — and every one of those is a
   * separate bug with the same symptom. A level test cannot be missed. It also
   * costs nothing: setting a false to false and telling a stopped oscillator
   * to stop are both no-ops.
   *
   * The death path used to carry its own copy of this. It calls this instead,
   * which is the other half of the point: one list of things that have to be
   * let go of, not one per situation that lets go of them.
   */
  releaseControls() {
    this.player.nitroOn = false;
    this.audio.nitro(false);
    this.audio.lowHull(false);
    this.nitroWas = false;
    this.lowHullWas = false;
  }

  /** Sell `n` shards of seam `i`. Returns the salvage paid. */
  sellOre(i: number, n: number): number {
    const have = this.oreHeld[i] ?? 0;
    const take = Math.max(0, Math.min(n, have));
    if (take <= 0) return 0;
    const paid = take * (ORE[i]?.value ?? 0);
    this.oreHeld[i] = have - take;
    this.purse(paid);
    this.player.score += Math.round(paid * SCORE.salvagePerUnit);
    return paid;
  }

  /** Sell everything aboard. Returns the salvage paid. */
  sellHold(): number {
    let paid = 0;
    for (let i = 0; i < this.oreHeld.length; i++) paid += this.sellOre(i, this.oreHeld[i] ?? 0);
    return paid;
  }

  /** Spare capacity, in shards. */
  get holdSpare(): number { return Math.max(0, HOLD_CAPACITY - this.holdUsed); }
  /** For the shop: what is aboard, richest first, with what it is worth. */
  holdManifest(): { i: number; key: string; label: string; colour: string; n: number; each: number }[] {
    const out: { i: number; key: string; label: string; colour: string; n: number; each: number }[] = [];
    for (let i = 0; i < this.oreHeld.length; i++) {
      const n = this.oreHeld[i] ?? 0;
      const o = ORE[i];
      if (n <= 0 || !o) continue;
      out.push({ i, key: o.key, label: o.label, colour: this.oreColours[i] ?? '#ffb454', n, each: o.value });
    }
    out.sort((a, b) => b.n * b.each - a.n * a.each);
    return out;
  }

  /**
   * Stable id for a deposit, from where it is rather than when it was made.
   *
   * The SITE, so every cluster in a group agrees — a mother lode is one thing
   * you break and all eleven of its clusters have to disappear together.
   */
  private oreKey(prop: Prop, lane: number): string {
    const x = prop.siteX ?? prop.x, y = prop.siteY ?? prop.y;
    return `${lane}:${Math.round(x * 2)}:${Math.round(y * 2)}`;
  }

  /**
   * How close the boat has to be to work a seam, and how slowly it has to be
   * going. "Suspended next to the deposit" is the brief, so both matter: range
   * alone would let a player strafe past at thirty knots and strip a mother
   * lode on the way through.
   */
  private static readonly MINE_RANGE = 15;
  private static readonly MINE_SPEED = 7;
  /**
   * The seam being worked, or null.
   *
   * MINING IS A PROCESS, NOT AN EVENT, and that is the whole of the redesign.
   * Breaking a deposit with the cannon made a mother lode and a lone crystal
   * the same single trigger pull — worse with the gun upgraded, where eleven
   * clusters came out in one shot and the only difference between the richest
   * find in the game and the poorest was a bigger number in a toast.
   *
   * Extraction over TIME fixes that by construction. A rich seam is worth more
   * because it takes longer and you have to hold station over it, which is
   * also the only thing in this game that has ever asked the player to stop.
   */
  private mining: {
    key: string; variant: number; x: number; y: number; z: number;
    taken: number; total: number; t: number;
  } | null = null;

  /** What the HUD needs to draw the progress bar, or null when idle. */
  get miningState(): { label: string; colour: string; taken: number; total: number } | null {
    const m = this.mining;
    if (!m) return null;
    return {
      label: ORE[m.variant]?.label ?? 'ORE',
      colour: this.oreColours[m.variant] ?? '#ffb454',
      taken: m.taken,
      total: m.total,
    };
  }

  /**
   * MINING, in real time, while the boat holds station.
   *
   * Here rather than in `entities.ts` because that file has no idea props
   * exist — it knows enemies, loot and stations, and a deposit is a prop. This
   * is the only place that can see the player and the chunk a seam was
   * generated into at the same time.
   *
   * Only the DOMINANT cluster is workable, which is the flag the point light
   * and the sonar echo already use: a deposit is one thing, and its shards are
   * its whole count.
   */
  private mineOre(dt: number) {
    const p = this.player;
    const lane = Math.max(0, p.lane);
    const R = World.MINE_RANGE;

    // The nearest workable seam, which is also what ENDS a session: fly away
    // and `best` comes back null on the next frame with no state to unwind.
    let best: Prop | null = null;
    let bestD = R * R;
    if (!p.dead && p.speed <= World.MINE_SPEED) {
      const c0 = this.terrain.chunkAt(p.x - R);
      const c1 = this.terrain.chunkAt(p.x + R);
      for (let ci = c0; ci <= c1; ci++) {
        for (const prop of propsForChunk(ci, lane)) {
          if (prop.kind !== 'mineral' || !prop.deposit) continue;
          if (this.minedOre.has(this.oreKey(prop, lane))) continue;
          const dx = prop.x - p.x, dy = prop.y - p.y;
          const d = dx * dx + dy * dy;
          if (d < bestD) { bestD = d; best = prop; }
        }
      }
    }

    if (!best) { this.mining = null; return; }
    const ore = ORE[best.variant];
    if (!ore) { this.mining = null; return; }
    const key = this.oreKey(best, lane);
    if (!this.mining || this.mining.key !== key) {
      // A new seam starts from zero rather than inheriting the last one's
      // progress — half a lode's worth of credit carried onto a lone crystal
      // would hand it out instantly.
      this.mining = {
        key, variant: best.variant, x: best.x, y: best.y, z: best.z,
        // RESUMED, not restarted. The reserve is what was left when the hold
        // filled last time, and a seam that reset would make the trip back a
        // punishment instead of a loop.
        taken: this.oreProgress.get(key) ?? 0,
        // The RESERVE, not the yield. See `RESERVE_PER_SHARD`: a cluster's
        // `shards` is what it gives up in one working, and treating that as
        // the whole deposit is why a hold cap never bound on anything.
        total: Math.min(best.deposit!, 13) * ore.shards * RESERVE_PER_SHARD,
        t: 0,
      };
      this.eventHint('mine');
    }
    const m = this.mining;

    // TOUGHNESS SETS THE RATE, which is the second thing the cannon version
    // could not express: it was a hit count, so a tough ore just needed one
    // more trigger pull. Here it is seconds per shard, and the difference
    // between brittle quartz and the abyssal crystal is how long you have to
    // sit still in the dark with your lights on.
    const perShard = secondsPerShard(ore.toughness);
    m.t += dt;
    while (m.t >= perShard && m.taken < m.total && !this.holdFull) {
      m.t -= perShard;
      m.taken++;
      // INTO THE HOLD ONLY. No salvage here: ore is worth nothing until it is
      // landed, which is what makes the run back to a station part of the
      // mining. Paying at the seam made the hold a souvenir and the capacity
      // meaningless.
      this.collectOre(m.variant, 1);
      this.oreProgress.set(key, m.taken);
      this.renderer.particles.sparks(m.x, m.y + 1.2, m.z, 1, 7, 0.7);
      this.audio.impact(0.22);
      this.rewardFlash = Math.max(this.rewardFlash, 0.3);
    }
    // A FULL HOLD STOPS THE BEAM rather than quietly wasting the seam. Said
    // once — the toast has its own repeat guard through `eventHint`.
    if (this.holdFull && m.taken < m.total) {
      m.t = 0;
      this.eventHint('holdfull');
    }

    if (m.taken >= m.total) {
      this.minedOre.add(key);
      this.oreProgress.delete(key);
      this.renderer.particles.sparks(m.x, m.y + 1.2, m.z, 1, 26, 1.5);
      this.camera.addShake(0.9);
      this.showToast(ore.label, `SEAM WORKED OUT  ·  ${m.total} SHARDS`,
                     this.oreColours[m.variant] ?? '#ffb454');
      this.mining = null;
    }
  }

  /** Take `n` shards of seam `i` aboard. Returns what the hold now holds. */
  collectOre(i: number, n = 1): number {
    if (i < 0 || i >= this.oreHeld.length) return 0;
    this.oreHeld[i] = (this.oreHeld[i] ?? 0) + n;
    return this.oreHeld[i]!;
  }

  /** Projection scratch for the ore callouts. Separate so nothing treads on it. */
  private proj2 = { x: 0, y: 0 };
  private tint: [number, number, number, number] = [1, 1, 1, 1];
  private emis: [number, number, number, number] = [0, 0, 0, 0];
  /** Metallic, roughness, dielectric F0, enable. Ore is the only user so far. */
  private pbr: [number, number, number, number] = [0, 0.5, 0.04, 0];
  private frame: FrameParams;
  private bubbleTimer = 0;
  private ambientBand = { fog: [0, 0, 0], ambient: [0, 0, 0], ext: [0, 0, 0], level: 0 };

  /**
   * Time of day, as a sun. Refracted once when it is chosen rather than every
   * frame — Snell and Fresnel depend only on the preset, and none of it
   * changes while the boat moves.
   */
  private sunPreset: SunPreset =
    SUN_PRESETS.find((p) => p.id === DEFAULT_SUN) ?? SUN_PRESETS[0]!;
  private sunState: SunState = sunUnderwater(this.sunPreset);
  private sunAzUsed = 0;
  private sunElUsed = 0;

  /** Which preset is in force. */
  get timeOfDay(): string { return this.sunPreset.id; }

  /** Pick a time of day. Unknown ids are ignored rather than throwing: this is
   *  reachable from a settings file a previous build wrote. */
  setTimeOfDay(id: string) {
    const p = SUN_PRESETS.find((q) => q.id === id);
    if (!p) return;
    this.sunPreset = p;
    this.applySun();
  }

  /**
   * WHERE THE SUN IS, WITH THE TUNER'S OFFSETS ON TOP.
   *
   * Both are offsets from the preset rather than absolutes, so all seven times
   * of day keep their relationship to each other while the pair is being found,
   * and zero means "exactly what the preset says".
   *
   * Azimuth is the one worth moving. This level runs along x and the camera
   * looks down -z, so a preset at 180 puts a low sun ninety degrees off the
   * view axis — out of frame past the right edge, whatever its elevation. That
   * is not incorrect, it is a choice of compass bearing for the coastline, and
   * every value of it is equally physical. Which is exactly why it belongs on a
   * slider rather than in an argument.
   */
  private applySun() {
    const t = this.renderer.skyTune;
    this.sunState = sunUnderwater({
      ...this.sunPreset,
      elevation: clamp(this.sunPreset.elevation + t.sunElevation, -12, 89),
      azimuth: (this.sunPreset.azimuth + t.sunAzimuth + 360) % 360,
    });
  }
  private muzzlePos = { x: 0, y: 0 };
  /** Scratch for rotating a mesh-local hardpoint into world space. */
  private hp = { x: 0, y: 0, z: 0 };
  /** Which flank tube fires next. Alternates, so a salvo comes off both pods. */
  private tubeSide = 1;
  private cone: Float32Array<ArrayBuffer> = new Float32Array(LIGHT.shadowBins);
  /**
   * Where the lamp lands, in a handful of angular sectors. One bounce of
   * global illumination for free: the beam march already knows the distance to
   * rock in every direction, so the surfaces it is hitting are already solved
   * — they just were not being asked to give any of that light back.
   */
  private bounce: { x: number; y: number; w: number; d: number }[] = [];
  /**
   * Solid props standing in the beam, as ellipses in the XY plane. Rebuilt each
   * frame the lamp is on and consumed by the same march that samples the
   * density field — see {@link BEAM_BLOCKERS}.
   */
  private blockers: { x: number; y: number; a: number; b: number }[] = [];
  /**
   * Whether props shadow the beam at all. A switch rather than a constant for
   * the same reason the renderer's probes are: a shadow cast by something the
   * player cannot see reads as a bug, and telling that apart from a shadow cast
   * by something they can means turning it off and looking at the same frame.
   */
  beamProps = true;
  private lastDt = 1 / 60;
  private wreckY = -92;
  /** Seabed-seated Y of each authored habitat base, by id. */
  private habitatY = new Map<string, number>();
  /** Half-extent along X of each base, for the cheap whole-base reject. */
  private habitatExt = new Map<string, number>();
  private habitatIdx = -1;
  /** Which habitat meshes have a companion glazing slot registered. */
  /**
   * Which extra slots each module actually has, so the draw does not queue a
   * slot nobody registered. Filled from the CAD kit when it loaded and from the
   * procedural builders when it did not — the two disagree: the builders have a
   * `_room`, the CAD has real interiors inside the hull and a `_glow` for its
   * strip lights instead.
   */
  /**
   * Which shading group owns a module's BARE slot name. `paint` where there is
   * any, and the first group otherwise — legs, tubes and junctions are
   * metal-only, so for those the bare name is the metal.
   */
  private habPrimary = new Map<string, string>();
  private habExtra = new Map<string, string[]>(
    HAB_MODULES.map((m) => [m.name, m.glass ? ['room', 'glass'] : []]),
  );
  /** Widest framing anything asked for this tick; reset at the end of update. */
  private framingZoom = 1;
  /** True when the Pathfinder is flying a baked hull rather than the built one. */
  private modelHull = true;
  /**
   * DEBUG: nitro's contribution to everything the RENDERER sees, by group.
   *
   * `ABYSS.world.nitroFx.wake = false` and so on. The burner's PHYSICS is
   * never affected — the boat still accelerates, drains and handles the same —
   * only what the renderer is told about it.
   *
   * Grouped because a subsystem toggle cannot answer this question. Switching
   * the lights off and turning them DOWN look identical, so `probe.lights`
   * could never separate a culprit from a victim. These switch off the CAUSE.
   *
   *   camera    the 1.28 zoom and the 2.1 look-ahead
   *   particles bubble rate, burst size and the churn gate
   *   wake      the fluid injection and the foam's `hot` multiplier
   *   lights    the drive light's range and intensity, and its LED bead
   *   post      the screen-space refraction amplitude
   *   drive     the exhaust throttle that MAT.drive reads
   */
  nitroFx = { camera: true, particles: true, wake: true, lights: true, post: true, drive: true };

  /**
   * How much of nitro reaches the WAKE. Zero, deliberately.
   *
   * The burner used to drive both wake inputs hard: the fluid injection by
   * 1.9x, and `hot` — a pure brightness multiplier on foam that already exists
   * — from 2.2x to 4.8x on the crest. Together they took whatever dye was in
   * the field and made it a screenful, everywhere except the clear ellipse the
   * composite keeps around the hull. It read as the fog and the water lighting
   * failing, which is why it took so long to find: nothing about it looks like
   * a wake.
   *
   * Isolated by switching the CAUSE off rather than the effect — every probe
   * toggle turns a subsystem OFF, and "the lights are off" is indistinguishable
   * from "the lights faded", so none of them could separate culprit from
   * victim. `nitroFx.wake = false` was the test that landed it.
   *
   * Zero rather than "tuned down" because I cannot see the result and have
   * guessed wrong at this repeatedly; off is the state that is definitely
   * correct. The nitro wake tell is worth one flourish and it was costing the
   * whole picture. Raise this to bring it back — 0.3 or so is where I would
   * start — and watch the crest, not the numbers.
   */
  private static readonly NITRO_WAKE = 0;
  /** Nitro as one group's consumers should see it. */
  private nb(g: keyof World['nitroFx']): number {
    return this.nitroFx[g] ? this.player.nitroBlend : 0;
  }
  /**
   * The CAD hull's parts, or null when flying one of the two single-mesh hulls.
   *
   * Parents always precede children, so `drawSub2` composes every world matrix
   * in one forward pass with no recursion and no second buffer.
   */
  private subParts: Sub2PartMesh[] | null = null;
  /**
   * Live splash crowns. See `buildCrown` for the mesh and `splash` for what
   * decides one has happened.
   *
   * A plain array rather than a pool: at most a couple exist at a time, they
   * live under a second, and the allocation is one object per breach.
   */
  private crowns: {
    x: number; y: number; z: number; t: number; life: number;
    r: number; h: number; out: boolean;
    /** Horizontal travel inherited from the hull, metres per second. */
    driftX: number;
    /** Lean of the thrown sheet in the gameplay XY plane. */
    lean: number;
    /** Footprint stretch along the hull's horizontal travel. */
    stretch: number;
  }[] = [];
  /**
   * Short-lived surface churn from a breach. It reuses the existing eighth-res
   * wake simulation, so an impact leaves advected foam instead of depending on
   * dozens of bright billboards to sell the event.
   */
  private surfaceImpact: {
    x: number; y: number; z: number; vx: number; vy: number; strength: number;
  } | null = null;
  /** Scratch for that pass: one local and one world matrix per part. */
  private partLocal = new Float32Array(16) as Mat4;
  private partWorld: Mat4[] = [];
  /** Draw matrix for a part whose mesh is stretched; never fed back into the tree. */
  private partDraw = new Float32Array(16) as Mat4;
  /** 0 stowed along the keel, 1 fully out. Follows the tow line's state. */
  private armDeploy = 0;
  /** 0 claw open, 1 closed on a load. */
  private armGrip = 0;
  /**
   * World position of the claw's grip centre, written by `drawSub2` and read
   * by `drawCargo` — which is safe only because `drawPlayer` runs first, by
   * the same deliberate ordering the lighting budget already depends on.
   */
  private clawTip = { x: 0, y: 0 };

  /**
   * Ask the camera to pull back this frame. Reset every frame after it is read,
   * so it is a max over whatever wants room rather than a piece of state
   * anybody has to remember to clear. The boss framing already used it; a mode
   * can now use it too.
   */
  requestZoom(z: number) {
    if (z > this.framingZoom) this.framingZoom = z;
  }
  private bossAwake = false;
  private bossEnraged = false;
  private nitroWas = false;
  private creakTimer = 6;
  private attractT = 0;
  private ramCarve = 0;
  /** Scratch for lane-aware field lookups in the crush test. */
  private fieldPt = { x: 0, y: 0 };
  /**
   * Per-frame light budget. The renderer keeps 48 dynamic lights and silently
   * drops the rest, and draw order decides who loses — so without a budget a
   * five-way cannon volley would put eighty bullet lights in front of the
   * boss and the scenery would go dark. Threats and rewards outrank bullets.
   */
  private propLights = 0;
  /** Background-lane prop lights, counted apart from the near lane's. */
  private bgLights = 0;
  /**
   * And the glowing animals get their own too, for exactly the reason the
   * background has one: on a shared counter they are starved by scenery.
   */
  private jellyLights = 0;
  private shotLights = 0;
  private shieldSfx = 0;
  private blastVoices = 0;
  private lowHullWas = false;
  private actorCtx: ActorCtx;
  private pendingCarve: { x0: number; x1: number } | null = null;

  constructor(
    private renderer: Renderer,
    private audio: Audio,
    private quality: Quality,
    rules: ModeRules = CampaignRules,
    profile: Profile = blankProfile(),
    /**
     * The habitat kit baked from the Fusion CAD, or null when its fetch failed.
     * Null falls back to the procedural builders in `habitat.ts`, which is why
     * they are still there. See `habKitMesh.ts`.
     */
    private habKit: HabKitMeshes | null = null,
  ) {
    this.rules = rules;
    this.profile = profile;
    this.terrain = new Terrain(renderer.device);
    this.terrain.radius = quality.chunkRadius;
    this.terrain.msBudget = quality.meshBudgetMs;
    this.actors = new Actors(rules.content);
    // Shoals come from content too — a survival arena wants one, not the
    // campaign's seventeen strung across 7.9 km.
    this.baitBalls = rules.content.baitBalls.map((b) => new BaitBall({
      id: b.id, x: b.x, y: profileY(b.x) + b.lift,
      radius: b.radius, height: b.height, count: b.count, spin: b.spin, sheen: b.sheen,
      mode: b.mode, glow: b.glow, range: b.range,
    }));
    if (rules.content.octopus) this.octopus.setSite(rules.content.octopus);
    const boot = this.hulls.has(profile.active as ChassisId)
      ? CHASSIS[profile.active as ChassisId] : PATHFINDER;
    this.player.setChassis(boot, statsFor(this.levelsFor(boot.id), boot));
    this.player.spawn(SPAWN.x, SPAWN.y, 0);
    this.resetLane();
    this.camera.snapTo(SPAWN.x, SPAWN.y);

    // One slot per hull. A slot with count 0 is skipped every frame, so the
    // only cost of an unflown boat is its one-time build (~4 ms) and its
    // buffers. Capacity 2 on each, for a hull under tow later.
    // The Pathfinder has two hulls to choose from: the scanned reference baked
    // into the bundle, and the procedural one it shipped with. Only the chosen
    // one is registered, so the loser costs no build time and no buffers.
    // THREE hulls now, and the CAD rebuild is the default. The other two are
    // not deprecated and not hidden: 'model' is the Tripo scan, 'classic' is
    // `buildSub()`, and both stay switchable for exactly the reason the scan
    // gave when it displaced the procedural one — you cannot tell whether a new
    // hull is better than the old one if you cannot put them side by side.
    const hullPref = loadSettings().hull;
    this.subParts = hullPref === 'mk2' ? buildSub2Parts() : null;
    this.modelHull = hullPref !== 'classic';

    if (this.subParts) {
      // The CAD boat is a tree of parts, not one mesh. The hull keeps the slot
      // name 'sub' so the chassis table, the tow code and every hardpoint go on
      // referring to the same thing they always did.
      for (const p of this.subParts) renderer.registerMesh(p.slot, p.mesh, 2);
    } else {
      renderer.registerMesh('sub', this.modelHull ? buildSubModel() : buildSub(), 2);
      // The reference hull carries its searchlight as a separate instance so it
      // can turn with the beam. The procedural hull has the lamp built in and
      // cannot, which is one of the things the comparison is for.
      if (this.modelHull) renderer.registerMesh('sublamp', buildSubLamp(), 2);
    }
    renderer.registerMesh('dray', buildDray(), 2);
    renderer.registerMesh('prop', buildProp(), 1);
    renderer.registerMesh('led', buildLed(), 8);
    // Translucent: the point of the shell is that you can see the boat inside
    // it. On the opaque pipeline it drew as a solid white ball that hid the
    // sub completely, which is worse than having no shell at all.
    renderer.registerMesh('aegis', buildAegisShell(), 1, true);
    // Scenery capacity scales with the tier. The numbers below are the
    // measured baseline; `instanceBudget` is what a machine with room to spare
    // gets on top. Actors and one-offs are NOT scaled — a boss is one boss on
    // every device, and scaling a capacity of 1 buys nothing.
    const cap = (n: number) => Math.max(1, Math.round(n * this.quality.instanceBudget));
    // SCENERY SLOTS HOLD TWO LANES.
    //
    // In the middle lane the near one is still drawn — it becomes the
    // foreground the camera looks past — while the occupied lane is drawn in
    // full, so every scenery capacity has to cover both at once. Measured peaks
    // for one lane are kelp 1201, coral 997, debris 204, rock 69; doubling
    // those and leaving headroom is what these are. Overflow is silent by
    // design (the F3 readout shows DROPPED), and a dropped instance is a plant
    // that flickers out of existence as you fly.
    renderer.registerMesh('kelp', buildKelp(), cap(5400));
    // The far build, for background growth and the foreground silhouettes.
    // Both are near-black shapes; neither has ever shown a gas bladder.
    renderer.registerMesh('kelplo', buildKelp(1), cap(3700));
    // 700 was sized for coral as an accent — a handful per chunk on the shelf.
    // The coral garden is a zone made OF it and peaks near 1000 over the six
    // chunks in flight, which `queue` would have dropped silently: the densest
    // part of the prettiest zone quietly losing scenery, and no error anywhere.
    renderer.registerMesh('coral', buildCoral(), cap(3100));
    renderer.registerMesh('corallo', buildCoral(1), cap(3400));
    // THE MATERIAL CHART. Six meshes carry eight probes — chrome is the metal
    // tag with the specular turned up and the bio ball is the organic one with
    // a light in it, so those two share geometry with their neighbours.
    // THE GLASS BALL GOES THROUGH THE GLASS PATH, which is the second time this
    // chart has had to be corrected for showing a material nothing ships.
    //
    // Pointing the probe at `MAT.crystal` was not enough on its own: the probe
    // spheres are registered opaque, so it was still being shaded by `fsMesh`
    // and `crystalCoat` while every window in the game had moved to `fsGlass`
    // in the translucent pass. It came out as a white ball for the same reason
    // the `MAT.glow` version did — a chart that does not run the shipping path
    // cannot answer the only question it is for.
    for (const m of PROBE_MESHES) {
      const isGlass = m.slot === 'probeGlass';
      renderer.registerMesh(m.slot, buildProbe(m.mat), 4, isGlass, isGlass);
    }
    // One, ever: the refit screen shows one hull at a time.
    renderer.registerMesh('backdrop', buildBackdrop(), 1);
    {
      // Measured here, from the very mesh the renderer is handed.
      const m = buildRock(1337);
      renderer.registerMesh('rock', m, cap(1250));
      this.beamBlockers.set('rock', meshHalfExtents(m));
    }
    // THE ORE. Sixteen meshes — a cluster and a shard for each of eight
    // minerals — registered under one loop rather than eight named calls,
    // because the set is generated and the engine should not have to be edited
    // when the art is re-exported with a ninth.
    //
    // Capacities are asymmetric on purpose. A node is scenery and there can be
    // a screenful of them across three lanes; a shard exists only between a
    // node breaking and the player flying through the pieces, so a handful per
    // mineral is generous.
    for (const [name, mesh] of mineralMeshes()) {
      renderer.registerMesh(mineralSlot(name), mesh,
        cap(name.startsWith('node_') ? 90 : 40));
    }
    // The same boulder at a sixth the triangles, for everything too far away or
    // too small for the difference to reach the screen.
    renderer.registerMesh('rocklo', buildRock(1337, 1), cap(1850));
    // One instanced mesh per medusa species — a field of mixed jellies still
    // costs one draw call per species, not one per animal.
    for (let i = 0; i < JELLY_SPECIES; i++) {
      renderer.registerMesh(`jelly${i}`, buildJellyfish(jellySpecies(i), i + 1), cap(190), true);
    }
    for (let i = 0; i < BLOOM_SPECIES; i++) {
      renderer.registerMesh(`bloom${i}`, buildBloom(i + 1), cap(40), true);
    }
    renderer.registerMesh('wreck', buildWreck(), 1);
    // Instance capacity, sized for Survival's horde rather than the campaign's
    // spacing. `queue` drops silently past capacity — a wave that overruns it
    // simply renders fewer enemies than are actually hitting you, which is the
    // worst possible failure mode. Measured: 211 mechanicals cost 83k triangles
    // and 0.21 ms of update, so the ceiling here is memory, not frame time.
    renderer.registerMesh('mine', buildMine(), cap(96));
    renderer.registerMesh('drone', buildDrone(), cap(180));
    renderer.registerMesh('seeker', buildSeeker(), cap(180));
    renderer.registerMesh('turret', buildTurret(), 48);
    // The splash crown. Translucent, so it draws in the pass that already
    // sorts and blends — a sheet of foam is exactly as see-through as a
    // jellyfish and wants the same treatment. Four is generous: a crossing
    // lasts under a second and nothing else in the game breaches.
    renderer.registerMesh('crown', buildCrown(), 4, true);
    // Four fish silhouettes, so a shoal is a mixed school rather than one shape
    // repeated. Each is its own instance buffer; a full reef is four draws.
    // Weighted 5:1:1:1 toward species 0, and a bait ball alone can be several
    // hundred fish, so the sleek one needs most of the room.
    for (let i = 0; i < FISH_SPECIES; i++) {
      renderer.registerMesh(`fish${i}`, buildFishSpecies(i), cap(i === 0 ? 1500 : 420));
      // Bait-ball fish are drawn from the far build unless they are one of the
      // big ones. A shoal fish is 0.24–0.40 m long at a fixed ~68 m from the
      // camera, which is four or five pixels — and it was costing 460 to 640
      // triangles to be four pixels. See `drawBait`.
      renderer.registerMesh(`fish${i}lo`, buildFishSpecies(i, 1), cap(i === 0 ? 1500 : 420));
    }
    renderer.registerMesh('lurker', buildLurker(), 24);
    renderer.registerMesh('sentinel', buildSentinel(), 72);
    renderer.registerMesh('hunter', buildHunter(), 36);
    renderer.registerMesh('dread', buildDreadnought(), 2);
    renderer.registerMesh('station', buildStation(), 6);
    // The habitat kit (docs/HABITAT.md §7). Fourteen slots is fourteen draws;
    // capacities are sized for a large base plus the next one visible in the
    // parallax, and overflow is SILENT — past capacity instances are dropped and
    // it reads as scenery vanishing (§2.5).
    /**
     * THE CAD KIT WHEN IT LOADED, THE PROCEDURAL BUILDERS WHEN IT DID NOT.
     *
     * `HABITAT.md` sec.2.4 made Fusion the dimension authority and left the
     * meshes as procedural TypeScript. That held while the kit was chamfered
     * boxes; it stopped holding once the CAD grew collars, fillets, decks,
     * ladders and bulkheads, because the builders were reproducing a drawing
     * from its measurements and losing everything else the drawing knew.
     *
     * Three slots a module now, split by SHADING PATH rather than by Fusion
     * material — see `bake-habitat.mjs`. The glass one goes through the
     * translucent pass, and holds only the panes, which is the same conclusion
     * `glazing()` reached the hard way: a translucent slot with two surfaces in
     * it sorts them against each other every frame and the window slides.
     */
    for (const mod of HAB_MODULES) {
      const kit = this.habKit?.groups.get(mod.name);
      if (kit && this.habKit) {
        /**
         * THE BARE SLOT NAME GOES TO WHICHEVER GROUP THE MODULE ACTUALLY HAS.
         *
         * This assumed `paint`, and a module with no painted surface therefore
         * never registered `hab_leg` or `hab_tube` at all — only
         * `hab_leg_metal`. The draw queues `part.mesh`, so those went to a slot
         * that did not exist and were dropped every frame:
         *
         *   [abyss] instance slot 'hab_leg' full at 0 (slot not registered)
         *
         * The counter said "20 dropped instances" and the message said which,
         * which is the whole reason that warning names the slot. Legs, tubes and
         * all three junctions are metal-only in the CAD.
         */
        const primary = kit.includes('paint') ? 'paint' : kit[0]!;
        this.habPrimary.set(mod.name, primary);
        this.habExtra.set(mod.name, kit.filter((g) => g !== primary));
        for (const g of kit) {
          // TWO DIFFERENT NAMES, and conflating them is what broke boot.
          //
          // `HAB_GROUP_SUFFIX` is how `buildHabKit` KEYED its map — always
          // `_metal`, `_glass` and so on, with only `paint` bare. The renderer
          // SLOT is named after the module's primary group, which on a
          // metal-only module is the metal. Looking the mesh up by slot name
          // therefore missed on exactly those modules and handed `registerMesh`
          // an undefined.
          const mesh = this.habKit.meshes.get(mod.name + HAB_GROUP_SUFFIX[g]!);
          if (!mesh) continue;
          const slotName = mod.name + (g === primary ? '' : `_${g}`);
          const glass = g === 'glass';
          renderer.registerMesh(slotName, mesh, cap(mod.capacity), glass, glass);
        }
        continue;
      }
      renderer.registerMesh(mod.name, mod.build(), cap(mod.capacity));
      if (mod.room) renderer.registerMesh(`${mod.name}_room`, mod.room(), cap(mod.capacity));
      if (mod.glass) {
        renderer.registerMesh(`${mod.name}_glass`, mod.glass(), cap(mod.capacity), true, true);
      }
    }
    renderer.registerMesh('gate', buildGate(), 12);
    // Translucent, so the veil reads as a surface hanging in water rather than
    // as a solid plate: it has to be seen THROUGH.
    renderer.registerMesh('gateveil', buildGateVeil(), 24, true);
    renderer.registerMesh('gateshard', buildGateShard(), 60, true);
    renderer.registerMesh('bullet', buildBullet(), 220);
    renderer.registerMesh('torpedo', buildTorpedo(), 48);
    renderer.registerMesh('pickup', buildPickup(), 24);
    renderer.registerMesh('canister', buildCanister(), 96);
    // One crate on the line and a handful sitting in the world; the cable is
    // twelve links per line. Neither takes a dynamic light — the 48-light
    // budget is arbitrated by draw order and drawPlayer runs first by design,
    // so anything added here comes out of the boss's allowance.
    renderer.registerMesh('crate', buildCrate(), 24);
    renderer.registerMesh('drum', buildDrum(), 12);
    renderer.registerMesh('core', buildCore(), 12);
    renderer.registerMesh('slab', buildSlab(), 12);
    renderer.registerMesh('pod', buildPod(), 12);
    renderer.registerMesh('cable', buildCableLink(), 64);
    renderer.registerMesh('hook', buildHook(), 4);
    renderer.registerMesh('vent', buildVent(), cap(48));
    {
      // Measured here, from the very mesh the renderer is handed.
      const m = buildMonolith();
      renderer.registerMesh('monolith', m, cap(48));
      this.beamBlockers.set('monolith', meshHalfExtents(m));
    }
    for (let i = 0; i < 3; i++) {
      const m = buildRuin(i + 11);
      renderer.registerMesh(`ruin${i}`, m, cap(190));
      // The LARGEST of the three variants, since the prop kind is one key and a
      // blocker that under-reaches lets the beam through a wall.
      const was = this.beamBlockers.get('ruin') ?? [0, 0, 0];
      const now = meshHalfExtents(m);
      this.beamBlockers.set('ruin',
        [Math.max(was[0], now[0]), Math.max(was[1], now[1]), Math.max(was[2], now[2])]);
      // Raised from 160 for the gravel scatter, which is 30 to 50 grains a
      // chunk on top of the existing clutter. The audit measures the real peak
      // over the busiest six-chunk window and said 206; the headroom above that
      // is for a region tuned later, not padding — overflow is scenery
      // vanishing, and it vanishes silently.
      renderer.registerMesh(`debris${i}`, buildDebris(i + 31), cap(880));
    }
    renderer.registerMesh('octohead', buildOctoHead(), 1);
    renderer.registerMesh('tentacle', buildTentacleSeg(), 128);

    // Seat the wreck on the generated seabed — the authored Y is only a hint.
    const wreck = rules.content.pois.find((q) => q.id === 'wreck');
    if (wreck) this.wreckY = floorAt(wreck.x) + 1.6;

    // Seat each authored habitat on the seabed. §8.2 wants per-leg terrain
    // adaptation and `floorAt` already gives the seabed height at any x, but a
    // base is a rigid frame: it sits on the HIGHEST ground under its footprint,
    // or the uphill end buries itself in rock.
    for (const b of TEST_BASES) {
      let hi = -Infinity;
      const x0 = b.x + Math.min(...b.parts.map((q) => q.x));
      const x1 = b.x + Math.max(...b.parts.map((q) => q.x)) + 8;
      for (let x = x0; x <= x1; x += 3) hi = Math.max(hi, floorAt(x));
      this.habitatY.set(b.id, hi + b.lift);
      this.habitatExt.set(b.id, (x1 - x0) / 2);
    }

    // Nothing in here may close over `this.player` by value. It is mutated in
    // place today, so a captured reference happens to stay correct — but the
    // moment the craft object is *replaced* (a vehicle change, Phase 3) every
    // enemy would be steering at a sub that no longer exists. That fails as a
    // silent wrong answer rather than a crash, so it is worth pre-empting.
    // `playerRadius` is a getter for the same reason: it becomes the chassis's
    // radius later, and this is the line that makes that a one-line change.
    const self = this;
    this.actorCtx = {
      px: 0, py: 0, pvx: 0, pvy: 0,
      playerAlive: true,
      hasLineOfSight: (x, y) => this.lineOfSight(x, y, self.player.x, self.player.y),
      lightOn: true,
      lightAngle: 0,
      noisy: false,
      sonarPing: null,
      get playerRadius() { return SUB.radius * 1.25; },
      playerLane: 0,
      playerTrack: {
        get x() { return self.player.x; },
        get y() { return self.player.y; },
        get alive() { return !self.player.dead; },
      },
      ramming: false,
      grazePlayer: (n) => this.hurtPlayer(n, false),
      hitPlayer: (n) => this.hurtPlayer(n, true),
      blastPlayer: (n) => this.hurtPlayer(n, true, true),
      onExplosion: (x, y, s) => this.explode(x, y, s),
      onBubbleTrail: (x, y) => this.renderer.particles.bubbles(x, y, 2, 0.6, 1.2, 0.13),
      onSpark: (x, y, nx, ny, n) => this.renderer.particles.sparks(x, y, nx, ny, n, 0.8),
      onBioBurst: (x, y, s, r, g, b) => {
        this.renderer.particles.bioBurst(x, y, s, r, g, b);
        // Its own light, spent. Bright for an instant and gone with it, which
        // is the whole read: the animal was the light source.
        this.renderer.addLight(x, y, 0, 18 + s * 12, r, g, b, 2.2 + s * 0.8);
        this.camera.addShake(Math.min(0.28, 0.06 * s));
      },
      onPickup: (pk) => this.collect(pk),
      onScore: (n) => { this.player.score += n; },
      onKill: (e) => this.onKill(e),
      onShake: (n) => this.camera.addShake(n),
      onCarve: (x0, x1) => this.queueCarve(x0, x1),
      onEnemyShot: (x, y, kind) => this.enemyShot(x, y, kind),
    };

    this.frame = {
      viewProj: mat4(), viewProjNJ: mat4(), invViewProj: mat4(), dt: 1 / 60,
      camX: 0, camY: 0, camZ: 0, focusZ: 0, focusDist: 60, viewZoom: 1,
      tanTop: -Math.tan(CAMERA.fov / 2), tanBot: Math.tan(CAMERA.fov / 2), time: 0,
      bloomRect: null,
      sunX: 0.16, sunY: -0.97, sunZ: 0.18, sunStrength: 1,
      skyX: 0.16, skyY: -0.97, skyZ: 0.18,
      sunRefY: 0.97,
      sunR: 1, sunG: 1, sunB: 1, planeView: 0, night: 0,
      fog: [0, 0, 0], ambientLevel: 1, ext: [0, 0, 0], surfaceY: WORLD.surfaceY,
      skyAmount: 0, planeZ: 0,
      ambient: [0, 0, 0], normDepth: 0,
      wakeX: 0, wakeY: 0, wakeZ: 0, wakeVX: 0, wakeVY: 0, wakeThrust: 0,
      wakeSubU: 0.5, wakeSubV: 0.5, wakeGap: 0, wakeNitro: 0,
      lightX: 0, lightY: 0, lightZ: 0, lightRange: LIGHT.searchRange,
      lightDX: 1, lightDY: 0, lightDZ: 0, lightInner: LIGHT.searchInner,
      lightR: LIGHT.searchColor[0], lightG: LIGHT.searchColor[1], lightB: LIGHT.searchColor[2],
      lightOuter: LIGHT.searchOuter, lightIntensity: LIGHT.searchIntensity,
      sonarX: 0, sonarY: 0, sonarRadius: 0, sonarStrength: 0,
      coneAngle: 0, coneHalf: LIGHT.searchHalfAngle, coneBins: LIGHT.shadowBins,
      exposure: 1.25, distortion: 0.0016, vignette: 0.55, bloom: 0.85,
      damageFlash: 0,
    };
  }

  get stats(): Stats {
    return this.player.stats;
  }

  // ==========================================================================
  // update
  // ==========================================================================

  update(input: Input, dt: number) {
    // Kept for the frame builder: particle reprojection needs the step that
    // actually moved them, and `buildFrame` runs after `update` has returned.
    this.lastDt = dt;
    this.time += dt;
    this.playTime += dt;
    if (this.surfaceImpact) {
      this.surfaceImpact.strength *= Math.exp(-dt * 5.2);
      if (this.surfaceImpact.strength < 0.025) this.surfaceImpact = null;
    }
    this.blastVoices = 0;
    this.shieldSfx = Math.max(0, this.shieldSfx - dt);
    const p = this.player;

    // The player's own zoom and pan. Fed here and nowhere else: `update` runs
    // only while the game is actually being played, so a scroll over the shop
    // or the chart is discarded rather than banked up and applied on exit.
    this.camera.viewControls(
      input.state.zoomDelta, input.state.viewPanX, input.state.viewPanY,
      input.state.viewReset, input.state.viewTilt,
    );

    if (this.deathTimer > 0) {
      // The sub is gone; hold the camera on the wreck for a beat before the
      // respawn so the death has a moment rather than a cut.
      this.deathTimer -= dt;
      this.camera.update(p.x, p.y, 0, 0, dt, this.time);
      this.terrain.update(this.camera.viewX(), this.quality.chunkBudget);
      this.damageFlash = damp(this.damageFlash, 0, 2, dt);
      if (this.deathTimer <= 0) this.respawn();
      return;
    }

    // The boat has to know how heavy it is before it works out where it is
    // going, so this is the one thing that must happen ahead of `p.update`.
    p.cargoMass = this.tow.load ? this.tow.load.mass : 0;

    // THE MANIPULATOR FOLLOWS THE GRAPPLE.
    //
    // G already fires the tow hook off the belly port, and the description of
    // that shot is that the hook is LOWERED rather than fired — half its
    // initial speed is straight down. So the arm swinging out of the same port
    // as the line goes is not decoration bolted onto an existing action, it is
    // the action finally having something to come out of.
    //
    // Eased, not switched. The joints are 0.5 m of steel, and a manipulator
    // that reaches its stop in one frame reads as a sprite changing state.
    this.armDeploy = damp(this.armDeploy, this.tow.state !== 'idle' ? 1 : 0, 4.5, dt);
    // The claw closes only once the hook has something. Between firing and
    // catching it is open, which is what tells the player the grab has not
    // landed yet without a single pixel of HUD.
    this.armGrip = damp(this.armGrip, this.tow.state === 'attached' ? 1 : 0, 7, dt);

    this.updateLane(dt);
    this.gates(dt);
    p.update(input.state, dt, this.time);
    this.splash(p);
    this.surfaceWash(p, dt);
    if (input.state.lightPressed) p.lightOn = !p.lightOn;

    // Hook flight and the tether constraint sit here, between the boat moving
    // and the actors moving: once per frame, never inside Player's substep
    // loop, where the step count is a function of how fast you are going.
    this.cargo(input, dt);

    this.hazards(dt);

    this.weapons(input, dt);
    this.sonar.update(dt);
    // The hold readout collapses on H, or on the phone's HOLD button. Edge
    // triggered by the input layer, so holding it does not flap the panel.
    if (input.state.holdPressed) this.holdOpen = !this.holdOpen;
    // The tuning panel's one-shot. Cleared here rather than there, so the flag
    // cannot survive a frame in which nothing was listening.
    if (this.renderer.probe.resetOre) {
      this.renderer.probe.resetOre = false;
      this.resetOre();
    }
    this.pings(input);
    this.targeting(dt, input);

    const ping = this.sonar.primary;
    const pingCtx = ping ? { x: ping.x, y: ping.y, r: ping.r } : null;

    const c = this.actorCtx;
    c.px = p.x; c.py = p.y; c.pvx = p.vx; c.pvy = p.vy;
    c.playerLane = p.lane;
    c.playerAlive = !p.dead;
    c.lightOn = p.lightOn;
    c.lightAngle = p.lightAngle;
    c.noisy = p.speed > 11 || this.time - this.sonar.lastPingAt < 2.5;
    c.sonarPing = pingCtx;
    c.ramming = p.nitroOn && p.speed > RAM_SPEED;
    this.actors.update(dt, c);

    // After `actors.update`, so a round that was fired this frame has been
    // stepped and a round that hit an enemy is already dead.
    this.mineOre(dt);
    this.updateOctopus(dt, pingCtx);
    this.octopusCollisions();
    this.updateContacts(pingCtx);
    // Everything mode-specific in the whole update loop, in one call. Above and
    // below this line is universal simulation that every mode wants unchanged —
    // which is why there is no phase registry here.
    this.rules.tick(this, dt);

    if (this.pendingCarve) {
      const mid = (this.pendingCarve.x0 + this.pendingCarve.x1) * 0.5;
      this.terrain.invalidate(this.pendingCarve.x0, this.pendingCarve.x1);
      this.audio.rockBreak(Math.abs(mid - p.x));
      this.pendingCarve = null;
    }

    for (const b of this.baitBalls) b.update(this.time, p.x, p.y, this.camera.x);

    this.camera.update(p.x, p.y, p.vx, p.vy, dt, this.time);
    this.terrain.update(this.camera.viewX(), this.quality.chunkBudget);
    this.region = regionAt(p.x + LAYERS[Math.max(0, p.lane)]!.warp);
    this.reachedX = Math.max(this.reachedX, p.x);

    this.effects(dt);
    this.mixAudio(dt);

    // One writer for the zoom: whichever wants the widest frame this tick wins,
    // and it resets every frame so nothing can leave the camera stuck out wide.
    const boss = this.actors.activeBoss();
    if (boss) {
      const bd = hyp(boss.x - p.x, boss.y - p.y);
      if (bd < 260) this.framingZoom = Math.max(this.framingZoom, 1.7);
      // Two announcements, both earned: it wakes, and it loses its guns.
      if (!this.bossAwake) {
        this.bossAwake = true;
        this.audio.bossRoar('wake', bd);
        this.bossBanner = 4.5;
        this.bossBannerText = 'CAPITAL CONTACT';
        this.showToast('CAPITAL CONTACT', 'HOSTILE SUBMARINE — ENGAGE THE PODS', '#ff5a4a');
      }
      const podsGone = boss.podA <= 0 && boss.podB <= 0;
      if (podsGone && !this.bossEnraged) {
        this.bossEnraged = true;
        this.audio.bossRoar('rage', bd);
        this.showToast('WEAPONS DESTROYED', 'IT IS GOING TO RAM YOU', '#ff5a4a');
      }
    } else {
      this.bossAwake = false;
      this.bossEnraged = false;
    }
    // The boat drifts back in the frame as it accelerates, and further still on
    // the burners. The lead already scaled with velocity, but terminal speed is
    // terminal however you reached it — so nitro used to move the boat faster
    // without showing any more of what it was about to hit.
    this.camera.leadScale = 1 + this.nb('camera') * (CAMERA.nitroLead - 1);

    // PULL BACK WHEN THINGS ARE SHOOTING AT YOU.
    //
    // Survival got this first, and it is not a Survival problem: a firefight in
    // the campaign has exactly the same complaint, which is that the thing
    // shooting you is off the edge of the screen. Counts what is actually
    // ENGAGED rather than what merely exists, so swimming past a sleeping
    // picket does nothing and a real fight opens the frame up.
    let engaged = 0;
    for (const e of this.actors.enemies) {
      if (!e.alive || e.state !== 'attack') continue;
      const dx = e.x - p.x, dy = e.y - p.y;
      if (dx * dx + dy * dy < 150 * 150) engaged++;
      if (engaged >= 6) break;
    }
    if (engaged > 1) this.requestZoom(1 + Math.min(5, engaged - 1) * 0.045);

    // Base framing, and it is wider on a phone: the same 52 m of water on a
    // 6-inch screen held at arm's length is a much smaller picture than on a
    // monitor, and the thing that suffers is seeing what is about to hit you.
    const base = this.touchControls ? CAMERA.touchZoom : 1;
    // And it opens with SPEED, not just with the burner. A boat crossing the
    // frame in a second needs to see further ahead than one station-keeping,
    // and tying that to nitro alone made it a step change on a keypress rather
    // than something the framing does continuously as you wind on.
    const fast = Math.min(1, p.speed / Math.max(1, p.chassis.terminalX));
    const speedZoom = fast * fast * (CAMERA.speedZoom - 1);
    this.camera.targetZoom = Math.max(
      this.framingZoom,
      base + speedZoom + this.nb('camera') * (CAMERA.nitroZoom - 1),
    );
    this.framingZoom = 1;

    if (!this.toast && this.toastQueue.length) this.toast = this.toastQueue.shift()!;
    if (this.toast) {
      this.toast.time -= dt;
      if (this.toast.time <= 0) this.toast = null;
    }
    if (p.nitroOn !== this.nitroWas) {
      this.nitroWas = p.nitroOn;
      this.audio.nitro(p.nitroOn);
      if (!p.nitroOn && p.nitro < 1) this.audio.nitroEmpty();
    }
    const low = p.hull < p.stats.maxHull * 0.28 && !p.dead;
    if (low !== this.lowHullWas) {
      this.lowHullWas = low;
      this.audio.lowHull(low);
    }

    this.bossBanner = Math.max(0, this.bossBanner - dt);
    this.damageFlash = damp(this.damageFlash, 0, 4.5, dt);
    this.rewardFlash = damp(this.rewardFlash, 0, 3.4, dt);

    if (p.dead && this.deathTimer <= 0) this.onDeath();
  }

  /**
   * Attract mode: the world keeps running behind the title card.
   *
   * The first thing anyone sees should be the game rendering itself — kelp
   * swaying, medusae breathing, light shafts moving, sediment drifting past a
   * sub with its lamp on — not a painted gradient. Gameplay is untouched: this
   * only advances the clock, flies the camera, and idles the sub along the
   * sunlit shelf. Nothing can shoot at it, because the actors never tick.
   */
  attract(dt: number) {
    this.time += dt;
    this.attractT += dt;
    const p = this.player;
    const parts = this.renderer.particles;

    // A slow loop along the shelf, where the shafts and the kelp stands are.
    const span = 540;
    const x = 150 + ((this.attractT * 6.2) % span);
    const glide = Math.sin(this.attractT * 0.31);
    const y = Math.min(-16, seabedY(x) + 25 + glide * 6);

    p.x = x;
    p.y = y;
    p.vx = 6.2;
    p.vy = Math.cos(this.attractT * 0.31) * 1.9;
    p.facing = 1;
    p.lightOn = true;
    p.dead = false;
    p.thrustLevel = damp(p.thrustLevel, 0.42, 2, dt);
    p.propSpin += (2 + p.thrustLevel * 26 + p.vx * 0.5) * dt;
    p.yaw = dampAngle(p.yaw, 0, 6, dt);
    p.pitch = damp(p.pitch, clamp(p.vy * 0.07, -0.28, 0.28), 3, dt);
    p.roll = damp(p.roll, glide * 0.12, 2.4, dt);
    p.lightAngle = dampAngle(p.lightAngle, p.pitch, 4.5, dt);

    // The camera deliberately does not sit on the sub: it leads by a wandering
    // offset so the boat drifts around the lower left of the frame instead of
    // parking behind the wordmark and the menu rows.
    const camX = x + 26 + Math.sin(this.attractT * 0.17) * 13;
    const camY = y + 11 + Math.sin(this.attractT * 0.23) * 4;
    this.camera.update(camX, camY, p.vx, p.vy, dt, this.time);
    this.camera.targetZoom = 1.22;
    this.terrain.update(this.camera.viewX(), this.quality.chunkBudget);
    this.region = regionAt(x);

    // The wake, and the odd puff off the seabed — the water has to look moved
    // through, not just looked at. Laid on thicker than in play: this shot has
    // no combat in it, so the life is the whole picture.
    this.bubbleTimer -= dt;
    if (this.bubbleTimer <= 0) {
      this.bubbleTimer = 0.045;
      parts.bubbles(x - this.player.chassis.hardpoints.wake, y, 3, 1.0, 2.6, 0.13);
    }
    const floor = seabedY(x);
    if (Math.random() < dt * 9) {
      parts.sediment(camX + (Math.random() - 0.5) * 90, floor + 0.8, 1, 0.45);
    }
    // A thin curtain of rising bubbles somewhere off in the water.
    if (Math.random() < dt * 3.5) {
      parts.bubbles(
        camX + (Math.random() - 0.5) * 110,
        floor + 2 + Math.random() * 10,
        2, 1.6, 3.4, 0.1,
      );
    }

    // Keeps the decorative medusae and the station beacons animating.
    this.actors.time += dt;
    for (const st of this.actors.stations) st.phase += dt;

    this.audio.update(dt, {
      throttle: 0.42, speed: p.vx, depth: p.depth,
      intensity: 0, boost: false, mood: 'calm',
    });
  }

  // --- hazards --------------------------------------------------------------

  /**
   * Vent plumes and rift currents. Both push; only the vents burn. They are
   * cheap enough to test against every frame because there are a few dozen of
   * them in the whole world, all in two zones.
   */
  private hazards(dt: number) {
    const p = this.player;
    const parts = this.renderer.particles;
    for (const h of this.rules.content.hazards) {
      const dx = p.x - h.x;
      if (Math.abs(dx) > h.r * 2.4) continue;
      if (h.kind === 'vent') {
        // The plume is a tall column, not a ball: wide in x, unbounded upward.
        const dy = p.y - h.y;
        if (dy < -h.r * 0.5 || dy > h.r * 4.5) continue;
        const k = clamp(1 - Math.abs(dx) / h.r, 0, 1) * clamp(1 - dy / (h.r * 4.5), 0.25, 1);
        if (k <= 0.02) continue;
        p.vy += h.power * k * dt * 2.2;
        p.graze(h.power * k * dt);
        if (Math.random() < dt * 30 * k) {
          parts.bubbles(p.x + (Math.random() - 0.5) * 5, p.y - 2, 1, 1.4, 6, 0.2);
        }
        this.damageFlash = Math.min(0.4, this.damageFlash + k * dt * 0.9);
      } else {
        const dy = p.y - h.y;
        const d = hyp(dx, dy);
        if (d > h.r) continue;
        const k = 1 - d / h.r;
        p.vx += (h.dx ?? 0) * h.power * k * dt;
        p.vy += (h.dy ?? 0) * h.power * k * dt;
      }
    }
  }

  // --- weapons --------------------------------------------------------------

  private weapons(input: Input, dt: number) {
    const p = this.player;
    const S = p.stats;
    this.cannonCooldown -= dt;
    this.torpedoCooldown -= dt;
    this.chargeCooldown -= dt;
    // WEAPONS STOW IN THE WATER BETWEEN LANES.
    //
    // A shot carries the lane it was fired in, and `laneAtZ` reports -1 out
    // here, which matches no creature and no rock: the round was created, cost
    // its energy or its last torpedo, drew its flash and its recoil, and then
    // flew through the world hitting nothing. A torpedo was worse than nothing
    // — `detonate` clamps the blast back to lane 0, so it went off at the end
    // of its run and carved a crater somewhere the player never aimed.
    //
    // Firing into nowhere is not a thing to make work; it is a thing not to
    // offer. The gating it fails is the same rule that makes the sub
    // untouchable out here, and it is right. Cooldowns still tick, so the
    // crossing is not also a punishment.
    if (p.dead || p.lane < 0) return;

    // The overdrive used to be three ternaries here, reaching into POWER and
    // testing a timer on the player. It is a modifier now, so these are just
    // the stats — and Survival's run upgrades will arrive through the same door
    // rather than adding a fourth ternary each.
    const energyCost = S.cannonEnergy;

    if (input.state.fire && this.cannonCooldown <= 0 && p.energy >= energyCost) {
      this.cannonCooldown = S.cannonCooldown;
      p.energy -= energyCost;
      // THE GUN, NOT THE HULL. `aimAngle` is where the boat is pointing; the
      // cannon is on a stand that turns, and until now the stand turned in the
      // render and the shot left along the keel regardless. Everything below
      // follows from the barrel: the fan is centred on it, the recoil pushes
      // back along it, and the muzzle flash and bubbles come off the emitter
      // `muzzle` now swings with it.
      const a = p.gunAngle;
      p.muzzle(this.muzzlePos);
      const shots = S.cannon.shots;
      const dmg = S.cannonDamage;
      for (let i = 0; i < shots; i++) {
        // Centre the fan on the aim axis whether the count is odd or even.
        const off = shots === 1 ? 0 : (i / (shots - 1) - 0.5) * 2 * S.cannon.spread;
        this.actors.fireBullet(
          this.muzzlePos.x, this.muzzlePos.y, a + off, 'player',
          WEAPONS.cannon.speed, dmg, p.lane, p.z,
        );
      }
      // Recoil, flash and a puff of bubbles from the barrel.
      p.vx -= Math.cos(a) * 1.5;
      p.vy -= Math.sin(a) * 1.5;
      this.renderer.particles.bubbles(this.muzzlePos.x, this.muzzlePos.y, 3, 0.5, 3, 0.1);
      this.camera.addShake(0.05 + shots * 0.008);
      this.audio.cannon(0);
    }

    if (input.state.torpedoPressed && this.torpedoCooldown <= 0 && p.torpedoes > 0) {
      this.torpedoCooldown = T.cooldown;
      p.torpedoes--;
      const a = p.aimAngle();
      const locked = this.lockProgress < 1 ? null : this.targetIsBoss ? this.octopus : this.target;
      // Out of a flank tube, alternating sides, rather than out of the nose.
      // The bow holds the sonar dome and the searchlight; the saddle pods on
      // the flanks are the tubes, and they are what the eye reads as tubes.
      const TUBE = p.chassis.hardpoints.tube;
      this.tubeSide = -this.tubeSide;
      const tx = p.x + Math.cos(a) * TUBE.x;
      const ty = p.y + Math.sin(a) * TUBE.x - TUBE.y;
      this.actors.fireTorpedo(
        tx, ty, a, 'player', locked,
        {
          damage: S.torpedoDamage, blast: S.torpedoBlast,
          z: TUBE.z * this.tubeSide, lane: p.lane, baseZ: p.z,
        },
      );
      this.renderer.particles.bubbles(tx, ty, 10, 1.2, 5, 0.16);
      this.camera.addShake(0.14);
      this.audio.torpedo(0);
    }

    if (input.state.chargePressed && this.chargeCooldown <= 0 && p.charges > 0 && S.chargeUnlocked) {
      this.chargeCooldown = CH.cooldown;
      p.charges--;
      this.actors.dropCharge(
        p.x, p.y - p.chassis.hardpoints.chargeDrop, p.vx, p.vy, p.lane, p.z,
      );
      this.renderer.particles.bubbles(p.x, p.y - 2, 6, 1, 2, 0.14);
      this.audio.torpedo(0);
    }
  }

  private targeting(dt: number, input: Input) {
    const p = this.player;
    const aim = p.aimAngle();

    // Holding the lock control keeps the current target; otherwise re-acquire.
    let candidate: Enemy | null =
      input.state.lock && this.target && this.target.alive ? this.target : null;
    // In the sub's own lane. Locking something unreachable is worse than not
    // locking: the torpedo launches, steers at a target 431.7 m through rock
    // and buries itself in the near wall, with a bracket on the HUD the whole
    // way saying it is working.
    if (!candidate) candidate = this.actors.lockCandidate(p.x, p.y, aim, p.lane);

    // The octopus body is a lockable component too (PRD §13).
    const o = this.octopus;
    let boss = false;
    // The octopus lives in the near lane and has no lane of its own.
    if (o.alive && o.awake && p.lane === 0) {
      const d = hyp(o.x - p.x, o.y - p.y);
      const off = Math.abs(Math.atan2(o.y - p.y, o.x - p.x) - aim);
      if (d < WEAPONS.lock.range && off < WEAPONS.lock.cone) {
        const cd = candidate ? hyp(candidate.x - p.x, candidate.y - p.y) : Infinity;
        if (d < cd) { candidate = null; boss = true; }
      }
    }

    if (!candidate && !boss) {
      this.target = null;
      this.targetIsBoss = false;
      this.lockProgress = Math.max(0, this.lockProgress - dt * WEAPONS.lock.decay);
      return;
    }

    if (candidate !== this.target || boss !== this.targetIsBoss) {
      this.target = candidate;
      this.targetIsBoss = boss;
      this.lockProgress = Math.max(0, this.lockProgress - dt * 4);
      if (this.lockProgress > 0.02) return;
    }
    this.lockProgress = Math.min(1, this.lockProgress + dt / WEAPONS.lock.time);
  }

  private pings(input: Input) {
    const p = this.player;
    if (!input.state.sonarPressed || p.dead) return;
    if (this.sonar.cooldown > 0 || p.energy < p.stats.sonarCost) return;
    p.energy -= p.stats.sonarCost;
    this.sonar.emit(p.x, p.y, this.time, p.stats.sonarRange);
    this.audio.ping();

    if (p.stats.pulseOn) {
      // The concussive front lands at once, right where the player is standing.
      this.actors.pulse(
        p.x, p.y, p.stats.pulseRadius, p.stats.pulseDamage,
        SONAR.pulseKnockback, this.actorCtx, p.lane,
      );
      this.renderer.particles.sparks(p.x, p.y, 0, 1, 26, 1.4);
      this.camera.addShake(0.2);
    }
  }

  // --- octopus --------------------------------------------------------------

  private updateOctopus(dt: number, ping: { x: number; y: number; r: number } | null) {
    const o = this.octopus;
    const p = this.player;
    const parts = this.renderer.particles;
    const dist = hyp(o.x - p.x, o.y - p.y);
    // A defeated octopus is not a participant. The campaign only ever retires
    // one after it has been killed and left behind, so the guard was never
    // needed; Survival retires one that is 20 m from the spawn and waiting for
    // wave 10, and without this it wakes on proximity and sweeps the arena
    // from the first second of the run with zero hit points.
    if (o.defeated) return;
    if (dist > 420 && !o.awake) return;

    const wasAwake = o.awake;
    o.update(dt, {
      px: p.x, py: p.y,
      playerAlive: !p.dead,
      lightOn: p.lightOn && dist < 130,
      sonarPinged: !!ping && hyp(ping.x - o.x, ping.y - o.y) < ping.r + 30,
      damagePlayer: (n) => this.hurtPlayer(n, true),
      pullPlayer: (x, y, strength) => {
        const dx = x - p.x, dy = y - p.y;
        const d = hyp(dx, dy) || 1;
        p.vx += (dx / d) * strength * dt;
        p.vy += (dy / d) * strength * dt;
      },
      onInk: (x, y) => parts.ink(x, y, 60),
      onSediment: (x, y, n, power) => parts.sediment(x, y, n, power),
      onShake: (n) => this.camera.addShake(n),
      onRoar: (kind) => this.audio.creature(kind, dist),
    });

    if (!wasAwake && o.awake) {
      this.bossBanner = 4.5;
      this.bossBannerText = 'UNKNOWN CONTACT';
      this.showToast('UNKNOWN CONTACT', 'MASS EXCEEDS SENSOR RANGE', '#c98cff');
    }

    // Frame the creature: pull the camera back when it is on screen (PRD §4).
    if (o.awake && o.state !== 'dead' && dist < 190) this.framingZoom = Math.max(this.framingZoom, 1.45);

    if (o.state === 'dead' && !this.defeatedBosses.has('octopus')) {
      this.defeatedBosses.add('octopus');
      this.player.score += 5000;
      this.purse(250);
      this.showToast('CONTACT NEUTRALISED', '+5000  ·  +250 SALVAGE', '#ffb454');
      this.rewardFlash = 1;
    }
  }

  private octopusCollisions() {
    const o = this.octopus;
    if (!o.alive) return;
    const parts = this.renderer.particles;

    for (const pr of this.actors.projectiles) {
      // The boss is a lane-0 creature. Without this a shot fired in another
      // lane is CONSUMED here and credited as a hit on something it is nowhere
      // near, which reads as ammunition that simply vanishes.
      if (!pr.alive || pr.from !== 'player' || pr.lane !== 0) continue;
      let hit = false;
      if (o.bodyHit(pr.x, pr.y, pr.radius)) {
        o.hurtBody(pr.damage * 1.4);
        hit = true;
      } else {
        const t = o.hitTest(pr.x, pr.y, pr.radius);
        if (t) { o.hurtTentacle(t.tentacle, pr.damage); hit = true; }
      }
      if (!hit) continue;
      pr.alive = false;
      if (pr.blast > 0) this.explode(pr.x, pr.y, pr.blast / 11);
      else parts.sparks(pr.x, pr.y, -pr.vx, -pr.vy, 12, 0.7);
    }

    for (const b of this.actors.blasts) {
      if (b.from !== 'player' || b.lane !== 0) continue;
      if (hyp(o.x - b.x, o.y - b.y) < b.radius + o.bodyRadius) o.hurtBody(b.damage * 0.9);
    }
  }

  // --- sonar contacts -------------------------------------------------------

  private updateContacts(ping: { x: number; y: number; r: number } | null) {
    if (!ping) return;
    const s = this.sonar;
    // The sub's own lane only. A contact is a mark on the display at a screen
    // position, and one painted from another lane puts a bogey where there is
    // visibly nothing — the player flies at it and finds open water.
    const lane = this.player.lane;
    for (const e of this.actors.enemies) {
      if (!e.alive || e.lane !== lane) continue;
      if (Math.abs(hyp(e.x - ping.x, e.y - ping.y) - ping.r) < 14) {
        s.addContact(e.x, e.y, e.boss ? 'unknown' : 'mechanical',
                     e.boss ? { label: 'CAPITAL CONTACT' } : {});
      }
    }
    for (const pk of this.actors.pickups) {
      if (!pk.alive || pk.id === '' || pk.lane !== lane) continue;
      if (Math.abs(hyp(pk.x - ping.x, pk.y - ping.y) - ping.r) < 14) {
        s.addContact(pk.x, pk.y, 'objective', { label: pk.label });
      }
    }
    for (const st of this.actors.stations) {
      if (Math.abs(hyp(st.x - ping.x, st.y - ping.y) - ping.r) < 16) {
        s.addContact(st.x, st.y, 'objective', { label: st.name });
      }
    }
    for (const poi of this.rules.content.pois) {
      if (Math.abs(hyp(poi.x - ping.x, poi.y - ping.y) - ping.r) < 16) {
        s.addContact(poi.x, poi.y, 'objective', { label: poi.label });
      }
    }
    const o = this.octopus;
    if (o.alive && Math.abs(hyp(o.x - ping.x, o.y - ping.y) - ping.r) < 40) {
      s.addContact(o.x, o.y, 'unknown', { label: 'LARGE BIOLOGICAL' });
    }

    // ORE. A survey instrument that cannot find a seam is a strange thing to
    // have on a mining submarine, and the deposits were already the only
    // objects in the world you have to go looking for.
    //
    // One echo per DEPOSIT, not one per cluster: `deposit` is non-zero on
    // exactly the dominant cluster of a group and carries how many are in it,
    // so a mother lode is one mark on the display rather than eleven on top of
    // each other. That flag was added for the point light and pays for itself
    // twice.
    //
    // The chunk window is the ping's own reach rather than the camera's — a
    // ping is 300-odd metres of range and the frame is 68, so surveying only
    // what is on screen would be answering a question nobody asked.
    const c0 = this.terrain.chunkAt(ping.x - ping.r - WORLD.chunkSize);
    const c1 = this.terrain.chunkAt(ping.x + ping.r + WORLD.chunkSize);
    for (let ci = c0; ci <= c1; ci++) {
      for (const prop of propsForChunk(ci, Math.max(0, lane))) {
        if (prop.kind !== 'mineral' || !prop.deposit) continue;
        // A seam that has been mined out is not a contact. Painting one is
        // worse than painting nothing: it sends the player back to a hole.
        if (this.minedOre.has(this.oreKey(prop, Math.max(0, lane)))) continue;
        if (Math.abs(hyp(prop.x - ping.x, prop.y - ping.y) - ping.r) >= 16) continue;
        const ore = ORE[prop.variant];
        const type = MINERAL_TYPES[prop.variant];
        if (!ore || !type) continue;
        const n = prop.deposit;
        const colour = oreHudColour(type.colour);
        const detail = `${depositName(n)}  ·  ${n} CLUSTER${n === 1 ? '' : 'S'}` +
                       `  ·  ${type.usedFor.toUpperCase()}`;
        // THE ECHO SITS ON THE CRYSTAL, NOT ON ITS ORIGIN.
        //
        // A cluster's origin is its lowest point, and the placement sinks that
        // into the boulder on purpose — so a mark at `prop.y` lands inside the
        // rock, under the thing it is pointing at, and the leader line appears
        // to come out of the ground beside the crystal rather than off it.
        // Half the cluster's drawn height puts it on the body. Same height the
        // point light uses, and for the same reason.
        const dial = ore.kind === 'metal'
          ? this.renderer.seaTune.metalOreScale : this.renderer.seaTune.crystalScale;
        const hgt = (ORE_HEIGHT.get(`node_${ore.key}`) ?? 0.8)
          * prop.scale * Math.max(0.05, dial);
        // Four times the default six and a half seconds. See `Contact.life`:
        // a bogey's echo decays because the bogey moved, and a seam does not.
        // NO BANNER, and the return value goes unread for that reason. A toast
        // said exactly what the callout says, in letters four times the size,
        // across the middle of the frame — two readings of one sentence, one
        // of them covering the thing it was about. A toast is for events with
        // nowhere on screen to point at; a deposit has somewhere, and a leader
        // line already pointing at it.
        // THE DEPOSIT'S OWN Z, and leaving it out is what put the leader in
        // open water. A cluster sits anywhere from z -7 to +1.5 and further
        // again on a background lane; anchoring the label at 0 projects it
        // through a different point in space from the rock it points at, and
        // at sixty-odd metres of standoff that is hundreds of pixels.
        s.addContact(prop.x, prop.y + hgt * 0.5, 'mineral', {
          label: ore.label, sub: detail, colour, rgb: oreHudRgb(type.colour),
          life: SONAR.contactLife * 4, z: prop.z,
        });
      }
    }
  }

  /**
   * Teach in place. Each line fires once, where the thing it explains is about
   * to matter — a child should never have to be told to read anything.
   */
  hints() {
    const p = this.player;
    // Onboarding lines are pinned to places in the near lane.
    if (p.lane !== 0) return;
    for (const h of HINTS) {
      if (this.seenHints.has(h.id)) continue;
      if (Math.abs(h.x - p.x) > h.r) continue;
      this.seenHints.add(h.id);
      this.showToast(h.text, this.touchControls ? (h.touch ?? h.key) : h.key, '#5fe4e0');
      return;
    }
  }

  /** An onboarding line tied to something that just happened. */
  eventHint(id: keyof typeof EVENT_HINTS) {
    if (this.seenHints.has(id)) return;
    const h = EVENT_HINTS[id];
    if (!h) return;
    this.seenHints.add(id);
    this.showToast(h.text, this.touchControls ? (h.touch ?? h.key) : h.key, '#5fe4e0');
  }

  discoveries() {
    const p = this.player;
    // The POIs are authored in the near lane, so nothing out in the parallax
    // lanes can stumble on one — it would be a discovery of a place the sub is
    // several hundred metres from.
    if (p.lane !== 0) return;
    for (const poi of this.rules.content.pois) {
      if (this.seenPois.has(poi.id)) continue;
      if (hyp(poi.x - p.x, poi.y - p.y) > 42) continue;
      this.seenPois.add(poi.id);
      p.score += SCORE.discovery;
      this.showToast('DISCOVERY', poi.label, '#5fe4e0');
    }
  }

  // --- stations and progression --------------------------------------------

  stationCheck() {
    const p = this.player;
    // Stations are in the near lane, and a sub mid-transit is in none.
    const near = p.lane === 0 ? this.actors.stationInRange(p.x, p.y) : null;
    this.dockable = p.dead ? null : near;

    if (near && !near.active) {
      // First arrival docks on its own — a checkpoint you can walk past is a
      // checkpoint that gets walked past.
      this.dock(near);
      this.pendingRefit = true;
    }
  }

  /** Repair, refill, save and open the refit. */
  dock(s: Station) {
    const p = this.player;
    const first = !s.active;
    s.active = true;
    this.activatedStations.add(s.id);
    this.lastStation = s.id;
    p.repair(p.stats.maxHull);
    p.rechargeShield();
    p.addEnergy(p.stats.maxEnergy);
    p.addNitro(p.stats.nitroMax);
    p.torpedoes = Math.max(p.torpedoes, Math.min(p.stats.torpedoMax, 8));
    if (p.stats.chargeUnlocked) p.charges = Math.max(p.charges, 3);
    p.invuln = Math.max(p.invuln, 1.6);
    // THE HOLD IS NOT SOLD HERE. Landing it automatically on arrival made the
    // ore vanish into the salvage counter with nothing to look at and nothing
    // to decide — and once refineries exist, WHERE a mineral goes is the whole
    // question. The shop has a hold screen; docking just makes it reachable.
    this.rewardFlash = 1;
    this.audio.dock(true);
    this.telemetry('dock', { station: s.id, first: first ? 1 : 0 });
    if (first) {
      p.score += SCORE.station;
      this.showToast('STATION ONLINE', s.name, '#5fe4e0');
    } else {
      this.showToast('REFITTED', s.name, '#5fe4e0');
    }
    this.save();
  }

  /**
   * What the station would pay for whatever is on the line right now, or 0.
   * Face value plus the delivery premium — the reason to drag a thing home
   * rather than to blow it up and scoop the pieces.
   */
  get cargoValue(): number {
    const load = this.tow.load;
    if (!load || !this.dockable) return 0;
    // Graded by how far it has actually come. `homeX` is where the thing was
    // authored, so this pays for the journey rather than for the last 500 m —
    // which is the difference between heavy salvage and a shuffle.
    const km = Math.min(CARGO.haulCap, Math.max(0, Math.abs(load.homeX - this.dockable.x) / 1000));
    return Math.round(load.amount * (CARGO.deliveryBonus + CARGO.haulBonus * km));
  }

  /** Hand the load over. Returns what it paid, or 0 if there was nothing to sell. */
  sell(): number {
    const load = this.tow.load;
    const paid = this.cargoValue;
    if (!load || !paid) return 0;

    this.purse(paid);
    this.player.score += Math.round(paid * SCORE.salvagePerUnit);
    // Marked taken as well as dead: `applySave` reads `takenPickups`, and a
    // sold crate that came back on the next reload would be an infinite
    // salvage printer.
    load.alive = false;
    if (load.id) this.takenPickups.add(load.id);
    this.dropCargo();

    this.rules.onDeliver?.(this, load.id, paid);
    this.telemetry('sold', { id: load.id, mass: load.mass, paid });
    this.rewardFlash = 1;
    this.audio.upgradeBought();
    this.showToast('CARGO DELIVERED', `+${paid} SALVAGE  ·  ${load.label}`, '#8fd9ff');
    this.save();
    return paid;
  }

  /**
   * Put the player in a different boat.
   *
   * The modules stay owned — a hull swap re-derives what they are worth on the
   * new chassis, and anything the new hull cannot fit is clamped to 0 by
   * `statsFor` rather than silently carried. Run-scoped modifiers survive; a
   * boat change is not a new run.
   */
  setChassis(c: Chassis) {
    const p = this.player;
    if (p.chassis.id === c.id) return;
    // WHICH BOAT YOU ARE IN IS ACCOUNT STATE, and nothing was writing it.
    //
    // `profile.active` is read in three places to decide the hull a dive
    // starts in, and was assigned in exactly one — the migration, out of the
    // value it was loading. So switching hulls changed the boat for the
    // session and nothing else: every resume, respawn and mode change re-read
    // a field that had never been updated, and put the player back in the
    // Pathfinder.
    this.profile.active = c.id;
    // Whatever is on the line stays in the water. Swapping hulls mid-tow with
    // the cable attached would hand the new boat a load off the old one's
    // winch, at the old one's fairlead.
    this.dropCargo();
    p.setChassis(c, statsFor(this.levelsFor(c.id), c));
    this.camera.followChassis(c.terminalX, c.terminalY, PATHFINDER.terminalX, PATHFINDER.terminalY);
    this.showToast('HULL CHANGE', c.name, '#5fe4e0');
    this.telemetry('chassis', { id: c.id });
  }

  /**
   * Buy a hull, or step into one already owned. Returns false when neither is
   * possible — the shop answers a refusal as loudly as a purchase, so silence
   * would read as a bug.
   */
  buyHull(id: ChassisId): boolean {
    const c = CHASSIS[id];
    if (!c) return false;
    if (this.player.chassis.id === id) return false;
    if (this.hulls.has(id)) { this.setChassis(c); this.save(); return true; }
    if (this.salvage < c.price) return false;
    this.salvage -= c.price;
    this.hulls.add(id);
    // A new hull starts with nothing structural fitted — but every piece of kit
    // already owned comes across with it, which is the whole reason a second
    // boat is a different tool rather than a demotion.
    if (!this.fleet.has(id)) this.fleet.set(id, emptyLevels());
    this.setChassis(c);
    this.audio.upgradeBought();
    this.rewardFlash = 1;
    this.telemetry('hull-bought', { id, paid: c.price });
    this.save();
    return true;
  }

  /** Spend salvage on a module. Returns false when it cannot be bought. */
  buy(id: keyof Levels, cost: number): boolean {
    // The shop's `cost !== null` was the only thing standing between a player
    // and an unbounded level: `statsFor` clamps cannon and hull, but sonar,
    // engine, torpedo and lamp scale linearly forever. A UI is not a guard.
    if (!this.player.chassis.modules.includes(id)) return false;
    if (this.levels[id] >= maxLevel(id)) return false;
    if (this.salvage < cost) return false;
    this.salvage -= cost;
    this.ledger(id)[id]++;
    this.player.applyStats(statsFor(this.levels, this.player.chassis));
    this.audio.upgradeBought();
    // Buying capacity should feel immediate, not "next time you dock".
    this.player.rechargeShield();
    this.rewardFlash = 1;
    this.save();
    return true;
  }

  /**
   * The grapple, wired to the world's effect sinks. `cargo.ts` knows nothing
   * about audio, particles or toasts; it reports what happened and this decides
   * what that sounds and looks like.
   */
  /**
   * A flight recorder, for the playthrough nobody has done yet.
   *
   * The balance table in `docs/NEXT.md` comes from a simulation flying a pilot
   * that never dodges, and the last three zones have never been judged by a
   * person. This records what somebody actually did — where they died, what
   * they hauled, what they lost, how long each region took — so the re-balance
   * after the first real run argues from evidence instead of from memory.
   *
   * Deliberately not part of `SaveData`: it is diagnostic, it is capped, and
   * losing it must never cost anyone their progress.
   */
  private log: string[] = [];

  telemetry(kind: string, data: Record<string, string | number> = {}) {
    const p = this.player;
    const bits = Object.entries(data).map(([k, v]) => `${k}=${typeof v === 'number' ? Math.round(v * 100) / 100 : v}`);
    this.log.push(
      `${this.playTime.toFixed(1)} ${kind} x=${p.x.toFixed(0)} y=${p.y.toFixed(0)} ` +
      `region=${this.region.name.replace(/ /g, '_')} hull=${p.hull.toFixed(0)} ` +
      `salvage=${this.salvage} ${bits.join(' ')}`.trimEnd(),
    );
    // Oldest first. A long session should not be able to fill localStorage and
    // start throwing quota errors inside a save.
    if (this.log.length > 900) this.log.splice(0, this.log.length - 900);
  }

  /** Write the recorder out. Called wherever the save is flushed. */
  flushTelemetry() {
    if (!this.log.length) return;
    try {
      localStorage.setItem('abyss.telemetry.v1', this.log.join('\n'));
    } catch { /* quota or private mode; it is only diagnostics */ }
  }

  /** Cut the line and leave whatever was on it where it is. */
  dropCargo() {
    if (this.tow.load) {
      this.tow.load.hold = 'free';
      this.tow.load.vx = 0;
      this.tow.load.vy = 0;
    }
    this.tow = newTowLine();
    this.nearCargo = null;
    this.player.cargoMass = 0;
  }

  private cargo(input: Input, dt: number) {
    driveTow(this.tow, this.player, this.actors.pickups, input.state, dt, this.towHooks);
    // The line can also part inside the constraint, which is where it usually
    // does — the same sinks serve both.
    solveTow(this.tow, this.player, this.towHooks);

    // Nearest free cargo in reach, for the HUD prompt and the phone's
    // contextual GRAB button. A button that is always there is a button nobody
    // reads; one that appears when it can do something teaches itself.
    this.nearCargo = null;
    if (this.tow.state === 'idle') {
      const p = this.player;
      let best: number = GRAPPLE.range;
      for (const pk of this.actors.pickups) {
        if (!pk.alive || pk.mass <= 0 || pk.hold !== 'free' || !pk.revealed) continue;
        const d = hyp(pk.x - p.x, pk.y - p.y);
        if (d < best) { best = d; this.nearCargo = pk; }
      }
    }
  }

  /**
   * Take salvage, if this mode has any. Survival has no currency at all, which
   * is what stops a run's power being bought into permanence.
   *
   * Public so a mode can pay a bonus through the same door everything else uses
   * — Expedition's contract premium has to obey `persists` exactly like a sold
   * crate does, and a second `salvage +=` somewhere else is how that rule gets
   * quietly broken later.
   */
  purse(n: number) {
    if (this.rules.persists) this.salvage += n;
  }

  private collect(pk: Pickup) {
    // Cargo is never collected by touching it. `updatePickups` already gates
    // this, and so does the tow line; this is the third door on the same room,
    // because a crate silently vanishing into the score is the worst possible
    // failure of the mechanic.
    if (pk.mass > 0) return;

    const p = this.player;
    const def = PICKUP_DEF[pk.type];
    let sub = def.label;
    switch (pk.type) {
      case 'salvage':
        this.purse(pk.amount);
        p.score += Math.round(pk.amount * SCORE.salvagePerUnit);
        sub = `+${pk.amount} SALVAGE`;
        break;
      case 'repair':
        p.repair(pk.amount);
        sub = `HULL +${Math.round(pk.amount)}`;
        break;
      case 'shield':
        p.rechargeShield();
        sub = 'SHIELD RESTORED';
        break;
      case 'energy':
        p.addEnergy(p.stats.maxEnergy);
        sub = 'POWER RESTORED';
        break;
      case 'nitro':
        p.addNitro(pk.amount);
        sub = 'NITRO REFILLED';
        break;
      case 'torpedo':
        p.addTorpedoes(pk.amount);
        sub = `TORPEDOES +${pk.amount}`;
        break;
      case 'charge':
        if (p.stats.chargeUnlocked) {
          p.addCharges(pk.amount);
          sub = `DEPTH CHARGES +${pk.amount}`;
        } else {
          // No bay to put them in yet — strip them for parts rather than
          // handing the player a pickup that visibly does nothing.
          this.purse(40);
          sub = 'CHARGES STRIPPED  ·  +40 SALVAGE';
        }
        break;
      case 'overdrive':
        p.grantOverdrive();
        sub = 'WEAPONS OVERCHARGED';
        break;
      case 'aegis':
        p.grantAegis();
        sub = 'AEGIS FIELD UP';
        break;
      case 'life':
        p.lives++;
        sub = `SPARE HULL  ·  ${p.lives} LEFT`;
        break;
      case 'module':
        this.purse(pk.amount);
        sub = `SALVAGED MODULE  ·  +${pk.amount}`;
        break;
      case 'ore': {
        // BOTH: the manifest and the money. The hold is what a refinery or
        // another player trades against, and the salvage is what the refit
        // screen spends — a shard that paid only one of them would either be
        // an inventory number with no use or a coin with no identity.
        const o = ORE[pk.ore ?? -1];
        if (o) {
          this.collectOre(pk.ore!, 1);
          this.purse(o.value);
          p.score += Math.round(o.value * SCORE.salvagePerUnit);
          sub = `${o.label}  ·  +${o.value}`;
        }
        break;
      }
    }
    if (pk.id) this.takenPickups.add(pk.id);
    // `powerup` switches on a kind it has never heard of; a shard is a
    // salvage-shaped event and sounds like one.
    this.audio.powerup(pk.type === 'ore' ? 'salvage' : pk.type);
    this.rewardFlash = Math.max(this.rewardFlash, pk.id ? 1 : 0.4);
    // Loot is constant; only announce the authored finds and the loud powerups.
    const loud = pk.id !== '' || pk.type === 'overdrive' || pk.type === 'aegis' || pk.type === 'life';
    if (loud) this.showToast('RECOVERED', sub, '#ffb454');
  }

  private onKill(e: Enemy) {
    // Remember it, so CONTINUE does not hand the player back a minefield they
    // already cleared. Bosses are tracked separately by `defeatedBosses` and
    // deliberately come back to full strength on respawn, so they are excluded.
    if (e.zoneKey && !e.boss) this.clearedEnemies.add(e.zoneKey);
    this.purse(e.salvage);
    if (!this.rules.persists) survivalKill(e.score);
    // A carrier pays out what it was carrying, thrown clear of the wreck so it
    // does not settle inside whatever killed it.
    if (e.carrier) {
      this.actors.spawnLoot(e.x, e.y + 2, e.carrier, undefined, -e.facing * 4, 6, { lane: e.lane });
      this.rewardFlash = 1;
      this.showToast('CARRIER BREACHED', PICKUP_DEF[e.carrier].label, '#ffb454');
    }
    if (e.boss) this.audio.bossRoar('die', hyp(e.x - this.player.x, e.y - this.player.y));
    if (e.boss && e.id) {
      this.defeatedBosses.add(e.id);
      this.player.score += 4000;
      this.showToast('CAPITAL CONTACT DESTROYED', '+4000  ·  THE BERTH IS CLEAR', '#ffb454');
      this.rewardFlash = 1;
      this.camera.addShake(1);
      this.save();
    }
  }

  checkFinale() {
    if (this.victory) return;
    const p = this.player;
    if (hyp(p.x - FINALE.x, p.y - FINALE.y) > FINALE.radius) return;
    this.victory = true;
    p.score += 25000;
    this.rewardFlash = 1;
    this.showToast('THE HEART', 'YOU MADE IT TO THE BOTTOM', '#ffb454');
    this.save();
  }

  // --- death and respawn ----------------------------------------------------

  private onDeath() {
    const p = this.player;
    // Drop the load here, on the frame the sub dies, and not a moment later:
    // `update` early-returns for the whole death beat, so `cargo()` never runs
    // again to do it. Left attached, the crate would hang frozen in the water
    // and then be dragged across the world by the respawn's teleport.
    //
    // Where it falls is where it stays. Phase 4 turns that into a recoverable
    // wreck with a fee attached; for now the crate simply sinks and waits.
    this.telemetry('death', { lives: p.lives, towing: this.tow.load ? this.tow.load.id : '-' });
    this.dropCargo();
    this.dockable = null;
    this.pendingRefit = false;
    this.releaseControls();
    p.lives--;
    this.deathTimer = 1.9;
    this.explode(p.x, p.y, 2.2);
    this.camera.addShake(0.9);
    this.damageFlash = 0.8;
  }

  /** Put the sub back at the last station it docked with. */
  respawn() {
    const p = this.player;
    // The sub never teleports with a load on the line. `onDeath` already drops
    // it on the normal path, but this is where the teleport actually is, and
    // the 'restart' command reaches here with the player still alive.
    this.dropCargo();
    const s = this.rules.content.stations.find((q) => q.id === this.lastStation);
    const x = s ? s.x : this.rules.content.spawn.x;
    const y = s ? s.y : this.rules.content.spawn.y;

    if (p.lives < 0) {
      // Out of hulls: the run does not end, it just costs. Kids should never
      // lose an hour of progress to one bad minute.
      p.lives = SUB.startLives;
      this.salvage = Math.floor(this.salvage * 0.7);
      this.showToast('SALVAGE TEAM RECOVERED YOU', 'REPAIR COSTS DEDUCTED', '#ff5a4a');
    }

    // Bosses come back to full strength; everything else stays dead, so a hard
    // stretch gets a little easier each attempt without trivialising the fight.
    for (const e of this.actors.enemies) {
      if (!e.boss || !e.alive) continue;
      e.hp = e.maxHp;
      e.podA = e.podMax;
      e.podB = e.podMax;
      e.alert = 0;
      e.state = 'idle';
      e.x = e.homeX;
      e.y = e.homeY;
      e.vx = 0; e.vy = 0;
    }
    if (this.octopus.awake && this.octopus.state !== 'dead') this.octopus.reset();

    p.spawn(x, y);
    this.resetLane();
    // Never send anyone back out with an empty rack. Respawning into the fight
    // that just killed you with no torpedoes is how a checkpoint becomes a wall.
    p.torpedoes = Math.max(p.torpedoes, Math.min(p.stats.torpedoMax, 6));
    if (p.stats.chargeUnlocked) p.charges = Math.max(p.charges, 2);
    this.camera.snapTo(x, y);
    this.target = null;
    this.lockProgress = 0;
    this.deathTimer = 0;
    this.damageFlash = 0;
  }

  /**
   * Start over. Rebuilds the actor set from the level data, clears every hole
   * the last run blew in the rock, and wipes the stored save — a "new dive"
   * that left the old world lying around would be a new dive in name only.
   */
  newRun() {
    // Only a mode that persists may wipe the stored run, and only ITS OWN.
    // Starting Survival must not cost somebody the campaign dive they were half
    // way through — it does not write a save, so it has no business deleting
    // one — and now that each mode keeps its own slot, starting a fresh
    // expedition must not take the descent with it either.
    if (this.rules.persists) clearSave(this.rules.id);
    resetCraters();
    // The mode's content, not the default. Building the campaign's 411 enemies
    // and 13 stations into a survival arena is silent — everything works, it is
    // simply the wrong world.
    this.actors = new Actors(this.rules.content);
    this.octopus.reset();
    // Ownership is NOT touched. A new dive is a new dive, not a new account —
    // the boats and the kit bolted to them are the profile's, and wiping them
    // here is exactly what having a profile is supposed to prevent.
    const hull = this.hulls.has(this.profile.active as ChassisId)
      ? CHASSIS[this.profile.active as ChassisId] : PATHFINDER;
    this.player.setChassis(hull, statsFor(this.levelsFor(hull.id), hull));
    this.camera.followChassis(
      hull.terminalX, hull.terminalY, PATHFINDER.terminalX, PATHFINDER.terminalY);
    this.salvage = 0;
    this.oreHeld.fill(0);
    this.minedOre.clear();
    this.oreHits.clear();
    this.oreProgress.clear();
    this.seenPois.clear();
    this.activatedStations.clear();
    this.defeatedBosses.clear();
    this.takenPickups.clear();
    this.clearedEnemies.clear();
    this.seenHints.clear();
    this.lastStation = '';
    this.reachedX = 0;
    this.playTime = 0;
    this.victory = false;
    this.deathTimer = 0;
    this.toast = null;
    this.toastQueue.length = 0;
    this.target = null;
    this.lockProgress = 0;
    this.intensity = 0;
    // A fresh Actors rebuilt every crate at its authored spot, so the old line
    // is pointing at a pickup that no longer exists.
    this.tow = newTowLine();
    this.nearCargo = null;

    const p = this.player;
    p.applyStats(statsFor(this.levels, p.chassis));
    p.cargoMass = 0;
    p.score = 0;
    p.lives = SUB.startLives;
    p.torpedoes = 8;
    p.charges = 0;
    const sp = this.rules.content.spawn;
    p.spawn(sp.x, sp.y, 0);
    this.resetLane();
    this.camera.snapTo(sp.x, sp.y);
    this.region = regionAt(sp.x);
    this.terrain.clear();
    this.terrain.update(sp.x, 16);
  }

  // --- save/load ------------------------------------------------------------

  /**
   * Every field listed explicitly, deliberately. This used to spread
   * `blankSave()` first, which meant a field added to `SaveData` and forgotten
   * here shipped as its blank default forever with no error anywhere — the
   * failure mode is silent data loss, discovered by a player. Written out in
   * full, the same mistake is a compile error.
   */
  toSave(): SaveData {
    const p = this.player;
    return {
      v: SAVE_VERSION,
      // Which mode wrote this, so CONTINUE can put it back where it came from.
      mode: this.rules.id,
      station: this.lastStation,
      at: { x: this.player.x, y: this.player.y, lane: Math.max(0, this.player.lane) },
      levels: { ...this.levels },
      salvage: this.salvage,
      ore: [...this.oreHeld],
      minedOre: [...this.minedOre],
      oreProgress: [...this.oreProgress],
      score: p.score,
      lives: p.lives,
      torpedoes: p.torpedoes,
      charges: p.charges,
      kills: this.actors.kills,
      seenPois: [...this.seenPois],
      stations: [...this.activatedStations],
      bosses: [...this.defeatedBosses],
      takenPickups: [...this.takenPickups],
      clearedEnemies: [...this.clearedEnemies],
      hints: [...this.seenHints],
      craters: craterState(),
      playTime: this.playTime,
      reachedX: this.reachedX,
      victory: this.victory,
      // Only what has actually moved. Writing every crate meant the restore
      // loop below touched crates the player had never found — and revealed
      // the three sealed-vault rewards without a ping ever being fired.
      fleet: [...this.fleet].map(([id, levels]) => ({ id, levels: { ...levels } })),
      equipment: { ...this.equipment },
      active: this.player.chassis.id,
      hulls: [...this.hulls],
      cargo: this.actors.pickups
        .filter((pk) => pk.alive && pk.mass > 0 && pk.id !== ''
          && (pk.x !== pk.homeX || pk.y !== pk.homeY))
        .map((pk) => ({ id: pk.id, x: pk.x, y: pk.y, lane: pk.lane })),
      towing: this.tow.load ? this.tow.load.id : '',
    };
  }

  save() {
    // A mode that does not persist writes nothing, ever. This one line disarms
    // dock, sell, buy, checkFinale and the boss branch of onKill together — the
    // alternative is remembering the rule at each of them.
    if (!this.rules.persists) return;
    writeSave(this.toSave());
    // Ownership is account-level, so it is persisted on the same beat rather
    // than waiting for a run to end — a hull bought and then a tab closed must
    // still be a hull owned.
    writeProfile(this.profile);
  }

  applySave(s: SaveData) {
    const p = this.player;

    // Ownership comes from the profile, not from the run save — `profile.ts`
    // owns the migration out of the old flat `levels`, so there is nothing to
    // reconstruct here.
    const active = (this.profile.active in CHASSIS ? this.profile.active : 'pathfinder') as ChassisId;
    const chassis = this.hulls.has(active) ? CHASSIS[active] : PATHFINDER;
    p.setChassis(chassis, statsFor(this.levelsFor(chassis.id), chassis));
    this.camera.followChassis(
      chassis.terminalX, chassis.terminalY, PATHFINDER.terminalX, PATHFINDER.terminalY);
    this.salvage = s.salvage;
    // Length-checked rather than trusted: a save written before the hold
    // existed has no `ore`, and a save written after a seam was appended to
    // `ORE` has a shorter one. Both are ordinary and neither should throw.
    for (let i = 0; i < this.oreHeld.length; i++) this.oreHeld[i] = s.ore?.[i] ?? 0;
    this.minedOre = new Set(s.minedOre ?? []);
    this.oreProgress = new Map(s.oreProgress ?? []);
    p.score = s.score;
    p.lives = s.lives;
    this.playTime = s.playTime;
    this.reachedX = s.reachedX;
    // Both of these were written by `toSave` and never read back: a continued
    // run reported zero kills on its summary, and re-entering the finale
    // re-awarded the 25,000 and re-opened the victory screen.
    this.actors.kills = s.kills;
    this.victory = s.victory;
    this.seenPois = new Set(s.seenPois);
    this.activatedStations = new Set(s.stations);
    this.defeatedBosses = new Set(s.bosses);
    this.takenPickups = new Set(s.takenPickups);
    this.clearedEnemies = new Set(s.clearedEnemies ?? []);
    // Apply it. Silently — no explosion, no loot, no score: these died in an
    // earlier session and have already been paid for.
    if (this.clearedEnemies.size) {
      for (const e of this.actors.enemies) {
        if (e.zoneKey && this.clearedEnemies.has(e.zoneKey)) e.alive = false;
      }
    }
    this.seenHints = new Set(s.hints ?? []);
    // Passages the player dug are part of the world they saved into.
    restoreCraters(s.craters ?? []);
    this.terrain.clear();
    this.lastStation = s.station;

    for (const st of this.actors.stations) st.active = this.activatedStations.has(st.id);
    for (const pk of this.actors.pickups) {
      if (pk.id && this.takenPickups.has(pk.id)) pk.alive = false;
    }
    for (const e of this.actors.enemies) {
      if (e.boss && e.id && this.defeatedBosses.has(e.id)) e.alive = false;
    }

    // Put cargo back where it was left, and re-hook whatever was on the line.
    //
    // `hold` is reset across the board first, the same way `alive` and `active`
    // already are. Replacing the TowLine without doing that can strand a crate
    // marked 'towed' with no line attached, and nothing ever writes the field
    // back: `updatePickups` skips it, and both the catch loop and the nearCargo
    // scan require 'free'. It would hang in the water, un-grapplable, forever.
    this.tow = newTowLine();
    for (const pk of this.actors.pickups) {
      if (pk.mass > 0) { pk.hold = 'free'; pk.vx = 0; pk.vy = 0; }
    }
    for (const rec of s.cargo ?? []) {
      const pk = this.actors.pickups.find((q) => q.id === rec.id);
      if (!pk) continue;
      pk.x = rec.x;
      pk.y = rec.y;
      // Optional, so a save written before lanes existed restores to the near
      // one — which is where every crate in such a save actually was.
      pk.lane = rec.lane ?? 0;
      // Only crates that moved are written, so being here is itself evidence
      // the player found this one — revealing it is right. It would not be
      // right for a crate still sitting sealed where it was authored.
      pk.revealed = true;
    }
    if (this.defeatedBosses.has('octopus')) this.octopus.retire();

    // Resume where the dive was left. The station is the fallback, for a save
    // written before this existed and for one left with no position at all.
    //
    // Not guarded against resuming inside rock: the position came from a frame
    // in which the sub was flying, and the field is deterministic, so what was
    // open water when it was written is open water now. A crater the player dug
    // is restored above, before this runs, for the same reason.
    const st = STATIONS.find((q) => q.id === s.station);
    const x = s.at ? s.at.x : st ? st.x : SPAWN.x;
    const y = s.at ? s.at.y : st ? st.y : SPAWN.y;
    p.spawn(x, y, 0);
    this.resetLane();
    // Back into the lane the dive was left in. It matters for more than
    // tidiness: those coordinates were clear water in THAT lane, and lane 0's
    // rock at the same x and y is a different question entirely — resuming in
    // the near lane could put the boat inside a cliff. Absent on a save written
    // before lanes existed, which is correctly the near one.
    const lane = s.at?.lane ?? 0;
    if (lane > 0) {
      this.travelTo(lane);
      this.snapLane();
    }

    // Re-hook AFTER the sub has been put back at its station, not before. The
    // crate is restored wherever it was actually left — after a background-tab
    // save, kilometres away — so the distance has to be measured from where the
    // run is about to resume, or the guard is testing the wrong two points and
    // the line parts on the first frame with a bang about a crate nobody can
    // see. If it is out of reach, leave it free: its position is already
    // restored above, so nothing is lost but the attachment.
    if (s.towing) {
      const load = this.actors.pickups.find((q) => q.id === s.towing && q.alive);
      if (load) {
        p.fairlead(this.muzzlePos);
        const d = hyp(load.x - this.muzzlePos.x, load.y - this.muzzlePos.y);
        if (d <= GRAPPLE.range + GRAPPLE.snap) {
          load.hold = 'towed';
          this.tow.state = 'attached';
          this.tow.load = load;
          this.tow.tether = clamp(d, GRAPPLE.minRest, GRAPPLE.range);
        }
      }
    }
    p.torpedoes = Math.min(p.stats.torpedoMax, s.torpedoes);
    p.charges = Math.min(p.stats.chargeMax, s.charges);
    this.camera.snapTo(x, y);
    this.terrain.update(x, 12);
  }

  // --- shared helpers -------------------------------------------------------

  private hurtPlayer(amount: number, discrete: boolean, blast = false) {
    const p = this.player;
    // The ward field is a resistance, not a pool: it thins what arrives rather
    // than absorbing it, which is what makes it read differently from the
    // deflector sitting beside it in the shop.
    if (blast) amount *= p.stats.blastMul;
    const beforeHull = p.hull;
    const beforeShield = p.shield;
    if (discrete) p.hit(amount);
    else p.graze(amount);
    if (!this.rules.persists && p.hull < beforeHull) survivalHurt();

    if (p.shield < beforeShield && this.shieldSfx <= 0) {
      this.shieldSfx = 0.12;
      this.audio.shieldHit(clamp(amount / 30, 0.1, 1));
    }
    if (p.hull < beforeHull) {
      const lost = beforeHull - p.hull;
      this.damageFlash = Math.min(0.6, this.damageFlash + lost * 0.014);
      this.camera.addShake(Math.min(0.5, lost * 0.02));
      if (p.hull < p.stats.maxHull * 0.34 && Math.random() < 0.25) this.audio.alarm();
    }
    if (p.shieldBroke) {
      this.audio.shieldBreak();
      this.camera.addShake(0.3);
      this.renderer.particles.sparks(p.x, p.y, 0, 1, 22, 1.1);
    }
  }

  private queueCarve(x0: number, x1: number) {
    if (!this.pendingCarve) this.pendingCarve = { x0, x1 };
    else {
      this.pendingCarve.x0 = Math.min(this.pendingCarve.x0, x0);
      this.pendingCarve.x1 = Math.max(this.pendingCarve.x1, x1);
    }
  }

  private enemyShot(x: number, y: number, kind: 'gun' | 'turret' | 'torpedo' | 'ping' | 'bite') {
    const d = hyp(x - this.player.x, y - this.player.y);
    switch (kind) {
      case 'turret': this.audio.turretFire(d); break;
      case 'ping': this.audio.hunterPing(d); break;
      case 'bite': this.audio.bite(d); break;
      case 'torpedo': this.audio.torpedo(d); break;
      default: this.audio.cannon(d); break;
    }
  }

  private explode(x: number, y: number, scale: number) {
    this.renderer.particles.explosion(x, y, scale);
    const d = hyp(x - this.player.x, y - this.player.y);
    this.camera.addShake(clamp((scale * 40) / (10 + d), 0.02, 0.85));
    // A chain reaction can set off a dozen mines on one frame. Past a few
    // voices the extra ones only muddy the mix, so the loudest few win.
    if (this.blastVoices < 4) {
      this.blastVoices++;
      this.audio.explosion(d, scale);
    }
  }

  /** Coarse terrain occlusion test between two points. */
  private lineOfSight(ax: number, ay: number, bx: number, by: number): boolean {
    const dx = bx - ax, dy = by - ay;
    const dist = hyp(dx, dy);
    const steps = Math.min(14, Math.max(3, Math.round(dist / 7)));
    const feats = featuresFor(Math.min(ax, bx) - 4, Math.max(ax, bx) + 4);
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      if (density(ax + dx * t, ay + dy * t, feats) > 0) return false;
    }
    return true;
  }

  showToast(text: string, sub: string, color = '#ffb454') {
    const t = { text, sub, time: 3.2, color };
    if (this.toast) this.toastQueue.push(t);
    else this.toast = t;
    if (this.toastQueue.length > 3) this.toastQueue.shift();
  }

  // --- reactive effects (PRD §24) -------------------------------------------

  private effects(dt: number) {
    const p = this.player;
    const parts = this.renderer.particles;

    this.bubbleTimer -= dt;
    // AND ONLY WHILE THERE IS WATER TO CHURN.
    //
    // Every emitter below is a screw beating water into bubbles. Out of the
    // sea there is no water and no bubbles, and a boat trailing a wake through
    // the air is the single thing most likely to make a breach read as a bug
    // rather than as a jump. Gated on the SCREW's own submersion rather than
    // the hull's — it sits aft and low, so it is still biting for a moment
    // after the nose is clear, which is exactly when the spray should be
    // heaviest.
    if (this.bubbleTimer <= 0 && !p.dead && p.submersion > 0.15) {
      // NITRO MULTIPLIES THE DRIVE, IT IS NOT A DRIVE OF ITS OWN.
      //
      // These three terms all used to ADD `nitroBlend` to `thrustLevel`, which
      // made lighting the burner while parked look identical to being under
      // full power. Measured on a stationary boat, shift held: the rate went
      // 11/s to 125/s, the burst 1 to 5, and `churn` crossed its gate on its
      // own to add two more bursts of 13 in 8 and 9 metre clouds. About 350x
      // the bubbles, from a boat going nowhere — enough to consume the entire
      // 12,000 dynamic particle pool and carpet the seabed in sprites, which
      // reads as the lighting failing rather than as particles.
      this.bubbleTimer = 0.022 / (0.25 + p.thrustLevel * (1 + this.nb('particles') * 2.5));
      const a = p.aimAngle();
      const bx = p.x - Math.cos(a) * p.chassis.hardpoints.wake;
      const by = p.y - Math.sin(a) * p.chassis.hardpoints.wake;
      parts.bubbles(
        bx, by,
        1 + Math.round(p.thrustLevel * (1 + this.nb('particles') * 2) * 2), 0.9,
        1.6 + p.speed * 0.15, 0.11,
      );
      // A second, wider burst of finer bubbles once the drive is actually
      // working. The simulated wake gives the water motion and the foam gives
      // it texture, but neither of them is a THING in the scene — these are,
      // and they are what makes the trail read as churned rather than as a
      // filter over the top of it.
      // Gated on THRUST, so "once the drive is actually working" is what the
      // line above it has always claimed it means.
      const churn = p.thrustLevel * (1 + this.nb('particles') * 1.6);
      if (churn > 0.12) {
        // Two more bursts, further aft and further apart, so the trail has
        // depth along its length instead of being one puff at the screw.
        for (let k = 0; k < 2; k++) {
          const off = 4.0 + k * 5.5;
          parts.bubbles(
            bx - Math.cos(a) * off, by - Math.sin(a) * off,
            2 + Math.round(churn * 7), 3.0 + churn * 3.0 + k * 1.6,
            0.8 + p.speed * 0.3, 0.05 + k * 0.02,
          );
        }
      }
    }

    // The seabed of the sub's own lane, in drawn y — which is what the
    // clearance test and the emit point below both want.
    const pl = LAYERS[Math.max(0, p.lane)]!;
    const floor = seabedY(p.x + pl.warp) + layerLift(p.x, pl);
    const clearance = p.y - floor;
    if (clearance > 0 && clearance < 7 && p.speed > 6 && Math.random() < dt * 26) {
      parts.sediment(p.x - p.vx * 0.15, floor + 0.6, 1, clamp(p.speed / 14, 0.2, 1.3));
    }

    for (const c of p.contacts) {
      if (c.speed > SUB.safeImpactSpeed * 0.75) {
        parts.sparks(c.x, c.y, c.nx, c.ny, Math.min(24, Math.round(c.speed)), clamp(c.speed / 12, 0.4, 1.6));
        this.camera.addShake(clamp(c.speed / 26, 0.05, 0.7));
      }
      parts.sediment(c.x, c.y, c.speed > 6 ? 3 : 1, clamp(c.speed / 10, 0.2, 1));
    }
    if (p.lastImpact > SUB.safeImpactSpeed) {
      this.damageFlash = Math.min(0.5, this.damageFlash + p.lastImpact * 0.014);
      this.audio.impact(p.lastImpact);
    } else if (p.contacts.length && p.speed > 5 && Math.random() < dt * 8) {
      this.audio.scrape(p.speed);
    }

    // Ramming at nitro speed digs into whatever it hits. Rate-limited: a
    // contact persists for as long as the hull is against the rock, and one
    // re-mesh per frame for the length of a scrape is not affordable.
    this.ramCarve -= dt;
    if (p.contacts.length && this.ramCarve <= 0) {
      const c = p.contacts[0];
      const ram = p.nitroOn && p.speed > RAM_SPEED;
      // How thick the rock behind the contact is. A spur thin enough to break
      // is one the hull can push through; a cliff is not, and stays a cliff.
      const reach = p.nitroOn ? CRUSH_NITRO : CRUSH_THIN;
      const thick = ram ? 0 : this.rockThickness(c, reach);
      // At nitro the hull breaks thin rock standing still, which is the point
      // of it: the alternative is being pinned by a spur too small to see with
      // no room left to build the speed that used to be the only way out.
      const crush = thick < reach && (p.nitroOn || p.speed > CRUSH_SPEED);
      if (ram || crush) {
        const span = this.carveHere(c.x, c.y, ram ? 5.5 : p.nitroOn ? 4.6 : 3.6);
        if (span) {
          this.ramCarve = ram ? 0.22 : 0.4;
          this.queueCarve(span.x0, span.x1);
          parts.sediment(c.x, c.y, ram ? 12 : 7, ram ? 1.6 : 0.9);
          this.camera.addShake(ram ? 0.25 : 0.09);
          this.audio.rockBreak(0);
        }
      }
    }

    // Pressure. The hull talks to you the deeper you take it, and talks more
    // once it is damaged — free tension, no damage attached.
    this.creakTimer -= dt;
    if (this.creakTimer <= 0) {
      const d = p.depth;
      this.creakTimer = Math.max(1.4, 16 - d * 0.005) * (0.6 + Math.random() * 0.9);
      if (d > 140) this.audio.creak(d);
    }

    // Damaged hulls leak.
    const hullFrac = p.hull / p.stats.maxHull;
    if (hullFrac < 0.5 && Math.random() < dt * (2 + (1 - hullFrac) * 8)) {
      parts.bubbles(p.x + (Math.random() - 0.5) * 4, p.y, 1, 0.6, 1.2, 0.1);
    }

  }

  private mixAudio(dt: number) {
    const p = this.player;
    let want = 0;
    for (const e of this.actors.enemies) {
      if (!e.alive) continue;
      const threat = ENEMY[e.type].threat;
      if (e.state === 'attack' || e.state === 'rush' || e.state === 'burst') {
        want = Math.max(want, 0.5 + threat * 0.5);
      } else if (e.state === 'investigate' || e.state === 'suspicious') {
        want = Math.max(want, 0.25 + threat * 0.25);
      }
    }
    const o = this.octopus;
    if (o.awake && o.state !== 'dead') {
      want = Math.max(want, 0.55 + (o.state === 'sweep' || o.state === 'grab' ? 0.45 : 0));
    }
    if (this.toast) want = Math.max(want, 0.4);
    if (p.hull < p.stats.maxHull * 0.34) want = Math.max(want, 0.5);
    this.intensity = damp(this.intensity, want, want > this.intensity ? 2.2 : 0.35, dt);

    const boss = this.actors.activeBoss();
    this.mood = boss || (o.awake && o.state !== 'dead') ? 'boss' : this.region.mood;

    this.audio.update(dt, {
      throttle: p.thrustLevel,
      speed: p.speed,
      depth: p.depth,
      intensity: this.intensity,
      boost: p.nitroOn,
      mood: this.mood,
    });
  }

  // ==========================================================================
  // frame parameters
  // ==========================================================================

  private updateBands() {
    // THE TABLE IS READ AT A WARPED DEPTH, which is what `depthFall` is.
    //
    // Extinction, fog, ambient colour and ambient level are four authored
    // curves against depth, and a gain on any one of them would slide it out
    // of agreement with the other three. Moving the depth they are all sampled
    // at keeps them consistent by construction: at 2 the water is simply twice
    // as deep as far as light is concerned. The zone NAME is not read here, so
    // where the player is stays a fact about where the player is.
    const tune = this.renderer.seaTune;
    const depth = Math.max(0, WORLD.surfaceY - this.camera.y)
      * Math.max(0, tune.depthFall);
    let a = BANDS[0], b = BANDS[BANDS.length - 1], t = 1;
    for (let i = 0; i < BANDS.length - 1; i++) {
      if (depth <= BANDS[i + 1].depth) {
        a = BANDS[i]; b = BANDS[i + 1];
        t = (depth - a.depth) / (b.depth - a.depth);
        break;
      }
    }
    t = clamp(t, 0, 1);
    const mix = (x: number, y: number) => x + (y - x) * t;
    const o = this.ambientBand;
    // The region tint is what stops the whole second half of the game reading
    // as one gradient: the depth bands set the physics, the region sets the key.
    const k = this.region.tint;
    for (let i = 0; i < 3; i++) {
      o.fog[i] = mix(a.fog[i], b.fog[i]) * k[i];
      o.ambient[i] = mix(a.ambient[i], b.ambient[i]) * k[i];
      // WATER DENSITY, on all three channels equally. Scaling the ratio between
      // them would change what COLOUR the sea is, which is the depth bands'
      // job; this only changes how much of it there is.
      o.ext[i] = mix(a.extinction[i], b.extinction[i]) * Math.max(0, tune.density);
    }
    o.level = mix(a.ambientLevel, b.ambientLevel);
  }

  buildFrame(aspect: number): FrameParams {
    this.aspect = aspect;
    this.camera.resize(aspect);
    // The renderer owns the sample sequence, because it is the thing that knows
    // whether temporal antialiasing is on and how big a pixel is. Stepped here,
    // once, immediately before the matrices that have to carry it.
    this.renderer.advanceJitter();
    this.camera.jitterX = this.renderer.jitterX;
    this.camera.jitterY = this.renderer.jitterY;
    const vp = this.camera.compute();
    this.updateBands();

    const f = this.frame;
    const eye = this.camera.eye();
    f.viewProj = vp;
    f.viewProjNJ = this.camera.viewProjNJ;
    invert(f.invViewProj, vp);
    f.camX = eye.x; f.camY = eye.y; f.camZ = eye.z;
    f.focusZ = this.camera.focusZ;
    // THE PLANE THE SEA IS READ IN, which has been zero since the day the
    // field was declared — initialised in the frame literal and never assigned
    // again. `U.sky.w` is the z the composite solves the waterline at and the
    // z it measures the horizon haze from, so on lane 0 it was right by
    // coincidence and on every other lane the sea was being solved 55 or 141
    // metres in front of the camera. That is the missing waterline: the ray to
    // the surface plane was reaching a plane the player is not flying in.
    f.planeZ = this.camera.focusZ;
    // What depth of field focuses ON, and how long the lens is. The play plane
    // is where the boat is and where the player is looking, so it is the
    // subject; `viewZoom` stands in for focal length, because magnifying is
    // the only thing in this camera that behaves like reaching for a longer
    // lens — and a longer lens is what makes depth of field shallow.
    f.focusDist = this.camera.dist;
    // WHILE THE HULL IS ON DISPLAY, FOCUS ON IT.
    //
    // The turntable sits a few metres off the lens and the world it is drawn
    // over is sixty metres away, so at any real aperture one of the two is a
    // smear. The refit screen scrims that world to near black anyway, which
    // settles which one matters — and focusing the thing the player is looking
    // at is what a camera would do.
    if (this.showcase) f.focusDist = World.SHOW_DIST;
    f.viewZoom = this.camera.viewZoom();
    // The frustum edges as seen from the eye, tilt included. Clamped short of
    // grazing: at a tilt where the lower edge approaches horizontal the tangent
    // runs away, and the slice cull would ask for an unbounded band.
    const halfFov = CAMERA.fov / 2;
    const t = clamp(this.camera.viewTilt(), -1.2, 1.2);
    f.tanTop = Math.tan(clamp(t - halfFov, -1.4, 1.4));
    f.tanBot = Math.tan(clamp(t + halfFov, -1.4, 1.4));
    f.time = this.time;
    f.dt = this.lastDt;

    const o = this.ambientBand;
    f.fog = [o.fog[0], o.fog[1], o.fog[2]];
    f.ambient = [o.ambient[0], o.ambient[1], o.ambient[2]];
    f.ext = [o.ext[0], o.ext[1], o.ext[2]];
    f.ambientLevel = o.level;
    // THE SUN, AS REFRACTED BY THE TIME OF DAY.
    //
    // Direction first, because it is now load-bearing: the sun casts the
    // scene's ambient shadow, so its elevation decides whether shadows pool
    // under things or rake across the floor.
    // HOW MUCH SKY THERE IS TO DRAW, and therefore whether to draw any.
    //
    // Measured from the EYE rather than the boat: the camera sits sixty metres
    // back and can be over the waterline while the sub is under it, or the
    // reverse when it tilts. It is the lens that either sees sky or does not.
    //
    // PROXIMITY, NOT DRYNESS — and getting that distinction wrong is what kept
    // the sky out of the frame after everything else about it was right.
    //
    // This started as "how much sky can the lens see", which made sense while
    // the sky was an all-or-nothing wash over the frame. It stopped making
    // sense the moment the composite learned to split sky from water PER PIXEL
    // against the waterline: that split already knows which pixels are air, so
    // asking a second time, globally, can only subtract. And it did. A boat
    // floating at its own trim sits about a metre under, which this scored at
    // 0.17 — so the air above the waterline was drawn 17% sky over 83% of the
    // water colour behind it, which is water. The sky was correct, the split
    // was correct, and this multiplied the two down to nothing.
    //
    // What is left for a whole-frame term is the only thing a whole frame can
    // answer: is the surface anywhere near. One at the top, zero forty metres
    // down, where the composite drops the feature entirely — which is still the
    // reason none of this costs anything in the abyss.
    f.skyAmount = clamp(
      1 - (WORLD.surfaceY - this.camera.eye().y - 2) / 38, 0, 1,
    );

    // The tuner writes `skyTune` straight into the renderer, so a slider move
    // has nobody to call. Rather than hand the panel a reference to the world
    // it does not otherwise need, the state re-derives itself when the two
    // numbers it depends on change — which is once per drag, not per frame.
    const st = this.renderer.skyTune;
    if (st.sunAzimuth !== this.sunAzUsed || st.sunElevation !== this.sunElUsed) {
      this.sunAzUsed = st.sunAzimuth;
      this.sunElUsed = st.sunElevation;
      this.applySun();
    }
    const sun = this.sunState;
    f.sunX = sun.dx; f.sunY = sun.dy; f.sunZ = sun.dz;
    f.skyX = sun.ax; f.skyY = sun.ay; f.skyZ = sun.az;
    // Where the PRESET put the sun, before the elevation slider. The preset's
    // authored colour was chosen for this elevation, so it is the reference the
    // atmospheric tint is measured from — see `sunTint` in post.wgsl.
    f.sunRefY = Math.sin((this.sunPreset.elevation * Math.PI) / 180);
    // THE SKY'S SUN, NOT THE WATER'S. `sunStrength` below is depth-attenuated
    // and carries the surface's Fresnel transmission, both of which are about
    // light entering the sea — and both of which were dimming the sky above it.
    // At a low sun that is most of the light: the transmission term alone can
    // halve it, and then depth halves it again.
    // WHETHER THE EYE CAN SEE THE SURFACE AT ALL, which is the question I kept
    // answering with the wrong quantity.
    //
    // Three goes at this. The ORBIT EYE opened the sea while the boat was a
    // metre under and cost the frame rate; the HULL closed it the instant the
    // boat cleared, which is when it is most wanted; the LENS CENTRED ON THE
    // WATERLINE then closed it while the camera was tilted and looking straight
    // down at water, which is the case that started the whole thread.
    //
    // The geometry decides it, not taste. A surface can only be drawn as a
    // plane by a ray that reaches it FROM ABOVE — and that happens exactly when
    // the eye is above the waterline, which the tilt is what produces: at ten
    // degrees the orbit lifts the eye eight metres and it is looking down at
    // water, at zero it sits with the boat and sees the column edge-on. So the
    // eye it is, with a couple of metres to fade in so surfacing is not a
    // switch.
    //
    // What made this unaffordable the first time was not the gate, it was that
    // the plane shaded the whole frame at full cost. The haze and the froth
    // threshold deal with that; the gate can now be the honest one.
    f.planeView = clamp((this.camera.eye().y - WORLD.surfaceY - 0.3) / 2, 0, 1);
    f.night = Math.max(
      clamp((0.105 + sun.dy) / 0.315, 0, 1),
      clamp((0.35 - sun.gain) / 0.30, 0, 1),
    );
    f.sunR = sun.color[0] * sun.gain;
    f.sunG = sun.color[1] * sun.gain;
    f.sunB = sun.color[2] * sun.gain;
    // `pathScale` lengthens the journey for light that came in at an angle, so
    // a low sun is extinguished sooner as well as arriving dimmer, and
    // `transmit` is how much got through the surface at all.
    // The same depth warp the bands take, so the sun and the water it is
    // shining through cannot disagree about how deep this is.
    const dep = Math.max(0, -this.camera.y) * Math.max(0, this.renderer.seaTune.depthFall);
    // AND THE SKY'S SUN IS THE WATER'S SUN. `sunIntensity` reached the
    // atmosphere shader and stopped there, so the slider lit the sky and left
    // everything below the waterline untouched. Carried as a RATIO to the
    // value the sky was authored at, so at the default it is exactly 1 and
    // this changes nothing about the frame as it stands.
    f.sunStrength = Math.max(0, Math.exp(-dep * 0.0125 * sun.pathScale) - 0.012)
      * 1.25 * sun.transmit * (st.sunIntensity / SKY_SUN_REF);
    // Colour rides on the ambient and the fog, and only near the surface. A
    // golden hour is golden in the top fifty metres; at three hundred every
    // wavelength that could carry the warmth has already been absorbed, and
    // tinting there would just undo the authored depth bands for no gain.
    const warm = Math.exp(-dep * 0.010);
    for (let i = 0; i < 3; i++) {
      const k = 1 + (sun.color[i] - 1) * warm;
      f.fog[i] *= k;
      f.ambient[i] *= k;
    }
    f.normDepth = clamp(Math.max(0, -this.camera.y) / 2500, 0, 1);

    const p = this.player;
    // The wash goes in BEHIND the boat, off the screw rather than the hull, and
    // pushes the way the boat is going. Thrust rather than speed decides how
    // hard: a sub coasting to a stop leaves no wake, which is the tell that the
    // field is being driven rather than following the sprite around.
    const HPw = p.chassis.hardpoints;
    const aw = p.aimAngle();
    // Well aft of the screw, not on it. The splat is a gaussian several metres
    // across, so injected at the drive hardpoint it sat ON the hull — and a
    // wake that refracts the boat that made it smears half the sub.
    const wakeOff = HPw.drive + 9.5;
    f.wakeX = p.x - Math.cos(aw) * wakeOff;
    f.wakeY = p.y - Math.sin(aw) * wakeOff;
    f.wakeZ = p.z;
    f.wakeVX = -p.vx;
    f.wakeVY = -p.vy;
    f.wakeThrust = p.dead ? 0 : Math.min(1,
      p.thrustLevel * (1 + this.nb('wake') * World.NITRO_WAKE * 0.9));
    // A breach owns the splat for a fraction of a second. The field retains
    // and advects it after this override has decayed, producing a widening,
    // irregular patch of collision foam at effectively no new render cost.
    if (this.surfaceImpact) {
      const q = this.surfaceImpact;
      f.wakeX = q.x; f.wakeY = q.y; f.wakeZ = q.z;
      f.wakeVX = q.vx; f.wakeVY = q.vy;
      f.wakeThrust = Math.max(f.wakeThrust, q.strength);
    }
    // Where the boat is on screen, and how much clear water to leave round it.
    // The gap is in uv, taken from the hull's own length against the visible
    // width, so it stays the right size at any zoom.
    const extW = this.camera.halfExtent();
    f.wakeSubU = (p.x - this.camera.viewX()) / (extW.w * 2) + 0.5;
    f.wakeSubV = 0.5 - (p.y - this.camera.viewY()) / (extW.h * 2);
    f.wakeGap = (HPw.drive + 3.4) / (extW.w * 2);
    // GATED ON THRUST, like the bubbles and for the same reason.
    //
    // `wakeNitro` becomes `hot` in the composite, where it is a pure BRIGHTNESS
    // multiplier on foam that already exists — 2.2x to 4.8x on the crest — and
    // injects nothing itself. So lighting the burner on a stationary boat took
    // whatever residual dye was left in the field and made it 2.2 times more
    // visible: faint leftovers nobody could see at rest became a screenful,
    // everywhere except the clear ellipse `gap` keeps around the hull.
    //
    // A hot wake means the drive is running hot, which requires the drive to be
    // running. Parked with the burner lit is a boat going nowhere.
    f.wakeNitro = this.nb('wake') * World.NITRO_WAKE * Math.min(1, p.thrustLevel * 1.6);
    const S = p.stats;
    const on = p.lightOn && !p.dead;
    const half = S.lampHalfAngle;
    // The lamp, at the lens the mesh actually draws. This used to be 2.6 here
    // and 2.8 where the light is cast, for the same lamp on the same hull.
    const HP = p.chassis.hardpoints;
    f.lightX = p.x + Math.cos(p.aimAngle()) * HP.lamp;
    f.lightY = p.y + Math.sin(p.aimAngle()) * HP.lamp + HP.lampDrop;
    // The lamp travels with the boat, so the volumetric march lights the lane
    // the sub is actually in rather than the one it started the dive in.
    f.lightZ = p.z;
    f.lightDX = Math.cos(p.lightAngle); f.lightDY = Math.sin(p.lightAngle); f.lightDZ = 0;
    f.lightIntensity = on ? S.lampIntensity : 0;
    f.lightRange = S.lampRange;
    f.lightInner = Math.cos(half * 0.44);
    f.lightOuter = Math.cos(half);

    const ping = this.sonar.primary;
    f.sonarX = ping ? ping.x : 0;
    f.sonarY = ping ? ping.y : 0;
    f.sonarRadius = ping ? ping.r : 0;
    f.sonarStrength = this.sonar.shellStrength();

    f.coneAngle = p.lightAngle;
    f.coneHalf = half;
    f.coneBins = on ? LIGHT.shadowBins : 0;
    if (on) {
      const t0 = performance.now();
      this.marchBeam(f.lightX, f.lightY, p.lightAngle, S.lampRange, half, HP.beamClear, p.lane);
      this.noteCpu('lamp', performance.now() - t0);
      this.renderer.setConeShadow(this.cone);
    }

    f.damageFlash = this.damageFlash;
    f.vignette = 0.42 + f.normDepth * 0.3;
    f.exposure = 0.95 + (1 - o.level) * 0.75;
    f.distortion = 0.0011 + (1 - o.level) * 0.0011 + this.nb('post') * 0.0016;
    f.bloom = 0.8 + this.rewardFlash * 0.5;
    // BLOOM CONFINED TO THE REFIT PANEL RATHER THAN SWITCHED OFF, and those
    // blue smudges in the corners of the viewport are the reason it was ever
    // one or the other.
    //
    // Bloom is a blur of the whole HDR frame, taken after the card is drawn and
    // long before the HUD scrims anything. The card is black, so nothing INSIDE
    // the panel contributed — but the lit station outside it did, and the blur
    // is wide enough to carry it across the border and lay it on the black.
    // Growing the card only moves the border. Switching bloom off killed the
    // smudges and cost the panel every glow it had, which was an acceptable
    // trade while the only thing standing there was a submarine held under the
    // threshold on purpose.
    //
    // It stopped being acceptable when the cargo went on the stand. The two
    // minerals a player most wants to look at — the abyssal crystal and the
    // bio seam — are the two that glow, and the screen that exists to show
    // them closely was the one screen that could not.
    //
    // A rect answers both directions at once: outside it nothing contributes,
    // inside it nothing escapes. See `bloomRect` and the scissored chain.
    f.bloomRect = this.showcase;
    return f;
  }

  /**
   * Solid props the beam has to stop at, as unit half-extents in mesh space —
   * the numbers came off the built meshes rather than out of the air.
   *
   * The density field is the collision truth for the terrain, but props are not
   * in it: they are instanced meshes placed on top. So a ruin slab would sit in
   * front of the sub, occlude it in the depth buffer, and let the beam pass
   * straight through — which is exactly the complaint.
   *
   * Kelp, coral and jelly are not here on purpose: they are soft, and light
   * through weed is most of what makes the kelp forest worth flying through.
   * Debris is not here either — it is rubble lying flat on the seabed, below
   * the beam and under the terrain's own shadow, so all it would add is
   * flicker. What is left is the architecture, which is what reads as solid.
   */
  /**
   * MEASURED OFF THE MESHES, NOT WRITTEN DOWN BESIDE THEM.
   *
   * These were three hand-written triples with a comment saying they came off
   * the built meshes. Two of them still did. The ruin had drifted: 4.5 by 7.9
   * by 2.0 against an actual 3.98 by 8.68 by 1.87 — thirteen percent too wide
   * and nine percent too short, so the beam went over the top of a ruin that
   * should have stopped it and was stopped beside one that should not have.
   *
   * Nobody would ever notice that by looking, which is the whole problem with
   * numbers copied out of geometry: the mesh changes and the copy does not, and
   * the only symptom is a shadow in slightly the wrong place.
   *
   * Filled in at registration from the same `MeshData` the renderer is given,
   * so it cannot drift again.
   */
  private readonly beamBlockers = new Map<string, [number, number, number]>();

  /**
   * Gathers the solid props standing between the lamp and its reach.
   *
   * Each becomes an ellipse in XY. Props carry a `z` and real depth, and the
   * beam is a fan in the plane the LAMP is in, so a prop only counts if its own
   * z extent actually crosses that plane — a slab parked well in front of the
   * camera is drawn over the sub but is not in the water the light travels
   * through, and shadowing from it would darken open water for no reason.
   */
  private gatherBlockers(lx: number, ly: number, range: number, lane = 0, lz = 0) {
    this.blockers.length = 0;
    const c0 = this.terrain.chunkAt(lx - range);
    const c1 = this.terrain.chunkAt(lx + range);
    for (let ci = c0; ci <= c1; ci++) {
      for (const prop of propsForChunk(ci, Math.max(0, lane))) {
        // A prop in another lane is another distance entirely.
        if (prop.layer !== Math.max(0, lane)) continue;
        const ext = this.beamBlockers.get(prop.kind);
        if (!ext) continue;
        const s = prop.scale;
        if (Math.abs(prop.z - lz) > ext[2] * s) continue; // does not reach the beam plane
        // `rot` turns the mesh about Y, which swings its depth into its width.
        const c = Math.abs(Math.cos(prop.rot)), sn = Math.abs(Math.sin(prop.rot));
        // 0.85 because these are lumpy meshes and the box overstates the
        // silhouette. Under-shadowing is a shadow that ends early; over-
        // shadowing is a dark patch in water that looks empty, which is worse.
        const a = (ext[0] * c + ext[2] * sn) * s * 0.85;
        const b = ext[1] * s * 0.85;
        const dx = prop.x - lx, dy = prop.y - ly;
        const reach = range + Math.max(a, b);
        if (dx * dx + dy * dy > reach * reach) continue;
        this.blockers.push({ x: prop.x, y: prop.y, a, b });
      }
    }
  }

  /**
   * Marches one ray per angular bin from the lamp and records the normalised
   * distance to the first rock. The beam is a cone in the XY plane, so this is
   * the entire shadow — a few thousand field samples against the same density
   * function the terrain is built from, and the GPU then gets a single small
   * array instead of a shadow map and an extra geometry pass.
   */
  private marchBeam(
    lx: number, ly: number, angle: number, range: number, half: number, clear: number,
    lane = 0,
  ) {
    const bins = LIGHT.shadowBins;
    // The lamp casts against the rock it is actually among. Left on lane 0 the
    // beam ignored the wall in front of the sub and was stopped instead by
    // whatever stood 431.7 m along — shadows with no visible cause, and lit
    // rock that should have been in the dark.
    const L = LAYERS[Math.max(0, Math.min(LAYERS.length - 1, lane))]!;
    const warp = L.warp;
    /** Drawn y to field y. The lift varies with x, so it is taken per sample. */
    const fy = (x: number, y: number) => y - layerLift(x, L);
    /** The lane's own waterline and floor, in drawn y. */
    const ceilY = WORLD.surfaceY + layerLift(lx, L);
    const floorY = WORLD.yMin + layerLift(lx, L);
    // Step scales with range, so upgrading the lamp buys reach rather than
    // quietly tripling the number of field samples every frame.
    const coarse = LIGHT.shadowStep * (range / LIGHT.searchRange);
    // One feature lookup for the whole fan rather than one per sample.
    const feats = featuresFor(lx + warp - range - 4, lx + warp + range + 4);

    for (let i = 0; i < bins; i++) {
      const rel = (((i + 0.5) / bins) * 2 - 1) * half;
      const a = angle + rel;
      const cx = Math.cos(a), cy = Math.sin(a);
      let hit = 1;
      // Start clear of the hull so the sub never shadows its own lamp. A longer
      // boat needs a longer clearance, which is what `beamClear` is for — it was
      // declared per-hull and then never read, so the Dray was casting its own
      // bow into its own beam.
      let t = clear;
      while (t < range) {
        const sy = ly + cy * t;
        // A ray heading for the surface or out the bottom of the meshed band
        // has nothing left to hit; in open water that is most of the fan, and
        // marching it to full range was the single most expensive thing in the
        // frame.
        if (sy > ceilY || sy < floorY) break;
        const sx = lx + cx * t;
        if (density(sx + warp, fy(sx, sy), feats) > 0) {
          // Refine backwards so the shadow edge does not step in 3 m jumps.
          let lo = t - coarse, hi = t;
          for (let k = 0; k < 4; k++) {
            const mid = (lo + hi) * 0.5;
            const mx = lx + cx * mid, my = ly + cy * mid;
            if (density(mx + warp, fy(mx, my), feats) > 0) hi = mid;
            else lo = mid;
          }
          hit = hi / range;
          break;
        }
        t += coarse;
      }
      this.cone[i] = hit;
    }

    // Props, second, and analytically rather than by marching. Blockers are on
    // the OUTSIDE of this loop because each one covers a handful of bins and
    // knows which: the alternative is every bin testing every prop, which is
    // the same answer for two orders of magnitude more arithmetic.
    if (this.beamProps) this.gatherBlockers(lx, ly, range, lane, laneZ(lane));
    else this.blockers.length = 0;
    for (const q of this.blockers) {
      const ox = q.x - lx, oy = q.y - ly;
      const dist = hyp(ox, oy);
      const rad = Math.max(q.a, q.b);
      // Angular span of the bounding circle. Inside it there is no span to
      // speak of, so every bin is a candidate and the exact solve below decides.
      const aw = dist > rad ? Math.asin(rad / dist) : Math.PI;
      let rel = Math.atan2(oy, ox) - angle;
      rel -= Math.PI * 2 * Math.round(rel / (Math.PI * 2));
      // Anything behind the lamp lands far outside [0, bins) and clamps away.
      const toBin = (r: number) => ((r / half + 1) * 0.5) * bins - 0.5;
      const i0 = Math.max(0, Math.floor(toBin(rel - aw)));
      const i1 = Math.min(bins - 1, Math.ceil(toBin(rel + aw)));
      if (i1 < i0) continue;
      // Ray against an ellipse is ray against a circle once both the origin and
      // the direction are divided by the half-axes.
      const px = ox / q.a, py = oy / q.b;
      const C = px * px + py * py - 1;
      if (C < 0) continue;                              // lamp inside it; never self-shadow
      for (let i = i0; i <= i1; i++) {
        const a = angle + (((i + 0.5) / bins) * 2 - 1) * half;
        const dx = Math.cos(a) / q.a, dy = Math.sin(a) / q.b;
        const A = dx * dx + dy * dy;
        const B = px * dx + py * dy;
        const disc = B * B - A * C;
        if (disc <= 0) continue;
        const t = (B - Math.sqrt(disc)) / A;
        if (t <= 0 || t >= range) continue;
        const norm = Math.max(t, clear) / range;
        if (norm < this.cone[i]) this.cone[i] = norm;
      }
    }

    // Fold the fan into a few sectors and put a dim light where each one
    // lands. Averaging within a sector matters: one light per bin would be 96
    // lights stacked on the same wall, and the budget holds 48 in total.
    this.bounce.length = 0;
    const sectors = LIGHT.bounceSectors;
    const per = bins / sectors;
    for (let s = 0; s < sectors; s++) {
      let sum = 0, hits = 0, angSum = 0;
      for (let i = Math.floor(s * per); i < Math.floor((s + 1) * per); i++) {
        if (this.cone[i] >= 0.999) continue;   // that ray never met anything
        const rel = (((i + 0.5) / bins) * 2 - 1) * half;
        sum += this.cone[i];
        angSum += angle + rel;
        hits++;
      }
      if (!hits) continue;
      const t = (sum / hits) * range;
      const a = angSum / hits;
      this.bounce.push({
        x: lx + Math.cos(a) * t,
        y: ly + Math.sin(a) * t,
        // How much of this sector is actually wall, and how near it is. Light
        // that has travelled further arrives dimmer and bounces back dimmer
        // still, which is why this falls off twice.
        w: (hits / per) * (1 - (t / range) * 0.75),
        d: t,
      });
    }
  }

  // ==========================================================================
  // draw
  // ==========================================================================

  /** `attract` draws the living scene without the actors, which are not
   *  ticking and would hang in the water like furniture. */
  /**
   * The material chart, on F2. Off by default and off in a build the player
   * sees; it exists to make a lighting change measurable rather than argued.
   */
  showProbes = false;

  /** Left-to-right names for the legend, so the UI cannot drift from the row. */
  get probeLabels(): string[] { return PROBES.map((b) => b.label); }

  draw(attract = false) {
    this.renderer.resetQueue(this.camera.viewProjNJ);
    this.propLights = 0;
    this.bgLights = 0;
    this.jellyLights = 0;
    this.shotLights = 0;

    // Culled against the RENDERED centre, not the follow target — panning the
    // view without moving these would cut geometry off at the leading edge.
    const ext = this.camera.halfExtent();
    const cx = this.camera.viewX(), cy = this.camera.viewY();
    const minX = cx - ext.w - 24;
    const maxX = cx + ext.w + 24;
    const minY = cy - ext.h - 24;
    const maxY = cy + ext.h + 24;
    const onScreen = (x: number, y: number, pad = 30) =>
      x > minX - pad && x < maxX + pad && y > minY - pad && y < maxY + pad;

    this.drawPlayer();
    this.drawScenery(minX, maxX, minY, maxY, onScreen);
    this.drawBait(onScreen);
    this.drawStations(onScreen);
    this.drawHabitats(onScreen);
    this.drawGates(onScreen);
    if (attract) return;
    this.drawActors(onScreen);
    this.drawOctopus(onScreen);
    this.drawCrowns();
    if (this.showProbes) { this.drawProbes(); this.drawOreChart(); }
    this.drawOreCallouts();
    this.drawMiningBeam();
    this.drawOreIcons();
  }

  /**
   * The splash crowns, advanced and drawn.
   *
   * ANIMATED BY SCALING THE INSTANCE, not by touching a vertex. The mesh is a
   * unit crown; radius and height are two numbers on the model matrix, and a
   * sheet of water expanding and collapsing is exactly what those two numbers
   * describe. One draw, no buffer writes, nothing per-frame on the CPU beyond
   * a sine.
   *
   * RADIUS RUNS AWAY FAST AND NEVER COMES BACK, because water does not
   * un-expand: `t^0.45` is most of the way out in the first fifth of the life.
   * HEIGHT is a half sine — up, over, down — so the sheet rises, hangs and
   * falls while it is still spreading. The two together are the whole motion;
   * a crown that scaled uniformly would read as a balloon.
   *
   * It holds its opacity through the rise, then thins on a smooth late curve.
   * The old `(1-u)^3` was already at 12.5% when the crown reached peak height,
   * which is why a sizeable mesh still read as a subtle flicker.
   */
  private drawCrowns() {
    if (!this.crowns.length) return;
    this.useLane(-1);
    const r = this.renderer;
    for (let i = this.crowns.length - 1; i >= 0; i--) {
      const c = this.crowns[i]!;
      c.t += this.lastDt;
      const u = c.t / c.life;
      if (u >= 1) { this.crowns.splice(i, 1); continue; }
      const rad = c.r * (0.22 + 1.05 * Math.pow(u, 0.45));
      const hgt = c.h * Math.sin(Math.min(1, u * 1.06) * Math.PI) * 0.92;
      const fq = clamp((u - 0.42) / 0.58, 0, 1);
      const fade = 1 - fq * fq * (3 - 2 * fq);
      // A crown is still rooted in the horizontal water surface, but the sheet
      // it throws is not obliged to be vertical. It follows the crossing for
      // the first half of its life, then gravity straightens the last scraps.
      const throwLean = c.lean * (1 - u * 0.58);
      const travel = c.driftX * c.t * (c.out ? 0.46 : 0.28);
      compose(
        this.m, c.x + travel, c.y, c.z,
        0, 0, throwLean,
        rad * c.stretch, Math.max(0.02, hgt), rad,
      );
      // White, and lit rather than emissive — foam is bright because it
      // scatters an enormous amount of light, not because it makes any. The
      // small emissive floor is the multiple scattering inside a sheet full of
      // bubbles, which no single-bounce term will ever produce.
      // `fade` rides in the alpha, which the foam branch of `fsJelly` reads as
      // the crown's age. White and lit rather than emissive: foam is bright
      // because it scatters an enormous amount of light, not because it makes
      // any.
      this.set(0.92, 0.97, 1.0, fade, 0.0, 0.0, 0.0, 0.0);
      r.queue('crown', this.m, this.tint, this.emis);
    }
  }

  /**
   * One ore cluster or shard, wherever it is.
   *
   * A method rather than inline in the prop switch because the ORE CHART draws
   * through it too, and a chart that sets up its own tint, emission and PBR is
   * a chart of a code path nobody plays. Every dial in the panel therefore
   * moves the row and the seabed by the same amount, which is the only reason
   * the row is worth having.
   */
  private drawOre(variant: number, part: 'node' | 'shard',
                  x: number, y: number, z: number, rot: number,
                  scale: number, deposit: number, display = false,
                  hero = 0) {
    const r = this.renderer;
    const t = MINERAL_TYPES[variant] ?? MINERAL_TYPES[0]!;
    const ore = ORE[variant];
    const key = ore?.key ?? t.key;

    this.tint[0] = t.colour[0]; this.tint[1] = t.colour[1];
    this.tint[2] = t.colour[2];
    // The emission the manifest authored, scaled by one live number. Six
    // minerals carry one; iron, copper and titanium do not, and deliberately
    // never will — a conductor that glows in the dark is a lamp, not a metal.
    // `oreSheen` is what those three get instead.
    const e = t.emissive * Math.max(0, r.seaTune.oreGlow);
    this.emis[0] = t.emission[0] * e;
    this.emis[1] = t.emission[1] * e;
    this.emis[2] = t.emission[2] * e;
    // An icon carries a little of its own colour, because six metres off the
    // lens there is nothing for it to reflect. Metals take most of it — they
    // ship no emission and their colour IS their reflection. Gems take a
    // touch: theirs is already emissive, and flooding a crystal with flat
    // colour is precisely what takes the transparency out of it.
    if (display) {
      const self = ore?.kind === 'metal' ? World.ICON_SELF_METAL : World.ICON_SELF_GEM;
      this.emis[0] += t.colour[0] * self;
      this.emis[1] += t.colour[1] * self;
      this.emis[2] += t.colour[2] * self;
    }
    // Ore is the most polished thing in the water. `emis.w` drives the
    // specular amount and the roughness floor together, so this is what stops
    // a cut facet reading as wet rock.
    this.emis[3] = 0.95;

    // TWO SIZE DIALS, NOT ONE, because the halves of the set are different
    // objects: the gems are spires and geodes that want to be landmarks, the
    // native metals are crusts and nuggets that want to look prised out of the
    // rock. Picked by the DECLARED class, never by `metallic` — see `Ore.kind`.
    //
    // Live multiplies rather than part of the placement, because the seams are
    // generated and cached and baking a size into them costs a reload.
    const big = ore?.kind === 'metal'
      ? r.seaTune.metalOreScale : r.seaTune.crystalScale;
    const os = scale * Math.max(0.05, big);
    // YAW ONLY. A cluster grows up out of its matrix; tipping one over reads as
    // glowing debris that happened to land there.
    compose(this.m, x, y, z, 0, rot, 0, os);
    this.pbr[0] = t.metallic;
    this.pbr[1] = t.roughness;
    // Dielectric F0. 0.17 is well above the 0.04 of ordinary rock: quartz and
    // the sulphides sit between 2.0 and 2.4 IOR, and that difference IS the
    // difference between a stone and a gem.
    this.pbr[2] = 0.17;
    this.pbr[3] = 1;
    // NEGATIVE, AND THE SIGN IS THE WHOLE TRICK. `params2.w` says four things
    // at once: the rig strength, and — when POSITIVE — that this mesh carries
    // baked occlusion in uv.y, hull rivets in local space and a packed base
    // colour in uv.x. A mineral has none of those. uv.y is zero on every
    // vertex, which is what made the first attempt at this render as a fully
    // occluded black lump.
    //
    // `vsMesh` clamps all three of those to zero for a negative value and
    // takes `abs` for the strength, so the sign asks for the lighting without
    // the bake. It was put there for the enemy hulls and fits this exactly.
    //
    // Offset by one because the shader reads a magnitude above 1.5 as "display
    // model, do not fade with depth" and subtracts the 1 back off — so what
    // arrives as `i.hero` is the number the dial says.
    const p2: [number, number, number, number] | undefined =
      hero > 0 ? [0, 0, 0, -(1 + hero)] : undefined;
    r.queue(mineralSlot(`${part}_${key}`), this.m, this.tint, this.emis,
            undefined, p2, this.pbr);

    // A LIGHT IN THE DEPOSIT, for the six minerals that glow.
    //
    // Without one a node is bright and lights nothing, and the giveaway is the
    // boulder underneath staying grey — it reads as a decal stuck on a rock
    // rather than as something in the water. At 55% of the cluster's own
    // height for the reason `ORE_HEIGHT` exists.
    //
    // ONE PER DEPOSIT, not one per cluster. `deposit` is non-zero on exactly
    // the dominant cluster of a group and carries how many clusters are in it,
    // so a thirteen-cluster mother lode spends one light rather than thirteen
    // — which matters against a budget of forty-eight on the lowest tier, and
    // would have looked identical anyway. A deposit is one glow.
    //
    // Its reach and its strength both grow with the count, because that is the
    // honest reading: more crystal is more light, and it is also what makes a
    // rich seam legible from further away than a poor one.
    //
    // `scatter` true, so the WATER glows around it and not only the surfaces it
    // reaches. That is exactly what the flag is for: below four hundred metres
    // an ore seam is the only thing making light besides the boat.
    if (e > 0 && deposit > 0 && r.probe.oreLight) {
      const h = (ORE_HEIGHT.get(`${part}_${key}`) ?? 0.8) * os;
      const n = Math.min(deposit, 13);
      r.addLight(x, y + h * 0.55, z, 4 + h * 2.4 + n * 0.9,
                 t.emission[0], t.emission[1], t.emission[2],
                 e * r.seaTune.oreLight * (1 + n * 0.05), true);
    }
  }

  /**
   * THE CUTTING BEAM, while a seam is being worked.
   *
   * Two world points and a line between them, which the text batch does not do
   * directly — it anchors ONE world position and offsets in screen pixels. So
   * both ends are projected here and the far one becomes a pixel delta from
   * the near one. Under perspective that is an approximation of a straight
   * line in space, and over the fifteen metres this can ever span it is an
   * approximation nobody can see.
   *
   * Drawn in the text pass, which means after the bloom — so it does not
   * glow. That is the one thing this gives up, and it is the reason the impact
   * still throws sparks: the sparks are in the scene and they DO bloom, so the
   * bright end of the beam is real light and the line itself is the path.
   */
  private drawMiningBeam() {
    const m = this.mining;
    if (!m) return;
    const p = this.player;
    const r = this.renderer;
    const cssW = r.cssWidth, cssH = r.cssHeight;
    if (!this.project(p.x, p.y, this.proj2, p.z)) return;
    const ax = this.proj2.x * cssW, ay = this.proj2.y * cssH;
    if (!this.project(m.x, m.y + 1.2, this.proj2, m.z)) return;
    const bx = this.proj2.x * cssW, by = this.proj2.y * cssH;

    const k = clamp(Math.min(cssW / 1280, cssH / 720), 0.58, 1);
    const col = this.oreColours[m.variant];
    const rgb = MINERAL_TYPES[m.variant]?.colour ?? [1, 1, 1];
    void col;
    // Flickering on purpose, and gently: a cutting beam that is perfectly
    // steady reads as a drawn line, and one that flickers hard reads as a
    // fault. A tenth either side at about nine hertz is the difference.
    const pulse = 0.78 + 0.22 * Math.sin(this.time * 57);
    const tint: [number, number, number, number] = [
      Math.min(1, 0.45 + rgb[0] * 0.9),
      Math.min(1, 0.45 + rgb[1] * 0.9),
      Math.min(1, 0.45 + rgb[2] * 0.9),
      pulse,
    ];
    // Anchored at the BOAT, so the near end is exact and the error such as it
    // is accumulates at the rock — which is also where the sparks are, and
    // sparks hide a pixel.
    r.text.line(p.x, p.y, p.z, 0, 0, bx - ax, by - ay, 2.2 * k, tint);
    // A thin white core inside the coloured beam. One line reads as a stripe;
    // two read as something hot with a colour around it.
    r.text.line(p.x, p.y, p.z, 0, 0, bx - ax, by - ay, 0.8 * k,
                [1, 1, 1, pulse * 0.9]);
  }

  /**
   * How much of its own colour an icon emits.
   *
   * NOT the hull's display rig, and that is the point. `params2.w` looks like
   * the way to light a display model and it is not — `vsMesh` does
   * `o.ao = mix(1, uv.y, clamp(params2.w, 0, 1))` and `o.riv = lp * clamp(...)`,
   * so any non-zero value tells the shader this mesh carries BAKED PER-VERTEX
   * OCCLUSION in uv.y and hull-space rivet coordinates in its local position.
   * Mineral meshes have 0 in uv.y, so every icon was being shaded as fully
   * occluded — that is the black, and it also switched on the rivets.
   *
   * The material chart has always known this. Its comment reads "params2.w
   * stays 0 — it gates the baked-occlusion path, which reads the vertex uv and
   * would hand a sphere its own latitude as an AO term." I walked into the
   * exact trap it was written about.
   *
   * So an icon is lit the honest way instead: it emits a little of its own
   * colour, and it gets a light of its own. Metals take most of it because
   * they ship no emission at all and their colour comes entirely from what
   * they reflect — and six metres off the lens in the abyss there is nothing
   * to reflect. Gems take a touch, because their emission already carries
   * them and flooding a crystal with flat colour is exactly what takes the
   * transparency out of it.
   */
  private static readonly ICON_SELF_METAL = 0.55;
  private static readonly ICON_SELF_GEM = 0.10;
  /** Collapsed by the H key or the phone's HOLD button. */
  holdOpen = true;
  /**
   * Metres a scenery jellyfish keeps behind the boat's own plane.
   *
   * Far enough that it is plainly in another layer of water — the lane gaps
   * are 36 m, so 16 puts it clearly past the near face without pushing it into
   * the next slab and out of the frame. Near enough that the field still
   * frames the shot, which is what these are for.
   */
  private static readonly JELLY_BEHIND = 16;
  /** Metres off the lens. Close enough to be UI, far enough not to near-clip. */
  private static readonly ICON_DIST = 6;

  /**
   * THE HOLD, AS THE ACTUAL ORE, top right.
   *
   * A coloured diamond told you a count and nothing else; the thing a player
   * has been looking at for the last twenty seconds is a faceted crystal, and
   * that is what should be in the corner. The SHARD mesh rather than the node:
   * a shard is literally what is in the hold, and it is 20 to 320 triangles
   * against a node's 182 to 2,180.
   *
   * Placed the way the refit turntable is placed — a screen uv turned into a
   * world point in the CAMERA's own basis, because the camera orbits and
   * anything pinned to world -Z climbs out of frame the moment it tilts. That
   * bug has been paid for once already; this is the same three lines.
   *
   * The COUNT goes through the text batch anchored at the icon's own world
   * point, so the glyphs and the crystal are placed by the same matrix and
   * cannot drift apart by a frame.
   */
  private drawOreIcons() {
    if (!this.holdOpen) return;
    const r = this.renderer;
    const eye = this.camera.eye();
    const D = World.ICON_DIST;
    const visH = 2 * D * Math.tan(CAMERA.fov / 2);
    const visW = visH * this.aspect;
    const tilt = this.camera.viewTilt();
    const ct = Math.cos(tilt), st = Math.sin(tilt);
    const fwdY = -st, fwdZ = -ct;
    const upY = ct, upZ = -st;
    const k = clamp(Math.min(r.cssWidth / 1280, r.cssHeight / 720), 0.58, 1);

    // Right to left, so the newest seam does not shove the others sideways
    // every time one is found — the column grows toward the middle and what is
    // already there stays where the eye last saw it.
    let slot = 0;
    const held = this.oreHeld.reduce((a, q) => a + (q > 0 ? 1 : 0), 0);
    for (let i = 0; i < this.oreHeld.length; i++) {
      const n = this.oreHeld[i] ?? 0;
      if (n <= 0) continue;
      const last = slot === held - 1;
      const u = 0.955 - slot * 0.052;
      // BELOW THE ORDNANCE COLUMN, not level with it. TORPEDO, DEPTH CHARGE
      // and the cannon name occupy the top right down to about a seventh of
      // the frame, and the icons were sitting straight on top of them.
      const v = 0.30;
      slot++;
      if (u < 0.5) break;

      const drop = visH * (v - 0.5);
      const ix = eye.x + visW * (u - 0.5);
      const iy = eye.y + fwdY * D - upY * drop;
      const iz = eye.z + fwdZ * D - upZ * drop;

      // Fitted to the slot rather than to itself: the shards run 0.10 to 0.22 m
      // and a raw scale would make silica half the size of copper for reasons
      // that are about geology rather than about the readout.
      const size = MINERAL_MESHES.find((mm) => mm.name === `shard_${ORE[i]?.key}`)?.size ?? [0.2, 0.2, 0.2];
      const span = Math.max(size[0], size[1], size[2]);
      const fit = (visH * 0.052) / Math.max(span, 0.01);
      // Turning, because a faceted stone that is not moving is a flat shape —
      // the whole of what says "crystal" is a highlight crossing the facets.
      this.drawOre(i, 'shard', ix, iy, iz, this.time * 0.7 + i, fit, 0, true);
      // A LIGHT OF ITS OWN, which is what gives a facet a highlight to move
      // across. Short range so it cannot reach the water behind it, and
      // `scatter` off — the glow of a lit medium is what a bioluminescent seam
      // wants and the opposite of what a readout does.
      r.addLight(ix, iy, iz, 1.4, 1, 0.97, 0.92, 3.2, false);

      r.text.text(ix, iy, iz, String(n), 0, 20 * k, {
        colour: [1, 1, 1, 0.92], size: 13 * k, tracking: 1 * k, align: 'center',
      });
      // The capacity, once, under the leftmost icon. A per-icon fraction would
      // be four copies of one number; what the player needs to know is whether
      // there is room, and that is a property of the hold rather than of any
      // seam in it. Amber at three quarters, red when it stops the beam.
      if (last) {
        const used = this.holdUsed;
        const warn: [number, number, number, number] = used >= HOLD_CAPACITY
          ? [1, 0.35, 0.29, 0.95]
          : used > HOLD_CAPACITY * 0.75 ? [1, 0.71, 0.33, 0.9] : [0.6, 0.78, 0.82, 0.8];
        r.text.text(ix, iy, iz, `${used} / ${HOLD_CAPACITY}`, 0, 38 * k, {
          colour: warn, size: 10 * k, tracking: 1 * k, align: 'center',
        });
      }
    }
  }

  /**
   * The ore callouts, on the GPU rather than the HUD canvas.
   *
   * Same shape as the 2D version it replaces — a ring on the crystal, an elbow
   * up and out, a plate and two lines of text — and the same rule about how
   * far it lifts: a third of the way toward the boat, so it clears the rock
   * and drifts with you without becoming a tether. What changes is where it
   * lives.
   *
   * THE ANCHOR IS THE DEPOSIT AND EVERYTHING ELSE IS AN OFFSET, which is what
   * the batch is for. The 2D version had to project the world point itself,
   * every frame, and then draw at whatever pixel came back — so the label
   * moved in steps of one CPU-computed position per frame while the scene
   * underneath moved continuously. Here the anchor goes through the same
   * matrix the geometry does, on the same hardware, at the same time.
   *
   * The projection is still done on the CPU, but only to decide the SHAPE —
   * which side the elbow leans, how far up to lift — and those change slowly.
   */
  private drawOreCallouts() {
    const r = this.renderer;
    const cssW = r.cssWidth, cssH = r.cssHeight;
    // The HUD's own scale rule, and then HALF AS BIG AGAIN on top of it.
    //
    // Matching the readout was the wrong target. A HUD label sits still in a
    // corner with a dark panel behind it and the eye knows where to find it; a
    // callout is out in the water, over rock and weed and its own glow, and
    // has to be read while the boat is moving. At 10 px it was legible in a
    // screenshot and not in play — which is the only test that counts.
    //
    // It also has a second job now that the atlas is mipped: bigger text sits
    // closer to the level it was rasterised at, so less of it is reconstructed
    // from a filter at all.
    //
    // 2.2, and the number has a second job beyond legibility. The atlas is
    // rasterised at 48 px; at 10 px it was being minified nearly five times
    // and at 22 it is barely twice, which is most of the way out of the range
    // where a filter has to invent the answer. Bigger text is literally less
    // reconstructed text.
    const k = clamp(Math.min(cssW / 1280, cssH / 720), 0.58, 1) * 2.2;
    const p = this.player;
    const onScreen = this.project(p.x, p.y, this.proj2);
    const subX = onScreen ? this.proj2.x * cssW : cssW * 0.5;
    const subY = onScreen ? clamp(this.proj2.y, 0.08, 0.86) * cssH : cssH * 0.42;

    let row = 0;
    for (const ct of this.sonar.contacts) {
      if (ct.kind !== 'mineral' || !ct.rgb) continue;
      const cz = ct.z ?? 0;
      if (!this.project(ct.x, ct.y, this.proj2, cz)) continue;
      const sx = this.proj2.x * cssW, sy = this.proj2.y * cssH;
      // On screen only. Off-screen contacts keep the HUD's edge markers, which
      // are a different job — an arrow saying "over there" rather than a label.
      if (this.proj2.x < 0.02 || this.proj2.x > 0.98) continue;
      if (this.proj2.y < 0.02 || this.proj2.y > 0.98) continue;

      const a = clamp(ct.strength, 0, 1);
      const gap = Math.max(0, sy - subY);
      const rise = (Math.min(34 + gap * 0.33, 120) + row * 16) * k;
      row = (row + 1) % 4;

      const tint: [number, number, number, number] = [ct.rgb[0], ct.rgb[1], ct.rgb[2], a * 0.95];
      const name = ct.label ?? '';
      const sub = ct.sub ?? '';
      const nameStyle = { colour: tint, size: 10 * k, tracking: 2 * k } as const;
      const subStyle = {
        colour: [0.75, 0.91, 1.0, a * 0.95] as [number, number, number, number],
        size: 9 * k, tracking: 2 * k,
      } as const;
      // Measured before the side is chosen, because the side depends on it.
      const tw = Math.max(r.text.width(name, nameStyle), r.text.width(sub, subStyle));

      // AWAY FROM THE BOAT, NOT TOWARD THE MIDDLE OF THE FRAME.
      //
      // Leaning toward frame centre is the obvious rule and it is wrong here
      // for one reason: the sub lives at frame centre. A deposit off to the
      // right therefore had its label thrown left, straight across the hull —
      // which is a label written over the one thing on screen the player is
      // always looking at.
      //
      // Away from the boat instead. That is also the side with the deposit
      // between the label and the sub, so the leader never crosses the hull
      // either.
      const lean = Math.min(rise * 0.72, 48 * k);
      const away = sx >= subX ? 1 : -1;
      // ...unless the text would then run off the edge. `reach` is everything
      // the callout occupies horizontally from the anchor, so this asks the
      // real question rather than guessing a margin.
      const reach = lean + 9 * k + tw + 6 * k;
      const fits = away > 0 ? sx + reach < cssW - 6 : sx - reach > 6;
      const dir = fits ? away : -away;
      const ex = dir * lean;
      const ey = -rise;
      const tx = ex + dir * 9 * k;

      // The plate. Still the only thing that makes the second line readable
      // over water that runs from near black in a cave to a bright sun shaft.
      const px0 = dir > 0 ? tx - 3 * k : tx - tw - 3 * k;
      r.text.rect(ct.x, ct.y, cz, px0, ey - 11 * k, px0 + tw + 6 * k, ey + 13 * k,
                  [0.012, 0.082, 0.106, a * 0.5]);
      // The elbow, and a ring on the crystal so the leader does not read as a
      // crack in the rock.
      // 1.5 rather than 1.2, and a floor under it. A quad thinner than a
      // device pixel does not draw a thin line — it covers a pixel centre on
      // some frames and misses it on others, which is a line that blinks. The
      // render target can be BELOW one device pixel per CSS pixel too, because
      // the pixel budget scales the backing store down, so the floor is in
      // device pixels rather than in the units this is authored in.
      const lw = Math.max(1.4 * k, 1.5 / r.gpuDpr);
      r.text.line(ct.x, ct.y, cz, dir * 5 * k, -5 * k, ex, ey, lw, tint);
      r.text.line(ct.x, ct.y, cz, ex, ey, ex + dir * tw, ey, lw, tint);
      const rad = 4.5 * k;
      for (let i = 0; i < 10; i++) {
        const a0 = (i / 10) * Math.PI * 2, a1 = ((i + 1) / 10) * Math.PI * 2;
        r.text.line(ct.x, ct.y, cz,
                    Math.cos(a0) * rad, Math.sin(a0) * rad,
                    Math.cos(a1) * rad, Math.sin(a1) * rad, lw, tint);
      }
      const align = dir > 0 ? 'left' : 'right';
      r.text.text(ct.x, ct.y, cz, name, tx + dir * 1 * k, ey - 2 * k,
                  { ...nameStyle, align });
      r.text.text(ct.x, ct.y, cz, sub, tx + dir * 1 * k, ey + 10 * k,
                  { ...subStyle, align });
    }
  }

  /**
   * THE ORE CHART, on the same key as the material chart.
   *
   * One node every 178 m is the density a find wants and exactly the wrong
   * density for tuning one: to compare what `ore glow` does to lithium against
   * what it does to abyssal crystal you had to fly a kilometre between them
   * and remember. So all eight stand in a row in front of the boat, drawn
   * through `drawOre` — the same function the seabed uses, so a dial that
   * moves the row moves the world.
   *
   * Shards beneath their own node, because the shard is what the player
   * actually collects and it is a different mesh with the same material.
   */
  private drawOreChart() {
    this.useLane(-1);
    const p = this.player;
    const n = MINERAL_TYPES.length;
    for (let i = 0; i < n; i++) {
      const x = p.x + p.facing * 20 + (i - (n - 1) / 2) * 5.2;
      // Below the sphere row, which sits at +2.6, and far enough under it that
      // a four-metre spire does not grow up through the chart above it.
      this.drawOre(i, 'node', x, p.y - 9.5, p.z, 0, 3.4, 1);
      this.drawOre(i, 'shard', x, p.y - 12.6, p.z, this.time * 0.6, 3.4, 0);
    }
  }

  /**
   * The material chart, riding above the boat. See `PROBES`.
   *
   * Drawn in the player's own lane and with the play layer's focus channel, so
   * the depth of field, the fog, the lamp and the caustics all treat these
   * exactly as they treat anything the player could run into. A probe that
   * needed its own rendering path would be measuring the path, not the light.
   */
  private drawProbes() {
    this.useLane(-1);
    const r = this.renderer;
    const p = this.player;
    for (let i = 0; i < PROBES.length; i++) {
      const b = PROBES[i]!;
      this.tint[0] = b.tint[0]; this.tint[1] = b.tint[1]; this.tint[2] = b.tint[2];
      this.tint[3] = 1;
      this.emis[0] = b.emis[0]; this.emis[1] = b.emis[1]; this.emis[2] = b.emis[2];
      this.emis[3] = b.spec;
      // IN THE BEAM, WHICH IS WHERE THE LIGHT IS.
      //
      // The row hung 7.5 m above the hull, and the lamp is a spot pointing
      // FORWARD — so the chart sat outside the one strong light in the game and
      // was lit by the ambient dome alone. Every material looked the same
      // because every material was getting the same flat wash, and the only
      // thing that ever moved them was a point light passing close: a shot, or
      // the flashlight's own spill.
      //
      // CENTRED ON THE BOAT AND BELOW IT, not ahead of it.
      //
      // `p.facing * 20` put the row twenty metres off to one side, so it left
      // the frame whenever the boat turned and it could never be centred to
      // zoom in on — which is the one thing a reference chart has to allow.
      // Centring on the boat instead means it is always in shot, always the
      // same size, and turning around does not throw it away.
      //
      // BELOW, because the lamp is a forward-pointing spot and the row wants to
      // be lit by it rather than by the ambient dome alone — the whole point of
      // the chart is a highlight you can watch move. Below and a little forward
      // still catches the edge of the cone, and it stops the row from cutting
      // across the hull, which it did at +2.6.
      //
      // No rotation: a sphere has none to show, and a still ball is what makes
      // a MOVING highlight legible.
      compose(this.m,
              p.x + (i - (PROBES.length - 1) / 2) * 3.4,
              p.y - 5.2, p.z, 0, 0, 0, 1.3);
      // params2.w stays 0 — it gates the baked-occlusion path, which reads the
      // vertex uv and would hand a sphere its own latitude as an AO term.
      // `pbr.w` 2, not 1: override the material AND show it clean. The detail
      // set tiles every 2.38 m and these balls are 2.6 m across, so each was
      // wearing one whole tile of rock or plating — which is what a reference
      // chart is specifically not for. See `refBall` in scene.wgsl.
      r.queue(b.slot, this.m, this.tint, this.emis, undefined, [0, b.bio, 0, 0],
              b.pbr ? [b.pbr[0], b.pbr[1], b.pbr[2], 2] : undefined);
    }
  }

  /**
   * Draw the CAD hull as its tree of parts.
   *
   * `this.m` already holds the hull's world matrix when this is called, so the
   * pass is one forward walk: a part's world matrix is its parent's times its
   * own local, and the baker guarantees parents come first. Nothing recurses
   * and nothing is allocated per frame.
   *
   * EVERY PART TURNS ABOUT ITS LOCAL Z. That is the rig's convention, verified
   * across all 23 of its controls in Blender, and it is why there is not a
   * single pivot coordinate in this function — the pivot is the part's own
   * transform, which came out of the GLB.
   *
   * `params2.w = 1` is what tells the shader this instance carries baked
   * occlusion in uv.y. Every other mesh in the game leaves it at 0 and keeps
   * the flat 1.0 it has always had.
   */
  /** Metres in front of the lens the display hull hangs at. */
  private static readonly SHOW_DIST = 9;

  /**
   * The boat, turning over, inside the refit screen's viewport.
   *
   * Drawn IN THE WORLD rather than in a pass of its own, which is what makes
   * this affordable: no second camera, no second globals buffer, no extra
   * target. The HUD leaves a hole and says where it is, and the hull is simply
   * placed at the point that PROJECTS into that hole — a few metres off the
   * lens, where the frustum is narrow enough that a boat fills a corner of it.
   *
   * The arithmetic is only easy because of the game this is. The camera looks
   * down -Z with no roll and no pitch, so right is +X and up is +Y and the
   * offsets come straight out of the visible extent at that distance. In a
   * free-look game this would need the basis; here it does not.
   *
   * It carries its own key and fill. Nine metres from the lens is sixty metres
   * from the boat, so nothing in the scene's light budget reaches it — without
   * these it would be a silhouette lit by ambient alone, which is the exact
   * failure the three-point rig exists to prevent.
   */
  private drawShowcase(p: Player) {
    const s = this.showcase;
    if (!s) return;
    const D = World.SHOW_DIST;
    const eye = this.camera.eye();
    const visH = 2 * D * Math.tan(CAMERA.fov / 2);
    const visW = visH * this.aspect;

    // THE CAMERA ORBITS NOW, AND THIS PANEL DID NOT KNOW.
    //
    // Every placement here used to be `eye + (0, 0, -D)`: straight down the
    // world's -Z, which is the view axis only while the camera is head-on. `V`
    // cycles a 0/10/20-degree tilt, and at 20 the eye is lifted and pitched
    // down — the view axis leaves -Z and the panel's rectangle goes with it,
    // while the model, pinned to an axis the camera is no longer looking along,
    // climbs out of the top of the frame. At the shipped viewport it lands
    // about 4.9 m above the centre of a frustum whose half-height at that
    // distance is 4.2.
    //
    // That is the whole of "the sub disappeared from the shop". It was never
    // not drawn. It was drawn off screen — which is also why the black card
    // went with it, and why the sea shows through the hole where both belong
    // instead of the panel simply being empty. One symptom, one cause, and the
    // giveaway was that the BACKDROP was missing too: nothing that only
    // affected the hull could have taken the card as well.
    //
    // So the offsets are taken in the CAMERA's own basis. There is no roll, so
    // right stays world X and only the forward/up pair turns; at tilt 0 this
    // reduces to exactly the three lines it replaced.
    const tilt = this.camera.viewTilt();
    const ct = Math.cos(tilt), st = Math.sin(tilt);
    const fwdY = -st, fwdZ = -ct;
    const upY = ct, upZ = -st;
    /** Down-screen offset for a rect centre: v is measured from the TOP. */
    const drop = visH * (s.v - 0.5);
    const cx = eye.x + visW * (s.u - 0.5);
    const cy = eye.y + fwdY * D - upY * drop;
    const cz = eye.z + fwdZ * D - upZ * drop;

    // EACH AXIS AGAINST THE ONE IT ACTUALLY HAS TO FIT IN.
    //
    // This fitted the hull's LENGTH into the smaller side of the viewport, out
    // of a worry about the boat swinging through the frame as it turns. But it
    // turns about the vertical, so its height never changes — only its width
    // does, between its beam nose-on and its full length side-on. The viewport
    // is 400 by 224, so the smaller side is the HEIGHT, and a seven-metre boat
    // was being fitted into it: correct, cautious, and a third of the size it
    // could be.
    //
    // Length against the width, height against the height. The width case is
    // the worst case already, since side-on is as wide as it ever gets.
    const scale = Math.min(
      (visW * s.wf * 0.88) / SUB.length,
      (visH * s.hf * 0.80) / 3.2,
    );
    const fit = SUB.length * scale;

    const r = this.renderer;
    this.useLane(-1);
    // Nose a little toward the viewer and tipped down, so it reads as a model
    // on a stand rather than as a broadside elevation drawing.
    // THE CARD GOES IN FIRST, AND THAT IS NOT A STYLE NOTE.
    //
    // `this.m` is one shared scratch matrix, and `drawSub2` reads it as the
    // hull's root long after this line has run. Composing the card into it
    // after the hull drew the boat with the CARD's transform: a flat sheet the
    // width of the viewport, no rotation, filling the panel. Build the card
    // first, then the hull, and the last thing written is the thing that is
    // read.
    //
    // Sized to overfill the viewport at its own
    // distance, and black, so the hull in front of it reads by contrast rather
    // than by output — which is what lets it stay under the bloom threshold
    // and still be the clearest thing on the panel.
    // Sized and placed for the VIEWPORT, not the screen. It sits further out
    // than the hull, so the same rectangle of screen is a bigger rectangle of
    // world — the projection has to be redone at its own distance rather than
    // reusing the hull's, or the card lands the wrong size in the wrong place.
    // A tenth over on each side, so the edge is never in frame.
    const bd = D + fit * 1.6;
    const bvH = 2 * bd * Math.tan(CAMERA.fov / 2);
    const bvW = bvH * this.aspect;
    const bdrop = bvH * (s.v - 0.5);
    this.set(0, 0, 0, 1, 0, 0, 0, 0);
    compose(
      this.m,
      eye.x + bvW * (s.u - 0.5),
      eye.y + fwdY * bd - upY * bdrop,
      eye.z + fwdZ * bd - upZ * bdrop,
      // Pitched to face the lens. A flat card left square to the world is seen
      // edge-on by a tilted camera and stops covering the rectangle it exists
      // to cover, which would have swapped one hole for a thinner one.
      -tilt, 0, 0,
      bvW * s.wf * 0.55, bvH * s.hf * 0.55, 1,
    );
    r.queue('backdrop', this.m, this.tint, this.emis, undefined, [0, 0, 0, 0]);

    // The tip is meant to be relative to the VIEWER — "nose toward you, tipped
    // down" — so the camera's own pitch comes out of it. Left in, a 20-degree
    // view added its tilt to the model's and the boat presented its deck.
    compose(this.m, cx, cy, cz, 0.10 - tilt, this.showAngle, 0, scale);

    // CARGO ON THE STAND. Same card, same turntable, same lighting — only the
    // thing standing on it changes. Returning before the hull is built is what
    // makes it a swap rather than an overlay: `this.m` is one scratch matrix
    // and whatever composes into it last is what gets drawn.
    if (this.showcaseOre !== null) {
      const key = ORE[this.showcaseOre]?.key;
      // THE SHARD, NOT THE NODE — the same mesh the hold readout uses.
      //
      // A node is the whole outcrop as it sits in the rock: a matrix with
      // several crystals growing out of it, which is the right object to fly up
      // to and the wrong one to be handed a receipt for. What the hold contains
      // is shards, the panel is a picture of what is in the hold, and a cargo
      // manifest that illustrates three shards with a boulder is answering a
      // question nobody asked.
      const size = MINERAL_MESHES.find((mm) => mm.name === `shard_${key}`)?.size;
      // FIT THE MESH, not a constant. The nodes differ by better than 3x in
      // span — a lone iron crust against an abyssal spire — and a fixed scale
      // would show one filling the frame and the next as a speck, which reads
      // as a bug rather than as a size difference.
      const span = size ? Math.max(size[0], size[1], size[2]) : 1;
      const os = Math.min(visW * s.wf * 0.62, visH * s.hf * 0.62) / Math.max(0.05, span);
      // Lifted by a third of its own height: the meshes are recentred with y=0
      // at the lowest point, so composing at the frame's centre would hang the
      // whole cluster below it.
      const lift = size ? size[1] * os * 0.5 : 0;
      // `display` true — the icon path, which adds the touch of self-colour
      // that keeps a metal from being a black silhouette this far from any
      // light. See `ICON_SELF_METAL`.
      //
      // And the hero rig, which is the rest of the answer: a stone lit by the
      // sea at whatever depth the station happens to be is lit by a dome, and
      // a dome gives every facet the same value — which is a flat shape. The
      // rig gives it a direction and the studio gives it a room to reflect.
      this.drawOre(this.showcaseOre, 'shard',
                   cx, cy - lift, cz, this.showAngle, os, 0, true,
                   Math.max(0, r.seaTune.oreStudio));
      return;
    }

    const keep = { x: this.clawTip.x, y: this.clawTip.y };
    const sub2 = this.subParts !== null && p.chassis.mesh === 'sub';
    // Lit for LOOKING AT. The three-point rig fades with depth on the boat in
    // the water, which is right there and wrong here: a hull on a stand should
    // not go dark because the station it is moored at happens to be shallow.
    // Above 1.5 the shader skips the fade and reads the excess as the
    // strength, so the +1 here is what says "display model" and the dial is
    // the strength. At the default 1.1 that is the 2.1 this used to be written
    // as — against the black card above, the hull no longer has to out-shine a
    // lit sea to be read, so it can sit under the bloom threshold and stop
    // wearing a halo.
    //
    // Live, and the same dial the cargo uses, because two objects share this
    // turntable now and they have to be balanced against each other. That
    // cannot be done while one of them is a constant in the middle of a draw
    // call.
    this.sub2Hero = 1 + Math.max(0, r.seaTune.subStudio);
    if (sub2) {
      // Cooler than the boat idles at, and the running lights come down with
      // it. Under the bloom threshold they describe the boat; over it they
      // become the boat.
      this.sub2Emis = 0.3;
      this.drawSub2(p, 0.10);
      this.sub2Emis = 1;
    } else {
      this.set(1, 1, 1, 1, 0.9, 1.5, 1.7, 0.6);
      r.queue(p.chassis.mesh, this.m, this.tint, this.emis, undefined, [0, 0, 0.42, 0]);
    }
    this.sub2Hero = 1;
    // `drawSub2` reports the claw's position for the tow line to hang from, and
    // the display hull is not the one holding anything.
    this.clawTip.x = keep.x; this.clawTip.y = keep.y;

    // AND IT LIGHTS NOTHING. There were two point lights here to shape the
    // hull, and they were real lights in real water a few metres off the lens —
    // so the volumetric marched them and the bloom found them, and the model
    // sat inside a halo of lit fog. A display model has no business putting
    // light into the ocean.
    //
    // Nothing is needed in its place. Shallow, the ambient is bright and does
    // the job on its own; deep, the ambient is a dome and the three-point rig
    // is at full strength for exactly that reason — which is the same handover
    // the rig was keyed to `sunAt` for, working here for free.
  }

  /**
   * Rig strength for the next `drawSub2`. 1 is the boat in the water, which
   * fades with depth; 2 is the display model, which does not.
   */
  private sub2Hero = 1;
  /**
   * Emissive scale for the next `drawSub2`.
   *
   * The running lights sit at 0.9 to 1.7 and the drive above 1, all of which
   * clear the bloom threshold at 0.85 — correct in the water, where they are
   * meant to be the brightest things for fifty metres, and wrong on a stand
   * against black, where bloom turns them into one white cloud the size of the
   * boat with the hull somewhere inside it.
   */
  private sub2Emis = 1;

  private drawSub2(p: Player, heat: number) {
    const parts = this.subParts;
    if (!parts) return;
    while (this.partWorld.length < parts.length) this.partWorld.push(new Float32Array(16));
    this.clawTip.x = 0;
    this.clawTip.y = 0;

    // NEAR WHITE, because the colour now comes from the vertices.
    //
    // The single grey the boat used to be drawn in was doing the job of an
    // albedo: every material multiplied through it and came out the same. With
    // `base` carrying each part's own colour, this goes back to being what a
    // tint is for — the state modifiers — and 1.0 means "as painted".
    const flash = p.invuln > 0 ? 0.5 : 0;
    const over = p.overdrive > 0 ? 1 : 0;
    const aeg = p.aegis > 0 ? 1 : 0;
    const e = this.sub2Emis;
    this.set(
      1.0 + flash + over * 0.35, 1.0 + flash * 0.3, 1.0 + aeg * 0.45, 1,
      (0.9 + over * 3) * e, (1.5 + aeg * 1.5) * e, (1.7 + aeg * 4) * e, 0.6,
    );

    for (let i = 0; i < parts.length; i++) {
      const q = parts[i];
      if (q.parent < 0) {
        // The hull IS the root, and `this.m` is already its world matrix.
        this.partWorld[i].set(this.m);
      } else {
        // The link slides out by its PARENT's stretch, because a part's
        // translation is expressed in the parent's frame — segment 2 moves down
        // segment 1 as segment 1 lengthens.
        const up = parts[q.parent].name;
        sub2Local(this.partLocal, q.part, this.sub2Angle(q.name, p),
          this.sub2Stretch(up), sub2PitchJoint(q.name));
        mat4Mul(this.partWorld[i], this.partWorld[q.parent], this.partLocal);
      }
      // The grip's centre, for the tow line to hang from. Averaged over the
      // three fingertips rather than taken from the wrist, because that is
      // where a claw actually holds something.
      if (q.name.endsWith('_Seg3')) {
        this.clawTip.x += this.partWorld[i][12] / 3;
        this.clawTip.y += this.partWorld[i][13] / 3;
      }
      // The part's own mesh is elongated at DRAW time only, never in the matrix
      // its children inherit.
      const k = this.sub2Stretch(q.name);
      const m = k === 1 ? this.partWorld[i] : sub2Stretch(this.partDraw, this.partWorld[i], k);
      // Only the hull carries drive-tagged material, so only it needs throttle.
      this.renderer.queue(
        q.slot, m, this.tint, this.emis, undefined,
        [0, 0, q.parent < 0 ? heat : 0, this.sub2Hero],
      );
    }
  }

  /**
   * How far each moving part is turned, in radians about its own local Z.
   *
   * The control planes deflect with the boat's PITCH ATTITUDE rather than with
   * the raw stick. A control surface that snaps to full deflection the instant
   * a key goes down and snaps back the instant it lifts reads as a switch;
   * tying it to the attitude the surfaces are there to produce means they lead
   * into a dive and centre as it settles, which is what the player already
   * expects from watching aircraft. The X-tail's upper and lower pairs deflect
   * in opposite senses, which is how an X-tail pitches.
   *
   * The turret elevates and does not yaw: its base turns about the vertical,
   * which in a side-on view moves nothing the player can see, while its head
   * turns in the screen plane. So the aim goes to the head and the base sits
   * still rather than spending a matrix on an invisible rotation.
   */
  /**
   * Uniform scale on a part, which COMPOUNDS down the tree.
   *
   * Only the manipulator uses it, and only its shoulder needs to: scaling
   * Arm_S1 lengthens the forearm, wrist and all nine finger joints with it,
   * because every child's matrix is multiplied by its parent's.
   *
   * This is how the arm reaches. The linkage is rigid — the rig has no
   * prismatic joint and the segments telescope by only about 6 cm, so pulling
   * them apart opens visible gaps long before the claw gets anywhere. Growing
   * the whole assembly instead gets the reach without the seams, and because
   * it is tied to `armDeploy` it happens WHILE the arm swings out, which reads
   * as a manipulator extending rather than as a boat with a rubber arm.
   *
   * Stowed it is 1.0, so the folded arm still tucks inside the hull's line.
   */
  /**
   * How far a manipulator LINK is drawn out along its own length, 1 = stowed.
   *
   * This replaced a uniform scale on the shoulder, which grew the whole
   * assembly — claw, fingers and all — and read as the hand ZOOMING rather than
   * reaching. A boom does not get bigger, it gets longer: only the three arm
   * segments stretch, along the axis they run in, and the wrist and fingers
   * stay the size they were modelled. So the claw arrives at the far end still
   * looking like a claw.
   *
   * Two places use it and they are not the same use. The link's own mesh is
   * elongated by its OWN value, at draw time. Its child is pushed away by its
   * PARENT's, because a child's translation is stated in the parent's frame.
   */
  private sub2Stretch(name: string): number {
    return name === 'Arm_S1' || name === 'Arm_S2' || name === 'Arm_S3'
      ? 1 + this.armDeploy * World.ARM_EXTEND : 1;
  }

  /**
   * How much longer each arm segment gets on the way out.
   *
   * The modelled manipulator is a survey-sized thing about 0.9 m long, which
   * disappears against a 7.2 m hull — the point of a grabber is that you can
   * watch it go and get something.
   *
   * 6.0 puts the grip about 8.1 m below the axis, which is `fairleadDrop` plus
   * `GRAPPLE.minRest`: the depth a load settles to on its tether. So the crate
   * arrives in the claw instead of hanging past it. The arm's reach IS the grab
   * range now — see `GRAPPLE.range`, which is set from this.
   */
  private static readonly ARM_EXTEND = 6.0;

  private sub2Angle(name: string, p: Player): number {
    switch (name) {
      case 'Propeller': return p.propSpin;
      // `* facing`, and it is the AXIS FIX that requires it, not a preference.
      // `lampSwivel` is stored as a delta between two WORLD headings, and the
      // hull's heading turns by π when the boat comes about — so the delta
      // flips sign with `facing`. The mesh is drawn through that same half
      // turn, which flips it back. One of the two has to be undone.
      //
      // It did not matter while this joint was yawing about the vertical,
      // because yawing a searchlight housing by ±0.6 either way looks the
      // same. Now that it pitches, the sign is visible.
      case 'Lamp_Head': return p.lampSwivel * p.facing;
      // The gun tracks THE GUN. It used to track the lamp, on the reasoning
      // that the player aimed both with one stick and a turret pointing
      // somewhere other than where the laser went would be visibly wrong.
      // The reasoning was right and the conclusion was backwards: the fix for
      // a turret that disagrees with its shot is to make the shot follow the
      // turret, not to pin the turret to a searchlight. `turretSwivel` is the
      // same quantity in the same local frame — see `gunAngle` — so this is
      // still one number driving both, and now it is the gun's own.
      case 'Turret_Head': return p.turretSwivel;  // already hull-local
      case 'Plane_UpperPort':
      case 'Plane_UpperStbd': return clamp(p.pitch * 1.1, -0.5, 0.5);
      case 'Plane_LowerPort':
      case 'Plane_LowerStbd': return clamp(p.pitch * -1.1, -0.5, 0.5);

      // ── the manipulator ──────────────────────────────────────────────────
      //
      // Only S1 and S3 fold. Measured off the baked rig rather than assumed:
      // in game space S1 and S3 turn about world +Z, which is the screen
      // plane, while S2 and the wrist turn about the boat's length axis and so
      // only ROLL the arm — invisible from a side-on camera and not worth a
      // matrix. Two joints is also all a fold needs: an elbow and a shoulder.
      //
      // Stowed is -1.5 and -1.2, which swings the arm aft and folds the
      // forearm back up so the whole thing lies along the keel instead of
      // hanging 2.1 m below the axis. That was the reason the arm was very
      // nearly cut from the model altogether: deployed, it reaches well past
      // `SUB.radius` and dips into seabed the boat is told it clears. Stowed,
      // it does not, and it is still there when the player needs it.
      // Solved against the baked rig rather than eyeballed. -1.5/-1.2 was the
      // first guess and it folded the forearm up THROUGH the keel: the claw
      // came to rest at y -0.29, inside the pressure hull. The shoulder is
      // fixed at -0.96 in the ArmBoot and cannot move, so the test is whether
      // everything below it stays under the skin. At -1.30/-0.20 the chain
      // spans -1.26..-0.96 with the tip at (-0.85, -1.11): lying along the
      // keel, aft of its boot, clear of the hull and inside `SUB.radius`.
      case 'Arm_S1': return (1 - this.armDeploy) * -1.30;
      case 'Arm_S3': return (1 - this.armDeploy) * -0.20;

      // The bay doors are shut. They are rigged and the engine can open them
      // the moment there is something in the bay worth showing.
      default:
        // Nine finger joints, curling together. The outer segments curl hardest
        // so the claw closes into a grip rather than collapsing into a cone.
        if (name.startsWith('Finger')) {
          const seg = name.endsWith('Seg3') ? 1.0 : name.endsWith('Seg2') ? 0.7 : 0.45;
          return (0.25 + this.armGrip * 0.75) * seg * 0.8;
        }
        return 0;
    }
  }

  /**
   * The boat's own instrument panel, on the outside.
   *
   * Everything the HUD says about the sub is said in a corner of the screen,
   * which is the one place the player is not looking during a fight — they are
   * looking at the boat. These are five beads on the hull that say the same
   * things in the same place as the thing they are about, so damage, shields
   * and a running powerup read without a glance away.
   *
   * They are emissive geometry and NOT dynamic lights. The renderer keeps 48
   * lights and drops the rest, the boss and the lamp have first claim on them,
   * and five beads that could push a boss's lighting off the list would be a
   * bad trade for a detail this small. Emissive is per-instance data already
   * being sent: it costs nothing and lights nothing.
   */
  private drawStatusLeds(p: Player) {
    this.useLane(-1);
    const r = this.renderer;
    const S = p.stats;
    const t = this.time;
    const rr = p.roll, ry = p.yaw, rp = p.pitch;

    // Steady 1 Hz for calm, a hard 4 Hz throb once the hull is in trouble.
    const hurt = clamp(1 - p.hull / (S.maxHull * 0.35), 0, 1);
    const slow = 0.45 + 0.55 * Math.pow(Math.max(0, Math.sin(t * 2.0)), 2);
    const fast = 0.25 + 0.75 * Math.pow(Math.max(0, Math.sin(t * 8.4)), 2);
    const beat = hurt > 0 ? slow + (fast - slow) * hurt : slow;

    // Powerups override the panel: a running aegis or overdrive is the most
    // important thing about the boat for as long as it lasts, and it is exactly
    // what a player loses track of.
    const aegis = p.aegis > 0;
    const over = p.overdrive > 0;
    const shielded = S.maxShield > 0 && p.shield > S.maxShield * 0.02;

    // ON THE MAST, mostly.
    //
    // The sidelights used to sit on the roof of the sail, which is a flat box —
    // they lit a plate and nothing else. The antenna is the one part of this
    // boat that stands clear of everything and can be lit from beside itself,
    // so a bead on it reads as a lamp on a mast instead of a dot on a lid, and
    // the rod picks up the colour along its whole length.
    //
    // Measured off the mesh: the mast is a rod at x +0.97 running y 1.38 to
    // 2.14 with a radius of 0.07, so z ±0.11 puts a bead just proud of it and
    // the masthead sits on the centreline at the tip.
    //
    // x aft-to-fore, y above the axis, z outboard.
    const enum Role { Port, Star, State, Drive }
    /**
     * ON THE FIXTURES THE HULL ACTUALLY HAS.
     *
     * The list below it was measured off the scanned hull, whose only lightable
     * feature was the mast. The MK II carries real navigation lights, a
     * masthead, a stern light and a pair of overdrive LEDs as modelled
     * geometry with emissive materials, so the beads go ON them: the bead
     * supplies the STATE the static geometry cannot, and the geometry supplies
     * the fitting the bead used to have to imply.
     *
     * Measured in Blender and converted to game space (game = (bx, bz, -by)
     * scaled by 7.2 / model length), so these move if the model does.
     *
     * PORT IS AT -Z HERE, which is the opposite of the list below. That is not
     * a mistake in either: the Fusion rig's port/starboard labels are mirrored
     * relative to the game's, and the model's own red LED is baked into the
     * hull at -Z. Following the mesh keeps the red bead on the red lamp; the
     * alternative puts a red state bead on a green running light.
     */
    const SUB2_BEADS: [number, number, number, Role][] = [
      [2.556, 0.107, -0.607, Role.Port],   // NavLight_Port, LEDRed in the mesh
      [2.556, 0.107, 0.607, Role.Star],    // NavLight_Stbd, LEDGreen
      [0.603, 2.152, 0, Role.State],       // MastheadLight, at the mast tip
      [-2.960, -0.010, 0, Role.State],     // SternLight
      [-3.018, 0.001, 0.260, Role.Drive],  // OverdriveLEDs, the two lobes
      [-3.018, 0.001, -0.272, Role.Drive],
    ];
    /** The scanned and procedural hulls, whose fittings are elsewhere. */
    const LEGACY_BEADS: [number, number, number, Role][] = [
      [0.97, 1.78, 0.11, Role.Port],
      [0.97, 1.78, -0.11, Role.Star],
      [0.97, 2.12, 0, Role.State],
      [2.05, 0.62, 0, Role.State],
      [-1.60, 0.72, 0, Role.State],
      [-2.60, 0.30, 0, Role.Drive],
    ];
    // Same gate as the hull draw: these fixtures are the Pathfinder's MK II
    // hull. The Dray has its own fittings and its own table below.
    const BEADS = this.subParts !== null && p.chassis.mesh === 'sub'
      ? SUB2_BEADS : LEGACY_BEADS;

    // Last frame's light usage, which is the only measure available before this
    // frame's lights are placed — and close enough, because the thing that
    // saturates the budget is a horde, and a horde does not appear in one frame.
    const crowded = r.stats.lights >= 40;

    // Accumulated for the spill below: the colour of the boat's own running
    // lights, weighted by how hard each is burning.
    let sr = 0, sg = 0, sb = 0, sk = 0;

    for (let i = 0; i < BEADS.length; i++) {
      const [bx, by, bz, role] = BEADS[i];
      // Sidelights keep their own colour; everything else reports state.
      const port = role === Role.Port, star = role === Role.Star;
      let cr: number, cg: number, cb: number, k: number;
      if (aegis) {
        cr = 0.45; cg = 0.85; cb = 1.0; k = 2.2 + Math.sin(t * 6 + i) * 0.6;
      } else if (over) {
        cr = 1.0; cg = 0.45; cb = 0.15; k = 2.4 + Math.sin(t * 9 + i) * 0.8;
      } else if (hurt > 0.35 && !port && !star) {
        cr = 1.0; cg = 0.16; cb = 0.10; k = 0.7 + beat * 3.0;
      } else if (port) {
        cr = 1.0; cg = 0.18; cb = 0.16; k = 0.5 + beat * 1.1;
      } else if (star) {
        cr = 0.22; cg = 1.0; cb = 0.34; k = 0.5 + beat * 1.1;
      } else if (shielded) {
        cr = 0.40; cg = 0.80; cb = 1.0; k = 0.6 + beat * 0.9;
      } else {
        // No shield left is worth saying out loud, quietly and in amber.
        cr = 1.0; cg = 0.62; cb = 0.18; k = 0.5 + beat * 1.3;
      }
      // The aft bead doubles as the drive tell: it runs with the throttle.
      const burn = role === Role.Drive ? this.nb('lights') : 0;
      if (role === Role.Drive) {
        k *= 0.5 + p.thrustLevel * 1.2 + burn * 2.2;
        // AND THE LIGHT TURNS VIOLET WITH THE FLAME.
        //
        // The exhaust emissive goes deep violet under nitro; the light thrown
        // by the same fixture stayed whatever the hull was reporting — amber,
        // or white, or red. So the boat had a violet burner casting an amber
        // glow on the rock beside it, which is the one combination that cannot
        // happen. A light and the thing emitting it have to agree.
        cr += (0.62 - cr) * burn;
        cg += (0.20 - cg) * burn;
        cb += (1.00 - cb) * burn;
      }

      rotateOffset(this.hp, rr, ry, rp, bx, by, bz);
      compose(this.m, p.x + this.hp.x, p.y + this.hp.y, p.z + this.hp.z, rr, ry, rp, 0.085);
      this.set(cr * 0.5, cg * 0.5, cb * 0.5, 1, cr * k, cg * k, cb * k, 0);
      r.queue('led', this.m, this.tint, this.emis);

      // AND EACH ONE LIGHTS THE WATER.
      //
      // These were emissive-only, on the reasoning that five beads must not
      // crowd a boss out of the 48 dynamic lights. The budget argument holds —
      // it is why there are five of these and not fifteen — but the conclusion
      // was wrong: a lamp that glows without touching anything reads as a decal
      // painted on the hull, and the whole point of a warning light is that you
      // catch it in the corner of your eye because the plating around it went
      // red. Five of forty-eight, with the scenery already self-capped at
      // twenty-two, is affordable.
      //
      // Scaled to what they are, but not TIMIDLY. The first pass reached about
      // three metres, which on a boat six metres long never leaves the hull —
      // the beads lit their own plating and the water beside them stayed black,
      // so they only registered at all when the alarm turned everything red.
      // Six metres or so puts light into the water the boat is moving through,
      // which is the thing that makes them read as lamps rather than as paint.
      //
      // Still nothing like the drive: that flare runs to 25 m at intensity 2.3
      // because it is a jet. Driven by the same `k` as the emissive, so the
      // light and the glow can never disagree about what the boat is doing —
      // and the aft bead flares with the throttle for free, since its `k`
      // already carries the thrust and nitro terms.
      // ONE LIGHT PER STATION, NOT PER BEAD, AND SMALLER THAN BEFORE.
      //
      // The first pass gave all six a light of their own at 3.2-6 m. Two
      // problems, one cause: the port and starboard beads are 0.22 m apart on
      // the mast, so their two lights are the same light at any zoom the game
      // is ever played at — and seven lights out of the thirty in a frame is a
      // real bill on a renderer that loops every light per fragment. The boat
      // also ended up lighting ITSELF so well it was fully legible in water
      // that is supposed to be dark, which costs the searchlight its job.
      //
      // Port is skipped and starboard stands for both, and the reach and
      // intensity come down by roughly a third. They still wash the plating and
      // the mast; they no longer replace the lamp.
      // AND THEY ARE THE FIRST THING TO GO WHEN THE FRAME IS FULL.
      //
      // Measured in Survival at wave 13: 48 of 48 lights used and two dropped,
      // with the CPU at 2.5 ms of an 18.9 ms frame — so the mode is fill-bound
      // and `points()` in scene.wgsl, which every shaded fragment runs, is the
      // loop paying for it. Five of those forty-eight were beads on the hull:
      // decoration, on a boat the player can already see, competing with the
      // things shooting at them. The emissive stays either way; only the light
      // yields, and only when the budget is actually contended.
      //
      // The burner is the exception to the yield rule. A bead is decoration on
      // a boat the player can already see; a jet lighting the tunnel it is
      // tearing through is the single most dramatic thing on screen, and it
      // lasts as long as the key is held. One light out of forty-eight, only
      // while nitro is lit, is worth it — and dropping THAT one is the drop
      // the player would actually notice.
      if ((!port && !crowded) || burn > 0.05) {
        r.addLight(
          p.x + this.hp.x, p.y + this.hp.y, p.z + this.hp.z + 0.35,
          // Reach past the hull. At `k` alone the burner lit its own plating
          // and about four metres of water, on a boat seven metres long, so the
          // rock it was passing never knew. 24 m at full burn does.
          2.4 + k * 1.1 + burn * 13.0,
          cr, cg, cb,
          0.17 + k * 0.24 + burn * 1.5,
        );
      }
      sr += cr * k; sg += cg * k; sb += cb * k; sk += k;
    }

    // THE SPILL.
    //
    // Five small lamps light five small patches and leave the boat sitting in a
    // hole. What is missing is the second bounce — the part of a lamp's output
    // that has already hit the hull, the water and whatever is beside it, and
    // comes back as a wash rather than as a spot. Marching that properly is
    // what the searchlight's beam does and it costs a beam march; at this scale
    // one wide, dim light standing in for the sum of them is indistinguishable
    // and costs a single slot.
    //
    // Deliberately broad and weak: it must never read as a source. It is the
    // reason the water around the boat has a colour at all when the searchlight
    // is off, and the reason a shielded boat sits in a faint blue and a hurt
    // one in a faint red without either being announced.
    if (sk > 0.001) {
      // Weak enough to be the reason the water has a colour, and not weak
      // enough to read the hull by. It was doing the second, which is why the
      // boat was fully visible with the searchlight off.
      r.addLight(
        p.x, p.y + 0.3, p.z + 0.6,
        10 + sk * 0.7,
        sr / sk, sg / sk, sb / sk,
        0.05 + Math.min(0.15, sk * 0.022),
      );
    }
  }

  /**
   * White water where the hull crosses the surface.
   *
   * `player.breach` is non-zero on exactly the frame the centre of the hull
   * changes which fluid it is in, signed so this does not have to ask twice:
   * positive is leaving, negative is arriving. Everything about the two is
   * asymmetric, which is why they are not one call with a scale factor.
   *
   * Going OUT, the boat drags a skin of water with it and most of it goes up
   * with the hull, so the burst is narrow, fast and follows. Coming IN, the
   * hull has to shove a hole in the sea and the water leaves sideways: wider,
   * slower, more of it, and thrown from the waterline rather than from the
   * boat, because by the time this runs the boat is already under it.
   */
  private splash(p: Player) {
    const v = p.breach;
    if (v === 0) return;
    const normalSpeed = Math.abs(v);
    if (normalSpeed < BREACH.minSplash) return;
    const parts = this.renderer.particles;
    const travelSpeed = Math.hypot(p.vx, p.vy);
    const tangent = Math.abs(p.vx);
    const grazing = tangent / Math.max(travelSpeed, 0.001);
    // Normal velocity opens the surface; tangential velocity drags the opening
    // into a fan. It contributes less energy, but it owns the fan's direction.
    const spraySpeed = normalSpeed + tangent * 0.28;
    // Scaled by how hard the crossing was, and capped: a nitro exit at terminal
    // is a spectacle, not a whiteout.
    const n = Math.min(52, Math.round(10 + normalSpeed * 2.7 + tangent * 0.65));
    // On the wave the hull actually crossed, not on the still level.
    const y = p.seaY;
    const impact = clamp((normalSpeed - BREACH.minSplash + tangent * 0.22) / 9, 0, 1);
    this.surfaceImpact = {
      x: p.x, y, z: p.z,
      // Backwash opposes the hull on exit; on entry the reflected vertical
      // component points back out of the water. The existing fluid field then
      // advects this vector instead of receiving the old fixed up/down splat.
      vx: -p.vx * (v > 0 ? 0.34 : 0.48),
      vy: -p.vy * (v > 0 ? 0.24 : 0.52),
      strength: 0.58 + impact * 0.42,
    };
    // THE SHEET, WHICH IS GEOMETRY. The particles below are the spray that
    // comes OFF it; they were never going to be the thing itself. See
    // `buildCrown`.
    //
    // Both directions get one and they are not the same crown. Leaving, the
    // hull drags a skin of water up with it: narrow, tall, brief. Arriving, it
    // punches a hole and the water leaves sideways: wider, lower, and it lasts
    // longer because there is more of it and it has further to fall.
    if (this.crowns.length < 4) {
      const k = Math.min(1.35, spraySpeed / 10);
      // compose() rotates local +Y toward -sin(rz), so the minus sign makes a
      // positive-X crossing lean the visible sheet toward positive X.
      const lean = clamp(
        -Math.atan2(p.vx * (v > 0 ? 0.72 : 0.46), normalSpeed + 1.5),
        -0.52, 0.52,
      );
      this.crowns.push({
        x: p.x, y, z: p.z, t: 0,
        life: v > 0 ? 0.48 + k * 0.14 : 0.64 + k * 0.18,
        r: v > 0 ? 1.55 + k * 1.35 : 2.35 + k * 2.25,
        h: v > 0 ? 2.0 + k * 1.9 : 1.25 + k * 1.45,
        out: v > 0,
        driftX: p.vx,
        lean,
        stretch: 1 + grazing * (v > 0 ? 0.32 : 0.62),
      });
    }
    this.audio.splash(spraySpeed, v > 0);
    this.camera.addShake(Math.min(
      v > 0 ? 0.16 : 0.28,
      spraySpeed * (v > 0 ? 0.016 : 0.026),
    ));
    if (v > 0) {
      // Water dragged off the deck: a tight upward veil, plus the bubbles that
      // remain below the line after the hull has left them behind.
      parts.spray(
        p.x, y, Math.round(n * 0.58), spraySpeed * 0.58 + 2.7,
        true, p.vx, p.vy,
      );
      parts.bubbles(p.x, y, Math.round(n * 0.48), 0.75, normalSpeed * 0.58 + 1.8, 0.16);
    } else {
      // A re-entry throws a broad ballistic fan to both sides. This used to
      // call the sediment emitter: brown hanging clouds with no gravity, which
      // could never read as white water however many were spawned.
      parts.spray(
        p.x, y, n, spraySpeed * 0.54 + 3.2,
        false, p.vx, p.vy,
      );
      parts.bubbles(p.x, y, Math.round(n * 0.82), 1.8, normalSpeed * 0.48 + 2.0, 0.20);
    }
  }

  /**
   * Froth where the hull is cutting the waterline.
   *
   * `splash()` handles the two INSTANTS — leaving the water and re-entering it
   * — and those are events. This is the state in between, which lasts as long
   * as the boat sits in the surface, and without it a hull half out of the sea
   * is a hull with a perfectly clean line drawn across it. The shader already
   * puts a foam collar wherever the water is thin over something, so the
   * geometry reads; what it cannot do is throw anything into the air.
   *
   * Gated on submersion rather than on depth, so it fires for a boat riding a
   * swell and not for one a metre under a flat sea, and scaled by speed,
   * because a drifting hull makes a wet line and a moving one makes spray.
   */
  private surfaceWash(p: Player, dt: number) {
    // Only while the hull is actually straddling: fully under makes bubbles
    // through the wake, fully clear makes nothing at all.
    if (p.submersion > 0.92 || p.submersion < 0.06 || p.dead) return;
    const parts = this.renderer.particles;
    const speed = Math.hypot(p.vx, p.vy);
    // How much of the hull is in the air, which is what sets the size of the
    // disturbance — a boat with its deck awash throws far less than one with
    // half its length out.
    const bite = 1 - p.submersion;
    const rate = (2.5 + speed * 2.2) * bite;
    this.washTimer -= dt;
    if (this.washTimer > 0) return;
    this.washTimer = 1 / Math.max(4, rate * 6);
    // Along the hull rather than at a point, so it is a line of froth and not a
    // fountain from the middle of the boat.
    const along = (Math.random() - 0.5) * p.chassis.length * 0.9;
    const x = p.x + Math.cos(p.yaw) * along;
    parts.bubbles(x, p.seaY, 2, 0.9, 1.4 + speed * 0.35, 0.16);
    // Spray only once there is enough speed to throw it, and it falls back —
    // which is what makes it read as water leaving the sea rather than as air
    // leaving the hull.
    if (speed > 4) {
      parts.spray(
        x, p.seaY + 0.2, 1, Math.min(7, 2.0 + speed * 0.38),
        true, p.vx, p.vy,
      );
    }
  }

  private washTimer = 0;

  private drawPlayer() {
    this.useLane(-1);
    const r = this.renderer;
    const p = this.player;
    if (p.dead) return;
    const HPd = p.chassis.hardpoints;

    // MESH PITCH IS `pitch`, NOT `pitch * facing`.
    //
    // `compose` builds R = Rz·Ry·Rx, so the 180° yaw that turns the boat around
    // is applied BEFORE the pitch: the forward vector is swung west and then
    // pitched, landing at `rz + π`. The aim, correctly, is `π − pitch*facing`.
    // Setting rz to `pitch * facing` therefore drew the hull pitched the
    // OPPOSITE way to where it was going every time it faced west — nose up
    // while diving, nose down while climbing — and the searchlight bolted to
    // that nose pointed somewhere the beam did not. Measured at 0.35 rad: the
    // drawn nose sat 1.32 m the wrong side of the axis.
    //
    // `rz = pitch` makes the two identical at both facings: east it is `pitch`
    // and the aim is `pitch`; west it is `pitch + π` and the aim is `π − (pitch
    // * −1)`, which is the same angle.
    compose(this.m, p.x, p.y, p.z, p.roll, p.yaw, p.pitch, 1);
    const flash = p.invuln > 0 ? 0.5 : 0;
    // Overdrive runs the hull hot; the aegis wraps it in a cold shell.
    const over = p.overdrive > 0 ? 1 : 0;
    const aeg = p.aegis > 0 ? 1 : 0;
    this.set(
      0.30 + flash + over * 0.3, 0.36 + flash * 0.3, 0.40 + aeg * 0.4, 1,
      0.9 + over * 3, 1.5 + aeg * 1.5, 1.7 + aeg * 4, 0.6,
    );
    // Drive throttle, read by MAT.drive in the mesh shader. It idles warm
    // rather than dark — a boat with a cold exhaust reads as dead in the water
    // — and goes past 1 under nitro, where the shader takes it white-hot.
    const heat = 0.34 + p.thrustLevel * 0.72 + this.nb('drive') * 0.9;
    // The MK II part tree is the PATHFINDER's hull, not every hull. Gating on
    // `subParts` alone drew the Pathfinder's parts over the Dray and skipped
    // the Dray's own propeller with it — the setting says which mesh the
    // Pathfinder flies, and says nothing about the other boat.
    const sub2 = this.subParts !== null && p.chassis.mesh === 'sub';
    if (sub2) {
      this.drawSub2(p, heat);
    } else {
      r.queue(p.chassis.mesh, this.m, this.tint, this.emis, undefined, [0, 0, heat, 0]);
    }

    this.drawShowcase(p);

    const a = p.aimAngle();
    // Bolted to the hull, not placed by heading. See `rotateOffset`: the two
    // agree while the boat flies straight and disagree for the sixth of a
    // second it takes to turn around, which is when the engine left the axis.
    const rr = p.roll, ry = p.yaw, rp = p.pitch;
    // The CAD hull brings its own propeller, on the pivot the rig gave it, so
    // the separately-built `prop` mesh is for every other boat.
    if (!sub2) {
      rotateOffset(this.hp, rr, ry, rp, -HPd.prop, 0, 0);
      compose(this.m, p.x + this.hp.x, p.y + this.hp.y, p.z + this.hp.z, p.propSpin, ry, rp, 1);
      this.set(0.34, 0.32, 0.30, 1, 0.2, 0.7, 0.9, 0.5);
      if (p.chassis.propMesh) r.queue(p.chassis.propMesh, this.m, this.tint, this.emis);
    }

    // THE SEARCHLIGHT, TURNED TO ITS OWN BEAM.
    //
    // `lightAngle` has always eased toward where the player is looking, but the
    // housing was part of the hull mesh and could only point where the boat
    // pointed — so the cone swept and the lamp sat still, which reads as the
    // light being detached from the object making it. Drawn as a second
    // instance about its mount, exactly as the propeller already is.
    //
    // `lightAngle` is a world angle and the mesh pitch is not, because the hull
    // yaws through 180 degrees to face west and its local pitch axis inverts
    // with it. This is the same inversion `aimAngle` undoes, run backwards.
    if (this.modelHull && !sub2 && p.chassis.mesh === 'sub') {
      // Mounted on the hull and swivelled off its axis by a LOCAL angle, so the
      // housing turns with the beam without ever coming adrift of the bow it is
      // bolted to. A world heading would jump by half a turn mid-manoeuvre.
      rotateOffset(this.hp, rr, ry, rp, LAMP_PIVOT[0], LAMP_PIVOT[1], 0);
      compose(
        this.m, p.x + this.hp.x, p.y + this.hp.y, p.z + this.hp.z,
        rr, ry, rp + p.lampSwivel, 1,
      );
      this.set(
        0.30 + flash + over * 0.3, 0.36 + flash * 0.3, 0.40 + aeg * 0.4, 1,
        0.9 + over * 3, 1.5 + aeg * 1.5, 1.7 + aeg * 4, 0.6,
      );
      r.queue('sublamp', this.m, this.tint, this.emis);
    }

    this.drawStatusLeds(p);

    if (p.lightOn) {
      r.addLight(
        p.x + Math.cos(a) * HPd.lamp, p.y + Math.sin(a) * HPd.lamp + HPd.lampDrop, p.z + 0.5,
        14, LIGHT.searchColor[0], LIGHT.searchColor[1], LIGHT.searchColor[2], 0.6,
      );
    }
    // The drive lights the water behind the boat, and it is the one warm light
    // in the game. Everything else down here — the lamp aside — is blue, so a
    // sodium-coloured wash off the stern separates the sub from the water it is
    // in and says which way it is facing even in silhouette. Under nitro it
    // still runs violet: that tell is worth more than the colour consistency.
    // IT LIGHTS THE WATER, NOT THE BOAT.
    //
    // This used to reach 25 m at 2.65 intensity from a point a couple of metres
    // off the hull, which floodlit the entire submarine every time the burner
    // lit — the boat glowed, which is not what a drive does. The glow belongs
    // in the exhaust, and it is there now: `MAT.drive` takes `heat` past 1.0
    // under nitro and the shader turns that violet and bright.
    //
    // What remains here is the spill: enough to put a coloured wash on the
    // water immediately astern and on the duct around it, and not enough to
    // reach the sail. Range roughly halved, intensity roughly a third.
    const nb = this.nb('lights');
    r.addLight(
      p.x - Math.cos(a) * HPd.drive, p.y - Math.sin(a) * HPd.drive, p.z,
      7 + nb * 6,
      1.0, 0.42 - nb * 0.22, 0.16 + nb * 0.84,
      0.30 + p.thrustLevel * 0.35 + nb * 0.55,
    );
    // The shell is drawn for the aegis field AND for a moment whenever the
    // ordinary shield takes a hit. A shield that silently eats damage is one the
    // player never sees working, and "why am I still alive" is the whole reason
    // this visual exists — so the answer flashes on the frame it happens.
    if (p.aegis > 0 || p.shieldHit > 0.01) {
      const life = p.aegis > 0 ? Math.min(1, p.aegis / 2) : 1;
      const hit = p.shieldHit;
      const beat = 0.97 + Math.sin(this.time * 2.4) * 0.025
        + (1 - life) * Math.sin(this.time * 20) * 0.045
        // The impact ripple: a quick swell that settles back.
        + hit * 0.07;
      // Rolls slowly, so the canals travel over the hull and the membrane reads
      // as a surface rather than a decal.
      compose(this.m, p.x, p.y, p.z, 0, this.time * 0.28, 0, 4.6 * beat);
      // The bell branch computes its own alpha from fresnel, so the tint is
      // colour only and the emissive is kept low — it is multiplied by
      // (0.35 + fres * 0.9), and anything brighter blows the rim to white.
      // A hit warms it toward white; a field with no aegis behind it is only
      // visible while the flash lasts.
      // WHICH defence is loudest decides the colour, so the shell is a readout
      // rather than decoration: deflector blue, ablative amber, ward violet. An
      // aegis overrides all three — it is the powerup, not a module, and it
      // should never be mistaken for one.
      const T = p.aegis > 0 ? AEGIS_TINT : p.stats.shellTint;
      this.set(
        T[0] + hit * 0.5, T[1] + hit * 0.2, T[2], 1,
        (p.aegis > 0 ? 0.05 + (1 - life) * 0.30 : 0) + hit * 0.9 * T[0],
        (p.aegis > 0 ? 0.16 : 0) + hit * 0.7 * T[1],
        (p.aegis > 0 ? 0.30 : 0) + hit * 0.8 * T[2],
        0,
      );
      r.queue('aegis', this.m, this.tint, this.emis);
    }
    if (p.aegis > 0) {
      r.addLight(p.x, p.y, p.z, 16, 0.6, 0.9, 1.0, 1.2);
    } else if (p.shieldHit > 0.02) {
      // A module flare throws its own colour into the water for a moment, which
      // is how you learn what stopped the hit without reading a number.
      const T = p.stats.shellTint;
      r.addLight(p.x, p.y, p.z, 13, T[0], T[1], T[2], p.shieldHit * 1.4);
    }

    // The bounce. Added here, before any scenery, because these are the lights
    // that change how the room reads and they must not lose the budget to a
    // field of medusae. Warmer and much less saturated than the lamp: what
    // comes back off wet rock is the rock's colour, not the lamp's.
    if (p.lightOn) {
      const S = p.stats;
      for (const b of this.bounce) {
        if (b.w < 0.05) continue;
        const k = b.w * S.lampIntensity * LIGHT.bounceGain;
        r.addLight(
          b.x, b.y, p.z,
          LIGHT.bounceRange + b.d * 0.14,
          LIGHT.bounceColor[0], LIGHT.bounceColor[1], LIGHT.bounceColor[2],
          k,
        );
      }
    }
  }

  private drawScenery(
    minX: number, maxX: number, minY: number, maxY: number,
    onScreen: (x: number, y: number, pad?: number) => boolean,
  ) {
    this.useLane(-1);
    const r = this.renderer;
    const p = this.player;
    const camZ = this.camera.eye().z;

    const wreck = this.rules.content.pois.find((q: { id: string }) => q.id === 'wreck');
    if (wreck && onScreen(wreck.x, this.wreckY, 60)) {
      compose(this.m, wreck.x, this.wreckY, -1.5, 0.05, 0.12, 0.04, 1);
      this.set(0.26, 0.27, 0.25, 1, 0.9, 0.55, 0.25, 0.9);
      r.queue('wreck', this.m, this.tint, this.emis);
      r.addLight(wreck.x + 1.5, this.wreckY + 0.4, 1.4, 22, 1.0, 0.55, 0.22, 0.8);
    }

    const c0 = this.terrain.chunkAt(minX) - 1;
    const c1 = this.terrain.chunkAt(maxX) + 1;
    const lane = Math.max(0, p.lane);
    for (let ci = c0; ci <= c1; ci++) {
      // The near lane's own set, plus a full population for the lane the sub is
      // in when that is not the near one. The thin decorative scatter the near
      // set carries for that lane is skipped below, so nothing is drawn twice.
      const sets = lane === 0
        ? [propsForChunk(ci)]
        : [propsForChunk(ci), propsForChunk(ci, lane)];
      for (const set of sets) for (const prop of set) {
        if (lane !== 0 && prop.layer === lane && set === sets[0]) continue;
        // A prop on a layer the camera has passed goes with its ground. Kept in
        // step with the terrain by asking the same function, so weed can never
        // be left hanging in front of a slab that is no longer drawn.
        const vis = layerVisibility(camZ, LAYERS[Math.min(prop.layer, LAYERS.length - 1)]!.zFront);
        if (vis <= 0) continue;
        // Parallax props sit further from the camera, so a wider slice of world
        // space projects into the frame; cull them with a looser box.
        const bg = prop.layer > 0;
        const mx = bg ? 90 : 30;
        const my = bg ? 140 : 40;
        if (prop.x < minX - mx || prop.x > maxX + mx) continue;
        if (prop.y < minY - my || prop.y > maxY + my) continue;
        // FOREGROUND WEED IS FRAMING, AND FRAMING WITH A VISIBLE ROOT IS A BUG.
        //
        // These are planted 6 to 16 m BELOW the seabed so the base is off the
        // bottom of the frame and never has to agree with ground the camera can
        // also see. That reasoning holds while the camera is near the seabed.
        // It fails the moment the player is well under it — in a cave a hundred
        // and fifty metres down, the root is now ABOVE the lens, and parallax
        // magnifies it away from the centre rather than towards it, so the weed
        // arrives high in frame with its stump showing: grass floating in open
        // water.
        //
        // So the test is not where the prop is, it is where it LANDS. A thing
        // at z sits `camZ / (camZ - z)` further from the centre of the view than
        // its world position, and this one has earned its place only if that
        // puts its base off the bottom.
        if (prop.kind === 'fgkelp' || prop.kind === 'fgrock') {
          const eye = this.camera.eye();
          const lift = eye.z / Math.max(1, eye.z - prop.z);
          if (eye.y + (prop.y - eye.y) * lift > minY) continue;
        }
        this.tint[0] = prop.tint[0]; this.tint[1] = prop.tint[1];
        this.tint[2] = prop.tint[2];
        // How much this prop belongs to the PLAY layer — the channel depth of
        // field reads. 1 for anything the player can run into, less the further
        // back the parallax puts it.
        this.tint[3] = 1 - Math.max(layerDefocus(prop.layer), 1 - vis);
        this.emis[0] = 0; this.emis[1] = 0; this.emis[2] = 0; this.emis[3] = 0.2;

        switch (prop.kind) {
          case 'kelp': {
            // Only the play layer reacts to the sub passing through it.
            const d = bg ? 1e9 : hyp(prop.x - p.x, prop.y - p.y);
            const push = d < 12 ? ((prop.x - p.x) / (d || 1)) * (1 - d / 12) * 0.75 : 0;
            const sway = Math.sin(this.time * 0.9 + prop.phase) * 0.16 +
                         Math.sin(this.time * 2.3 + prop.phase * 1.7) * 0.05;
            compose(
              this.m, prop.x, prop.y, prop.z, 0, 0, prop.rot + sway - push,
              prop.scale * 0.5, prop.scale, prop.scale * 0.5,
            );
            // Parallax growth is a silhouette by the time the water is done
            // with it, and there are more blades back there than in front.
            r.queue(bg ? 'kelplo' : 'kelp', this.m, this.tint, this.emis);
            break;
          }
          case 'coral': {
            // AND IT GLOWS. The colour was already there and was being lit
            // rather than emitting, so a stand of it went dark with the rest of
            // the water and the one thing in the zone carrying any hue was
            // invisible without the lamp on it. `bio` is rim-weighted in the
            // shader, which on a branching form lights the edges and leaves the
            // mass of it dark — the way a bioluminescent thing actually reads,
            // and the reason it does not simply look like a lamp painted coral.
            const glow = 0.62 + 0.38 * Math.sin(this.time * 0.8 + prop.phase);
            this.emis[0] = prop.tint[0] * glow;
            this.emis[1] = prop.tint[1] * glow;
            this.emis[2] = prop.tint[2] * glow;
            compose(this.m, prop.x, prop.y, prop.z, 0, prop.rot, 0, prop.scale);
            // Same rule rock already uses, at the same threshold. The median
            // head is scale 1.65, so anything much lower than this catches
            // almost nothing — 1.1 would have taken 22 percent of them. This
            // takes 64, and leaves full detail on every head above 1.8, which
            // is where the mound's centre and everything worth looking at is.
            r.queue(bg || prop.scale < 1.8 ? 'corallo' : 'coral',
              this.m, this.tint, this.emis, World.CORAL_BIO);
            break;
          }
          case 'rock':
            compose(this.m, prop.x, prop.y, prop.z, prop.rot * 0.3, prop.rot, prop.rot * 0.2, prop.scale);
            // Full density only for play-layer rock big enough to read as shape.
            // A parallax boulder is a silhouette by the time the water is done
            // with it, and a two-metre pebble never had 768 triangles of shape
            // to show in the first place.
            r.queue(bg || prop.scale < 2 ? 'rocklo' : 'rock', this.m, this.tint, this.emis);
            break;
          case 'mineral': {
            // MINED OUT MEANS GONE. Every cluster of the group carries the
            // site, so breaking the one that was shot takes the whole deposit
            // with it rather than leaving ten crystals round a hole.
            const mkey = this.oreKey(prop, Math.max(0, prop.layer));
            if (this.minedOre.has(mkey)) break;
            // WORKING A SEAM SHRINKS IT. Without this the only feedback that
            // anything is happening is a bar on the HUD, and the thing the
            // player is looking at is the rock.
            const mine = this.mining;
            const worked = mine && mine.key === mkey && mine.total > 0
              ? mine.taken / mine.total : 0;
            // ORE. The one prop whose colour is not authored per instance in
            // props.ts — it comes from the manifest the art shipped with, so
            // re-exporting the set moves the game's ore without a code change.
            // A background deposit is a silhouette by the time the water has
            // finished with it, and its light would be spent on geometry the
            // player can neither reach nor see lit.
            // Down to 45% as the last shard comes out, not to nothing: a
            // seam that vanishes gradually reads as one being consumed, while
            // one that shrinks to a point reads as a bug.
            this.drawOre(prop.variant, 'node', prop.x, prop.y, prop.z,
                         prop.rot, prop.scale * (1 - worked * 0.55),
                         bg ? 0 : (prop.deposit ?? 0));
            break;
          }
          case 'fgrock':
            // Near-black by design, so it is a silhouette and nothing else.
            this.emis[3] = 0;
            compose(this.m, prop.x, prop.y, prop.z, prop.rot * 0.4, prop.rot, prop.rot * 0.3, prop.scale);
            r.queue('rocklo', this.m, this.tint, this.emis);
            break;
          case 'fgkelp': {
            // Slower and wider than the play-layer sway. Near the lens a small
            // angular movement covers a lot of screen, so the same rate that
            // reads as a current on the seabed reads as a flap up here.
            this.emis[3] = 0;
            const sway = Math.sin(this.time * 0.52 + prop.phase) * 0.1 +
                         Math.sin(this.time * 1.1 + prop.phase * 1.7) * 0.035;
            compose(
              this.m, prop.x, prop.y, prop.z, 0, 0, prop.rot + sway,
              prop.scale * 0.5, prop.scale, prop.scale * 0.5,
            );
            // Unlit and near-black by design, so detail on it is unreachable.
            r.queue('kelplo', this.m, this.tint, this.emis);
            break;
          }
          case 'ruin':
            compose(this.m, prop.x, prop.y, prop.z, 0, prop.rot, prop.phase * 0.04, prop.scale);
            r.queue(`ruin${prop.variant % 3}`, this.m, this.tint, this.emis);
            break;
          case 'debris':
            compose(this.m, prop.x, prop.y, prop.z, prop.rot * 0.7, prop.rot, prop.rot * 0.5, prop.scale);
            r.queue(`debris${prop.variant % 3}`, this.m, this.tint, this.emis);
            break;
          case 'monolith':
            this.emis[0] = 0.06; this.emis[1] = 0.22; this.emis[2] = 0.3; this.emis[3] = 0.4;
            compose(this.m, prop.x, prop.y, prop.z, 0, prop.rot, 0, prop.scale);
            r.queue('monolith', this.m, this.tint, this.emis);
            if (!bg && this.propLights < 16) {
              this.propLights++;
              r.addLight(prop.x, prop.y + prop.scale * 6, prop.z, 26, 0.3, 0.9, 1.0, 0.5);
            }
            break;
          case 'vent': {
            // Black smokers glow at the throat and breathe a plume of hot water.
            const heat = 0.55 + 0.45 * Math.sin(this.time * 1.3 + prop.phase);
            this.emis[0] = 2.2 * heat; this.emis[1] = 0.5 * heat; this.emis[2] = 0.12 * heat;
            this.emis[3] = 0.5;
            compose(this.m, prop.x, prop.y, prop.z, 0, prop.rot, 0, prop.scale);
            r.queue('vent', this.m, this.tint, this.emis);
            if (!bg) {
              if (this.propLights < 18) {
                this.propLights++;
                r.addLight(prop.x, prop.y + prop.scale * 7, prop.z, 30, 1.0, 0.36, 0.1, 0.6 + heat * 0.7);
              }
              if (Math.random() < 0.35) {
                this.renderer.particles.sediment(prop.x, prop.y + prop.scale * 7.5, 1, 1.1);
              }
            }
            break;
          }
          case 'jelly': {
            // BEHIND THE PLAYER'S LANE, ALWAYS, and this is a rule about what
            // the background IS rather than a placement tweak.
            //
            // A scenery jellyfish is not an entity. It has no hit box, no
            // health and nothing to lock onto — and when one is drifting at
            // the player's own z it looks exactly like something that should
            // have all three. The complaint was "I cannot target this jelly
            // even though it is clearly on my lane", and the honest answer is
            // that it was never on any lane; it was decoration standing in the
            // playfield.
            //
            // Clamped rather than re-placed, so the field keeps its authored
            // spread and only the ones that would intrude move. Against the
            // BOAT's eased z rather than its lane index: `subZ` glides across
            // the gap when you change lane, so the ones that have to give way
            // swing back smoothly instead of snapping the moment `laneAtZ`
            // flips. Which is also the behaviour you asked for — travel to a
            // background lane and the animals that were in front of it settle
            // in behind.
            const jz = Math.min(prop.z, p.z - World.JELLY_BEHIND);
            // The swim cycle lives in the shader; the CPU only supplies the
            // drift and the phase, so a field of them costs one draw call.
            const rate = 1.15 + (prop.phase % 1) * 0.5;
            const beat = Math.sin(this.time * rate + prop.phase);
            const drift = Math.sin(this.time * 0.19 + prop.phase) * 7;
            // Medusae climb on the contraction and sink between strokes.
            const rise = Math.sin(this.time * 0.27 + prop.phase * 1.3) * 4 + beat * 0.9;
            this.emis[0] = prop.tint[0] * 1.5;
            this.emis[1] = prop.tint[1] * 1.5;
            this.emis[2] = prop.tint[2] * 1.5;
            this.emis[3] = 0;
            compose(
              this.m, prop.x + drift, prop.y + rise, jz,
              Math.sin(this.time * 0.31 + prop.phase) * 0.16,
              Math.sin(this.time * 0.13 + prop.phase) * 0.5,
              Math.sin(this.time * 0.23 + prop.phase) * 0.2,
              prop.scale,
            );
            r.queue(`jelly${prop.variant % JELLY_SPECIES}`, this.m, this.tint, this.emis, [prop.phase, rate, prop.scale * 0.2, 0]);
            // A BACKGROUND ONE LIGHTS ITS OWN CORNER, and gets its own
            // allowance to do it with.
            //
            // Reaching further and burning softer than a near one: it is
            // lighting a wall tens of metres behind the player rather than the
            // water beside them, and what makes a distant glow read as distant
            // is the SIZE of the pool it casts, not its brightness. A tight
            // bright point at that range is a pixel; a wide dim wash is a place.
            //
            // The separate counter matters. On one budget the near lane fills
            // it first — it is drawn first and there are more of them — so the
            // background would get lights only on the frames nothing else
            // wanted any, which is a flicker rather than a feature.
            // ITS OWN COUNTER, AND THIS IS WHY.
            //
            // `propLights` is shared, and the caps on it are staggered: coral
            // stops at 16, vents at 18, and the jellyfish — the one animal in
            // the game whose entire character is that it glows — stopped at 12.
            // Lowest cap, and it is not first in the switch. In a zone built
            // out of vents the counter is past 18 before a jellyfish is ever
            // considered, so every one of them got NO LIGHT AT ALL, and the
            // water around them had nothing to scatter. Which is precisely how
            // it read: inscatter that worked on the boat's LEDs and on the
            // stations and never once on the animals.
            //
            // The comment below already argued this case for the background
            // and won it. The same argument applies here and was not made.
            if (bg ? this.bgLights < 6 : this.jellyLights < 12) {
              if (bg) this.bgLights++; else this.jellyLights++;
              r.addLight(
                // `jz`, not `prop.z` — the light has to travel with the animal
                // or a jelly pushed behind the lane leaves its own glow
                // hanging in the water where it used to be.
                prop.x + drift, prop.y + rise, jz,
                (bg ? 34 : 13) * prop.scale,
                prop.tint[0], prop.tint[1], prop.tint[2],
                (bg ? 0.30 : 0.45) + Math.max(0, beat) * (bg ? 0.22 : 0.35),
                true,
              );
            }
            break;
          }
        }
      }
    }
  }

  private drawBait(onScreen: (x: number, y: number, pad?: number) => boolean) {
    this.useLane(-1);
    const r = this.renderer;
    for (const ball of this.baitBalls) {
      if (!ball.live) continue;
      // Reject the whole ball before touching its fish. `live` is a 175 m
      // radius, which is several screens wide — without this we run an
      // on-screen test against eight hundred fish that are all off it.
      const sp = ball.spec;
      if (!onScreen(sp.x, sp.y, sp.radius + sp.range + sp.height + 40)) continue;
      // Silver, not tinted. A bait ball's whole effect is a thousand identical
      // flanks flashing together; giving it a hue turns a shoal into confetti.
      const sh = ball.spec.sheen;
      const gl = ball.spec.glow;
      // How luminescent this shoal is. The authored `glow` is the main term,
      // but depth earns some on its own: below the light a shoal has no other
      // way of being seen, and lighting it from the sub would be a lie — down
      // there the animal is the light source. That is the jellyfish reading.
      const dark = clamp((-sp.y - 240) / 460, 0, 1);
      const lum = Math.min(1, gl + dark * 0.6);
      for (const f of ball.fish) {
        if (!onScreen(f.x, f.y, 12)) continue;
        const spi = f.species;
        const fx = FISH_FLEX[spi];
        const vary = 0.86 + ((f.phase * 0.37) % 1) * 0.3;
        const lantern = spi === 1 ? 1 : 0;
        // Lanterns carry the light organs so they burn hardest, but in the deep
        // everything glows a little: one unlit fish among lit ones reads as a
        // hole punched in the shoal, not as a fish.
        const bio = lum * (0.35 + lantern * 0.65);
        this.set(
          0.455 * vary * sh, 0.475 * vary * sh, 0.470 * vary * sh, 1,
          // Bioluminescence is cyan-green nearly everywhere it occurs in the
          // ocean, which is convenient: that is also the only part of the
          // spectrum that survives any distance through water, so the accurate
          // colour is the one that stays visible.
          //
          // Still driven past what the depth justifies — a fish covering four
          // pixels at a plausible brightness averages out to nothing — but no
          // longer by the factor it was. These numbers were set when the fog
          // charged the play plane for the camera's whole 68 m standoff, so
          // roughly half of this was paying for absorption that is no longer
          // applied; left alone the deep shoals blew out to white blobs.
          0.05 + bio * 0.44, 0.16 + bio * 1.58, 0.22 + bio * 1.38,
          // Hard spec: the flash off a turning flank is the whole point.
          1.15,
        );
        const local = f.facing > 0 ? f.aim : Math.PI - f.aim;
        compose(
          this.m, f.x, f.y, f.z,
          f.bank * f.facing, f.facing > 0 ? 0 : Math.PI, local * f.facing, f.size,
        );
        // Level of detail, chosen by body size rather than by distance: the
        // camera sits a fixed ~68 m off the play plane, so a shoal fish never
        // gets nearer however hard the player flies at it — driving through a
        // bait ball parts it sideways, it does not bring one up to the lens.
        // Size is therefore the only thing that decides how many pixels a fish
        // covers, and seven in eight of them are the small kind.
        r.queue(
          f.size > 0.55 ? `fish${spi}` : `fish${spi}lo`,
          this.m, this.tint, this.emis,
          [f.phase, f.rate, fx[0], fx[1]],
          [f.curve, bio, 0, 0],
        );
        if (lantern && lum > 0.5 && this.propLights < 22) {
          this.propLights++;
          r.addLight(f.x, f.y, f.z, 11, 0.35, 0.85, 0.8, 0.28 * lum);
        }
      }
    }
  }

  /**
   * The authored habitat bases (docs/HABITAT.md §3, Phase 0).
   *
   * Set dressing: they do not collide (§2.7 — collision is analytic against the
   * density field and there is no mesh collision anywhere in this game), they
   * carry no dynamic lights of their own beyond one per base, and they add no
   * systems. Colour is per-instance tint, which is why §6.5's role split costs
   * nothing: the same meshes tinted differently give each base its own identity.
   */
  /**
   * Debug: fly the boat to the next authored habitat.
   *
   * §10's success test is a question about pixels — "can you tell what kind of
   * module it is from the silhouette alone, at 63 px?" — and the only way to
   * answer it is at gameplay framing, in the water, with the lamp on. Walking
   * there takes minutes; this takes a keystroke, and it lives behind F3 with
   * the rest of the tuning.
   */
  gotoHabitat(step = 1): string {
    const n = TEST_BASES.length;
    this.habitatIdx = ((this.habitatIdx + step) % n + n) % n;
    const b = TEST_BASES[this.habitatIdx]!;
    const p = this.player;
    // Park under and to the west, looking along the base: the composition is a
    // side elevation read left to right, so arriving nose-on to its middle
    // shows the least of it.
    p.x = b.x - 6;
    p.y = (this.habitatY.get(b.id) ?? 0) + 1.5;
    p.vx = 0; p.vy = 0;
    return b.name;
  }

  private drawHabitats(onScreen: (x: number, y: number, pad?: number) => boolean) {
    this.useLane(-1);
    const r = this.renderer;
    for (const b of TEST_BASES) {
      const by = this.habitatY.get(b.id) ?? 0;
      // One cheap reject for the whole base before touching its parts. Sized
      // from the base's REAL extent: the stage-5 complex is 72 m long, so a
      // fixed +24 m centre guess rejected it while its far end was on screen.
      const ext = this.habitatExt.get(b.id) ?? 40;
      if (!onScreen(b.x + ext, by, ext + 30)) continue;
      // NO PER-PART CULLING, and that is what fixes the swimming glass.
      //
      // `prevModel` is carried by the renderer per slot per INDEX — "World draws
      // its pools in a fixed order, so index and entity line up for as long as
      // the live set holds". Culling parts individually changed the live set
      // every time the camera moved, so indices shifted, instances inherited
      // another module's history, and the motion vectors were wrong. TAA then
      // resolved that as geometry sliding up and down — worst on the glazing,
      // which is translucent and has the least to hide behind.
      //
      // The whole-base reject above already does the useful work; a base is at
      // most 28 parts and drawing all of them keeps the order fixed.
      for (const part of b.parts) {
        const x = b.x + part.x, y = by + part.y;
        compose(this.m, x, y, -1, 0, 0, part.rot ?? 0, 1, part.sy ?? 1, 1);
        const t = part.tint ?? [0.3, 0.32, 0.34, 1];
        const e = part.emis ?? [0.2, 0.9, 1.1, 1];
        this.set(t[0], t[1], t[2], t[3], e[0], e[1], e[2], e[3]);
        // params2.w STAYS 0, and that is a decision rather than an oversight.
        //
        // Setting it to 1 is the boat's switch, and it is a BUNDLE: per-vertex
        // albedo and AO, yes, but also `onHull`, which turns on fsMesh's
        // procedural rivet generator — 46 rows by 34 columns, cylindrical around
        // the hull axis, sized for a 7.2 m submarine. On a 16 m module that came
        // out as a speckle of dirt over every surface. There is no way to take
        // the colour without the rivets from this side of the shader.
        /**
         * AND THE BARE SLOT IS SHADED AS WHATEVER IT HOLDS.
         *
         * On a metal-only module — legs, tubes, junctions — the bare name IS
         * the stainless, so handing it `part.pbr` would shade a machined
         * collar as lacquered paint at metallic 0.08. Same override the
         * `_metal` branch below uses.
         */
        if (this.habPrimary.get(part.mesh) === 'metal') {
          this.set(0.62, 0.66, 0.72, 1, 0, 0, 0, 0.45);
          r.queue(part.mesh, this.m, this.tint, this.emis,
                  undefined, undefined, HAB_STEEL);
          this.set(t[0], t[1], t[2], t[3], e[0], e[1], e[2], e[3]);
        } else {
          // 0.40, not the 1.0 a `pbr` override used to force. The CAD hulls
          // are large and smoothly curved where the procedural boxes were
          // faceted, so they catch far more of the searchlight; at full
          // specular the highlight saturates and bloom turns the module into a
          // white blob. See `specAmtV`.
          this.set(t[0], t[1], t[2], t[3], e[0], e[1], e[2], 0.40);
          r.queue(part.mesh, this.m, this.tint, this.emis, undefined, undefined, part.pbr);
          this.set(t[0], t[1], t[2], t[3], e[0], e[1], e[2], e[3]);
        }
        // WARM WHITE #FFDCC2 behind the glass, against the body's cyan. Two
        // instances at one transform, which is how the boat already does
        // `sub` + `sublamp` (§2.3).
        for (const g of this.habExtra.get(part.mesh) ?? []) {
          if (g === 'room') {
            // The procedural fallback's stand-in interior. Full warmth: it is
            // what you see through the window rather than a hint behind a pane.
            this.set(0.62, 0.52, 0.42, 1, 1.15, 0.78, 0.5, 1);
            r.queue(`${part.mesh}_room`, this.m, this.tint, this.emis);
          } else if (g === 'glass') {
            // The pane, barely emitting. `fsGlass` scales what is left by
            // coverage, so this is the faint bloom ON the glass and not the
            // room's light — that arrives from behind now, through it.
            this.set(0.5, 0.44, 0.36, 1, 0.16, 0.12, 0.09, 1);
            r.queue(`${part.mesh}_glass`, this.m, this.tint, this.emis);
          } else if (g === 'metal') {
            /**
             * STAINLESS, AND THIS IS THE POINT OF SPLITTING THE CAD BY
             * MATERIAL.
             *
             * The frames, collars, struts, decks and ladders were in the same
             * slot as the painted hull, sharing its `PBR_HULL` override — which
             * is metallic 0.08. Every metal surface on the base was therefore a
             * dielectric wearing a grey tint, and no amount of probe resolution
             * or reflection work could make it read as steel, because nothing
             * had told the shader it was steel.
             *
             * Metallic 1 kills the diffuse outright and hands the surface to
             * the conductor lobe, where the split-sum DFG and the energy
             * compensation actually mean something. 0.30 roughness is brushed
             * stainless: tight enough to return a legible reflection, rough
             * enough that `iblBrushed` has something to smear.
             */
            this.set(0.62, 0.66, 0.72, 1, 0, 0, 0, 0.55);
            r.queue(`${part.mesh}_metal`, this.m, this.tint, this.emis,
                    undefined, undefined, HAB_STEEL);
          } else if (g === 'lamp') {
            /**
             * THE CEILING FIXTURE. The brightest thing in the cabin and the
             * only thing in there that is meant to read as a light.
             *
             * Emissive rather than a point light, and not for want of trying
             * the other way: this engine's point lights cast no shadows, so a
             * lamp inside a sealed hull would light the OUTSIDE of the hull it
             * is inside, and eight modules would cost eight of the 48 slots to
             * do it. A lit room seen through a window is a set of emissive
             * surfaces with a transmissive pane in front, which is what this is.
             */
            // Raised now that `wCabin` has taken the blue ambient away: this
            // is the only light in the room rather than a warm tint fighting a
            // dome. See `wCabin` in scene.wgsl.
            // Same reasoning as the strips: 2.10 / 1.42 / 0.86 is luminance
            // 1.52, so 44 percent of the ceiling bloomed and washed the cabin
            // it is supposed to be lighting. 1.05 keeps it the brightest thing
            // in the room without haloing through the glass.
            this.set(0.70, 0.60, 0.48, 1, 1.45, 0.98, 0.59, 1);
            r.queue(`${part.mesh}_lamp`, this.m, this.tint, this.emis);
          } else if (g === 'cabin') {
            /**
             * AND THE ROOM THE FIXTURE LIGHTS, at a fifth of it.
             *
             * These were the same value as the ceiling, and because emissive
             * surfaces take no shading whatsoever, that made the cabin one flat
             * tone — `int_core_shell` alone is 13.6 m3 of inner wall, so the
             * core's window came out as a solid cream slab with no depth in it
             * at all, and two modules pushed past the bloom threshold into
             * white.
             *
             * A fifth is roughly what a matt surface returns of the light
             * falling on it, so this is not only dimmer, it is the right KIND
             * of dimmer: the deck and bulkheads now read as things being lit
             * rather than as things emitting.
             */
            // A fifth of the fixture, which is roughly what a matt surface
            // returns of the light falling on it — so the deck and bulkheads
            // read as lit rather than as emitting.
            this.set(0.62, 0.54, 0.45, 1, 0.44, 0.29, 0.18, 1);
            r.queue(`${part.mesh}_cabin`, this.m, this.tint, this.emis);
          } else if (g === 'glow') {
            // The CAD's strip lights, which are a separate Fusion appearance
            // from its glass — the distinction the engine never had. These are
            // the only surfaces on a module that should actually emit.
            /**
             * JUST OVER THE BLOOM THRESHOLD, NOT THREE TIMES IT.
             *
             * At 1.30 / 2.30 / 2.60 a strip has luminance 2.11 against a
             * threshold of 0.85, so SIXTY PERCENT of it is fed to the bloom
             * chain — which is why a thin line of light came out as a glowing
             * blob that survived cutting the searchlight to a hundredth. A
             * light source should read as a bright line with a halo, not as a
             * cloud with a line somewhere inside it.
             *
             * 1.02 puts about a sixth into bloom: still unmistakably a lamp,
             * still the brightest thing on the hull, no longer a smear.
             */
            this.set(0.30, 0.62, 0.72, 1, 0.63, 1.11, 1.26, 1);
            r.queue(`${part.mesh}_glow`, this.m, this.tint, this.emis);
          }
        }
      }
      // ONE light, and it sits BELOW and OUTSIDE the hull.
      //
      // The first version put two lights (r 26 and r 42) at the centre of a
      // 37 m row of solid modules — i.e. INSIDE the geometry. Every surface
      // then faced a light at much the same intensity, which is exactly how you
      // erase form: uniform illumination is flatness, and no amount of extra
      // geometry survives it. A light under the base grazes the undersides and
      // leaves the flanks to the lamp, the sun and the emissive strips, which is
      // what gives the modules a light side and a dark side at all.
      //
      // §2.7: lights are capped at 48 and dropped silently past that, and an ore
      // deposit of thirteen clusters deliberately shares ONE.
      r.addLight(b.x + 23, by - 5.5, 3, 30, 0.5, 0.85, 1.0, 0.55);
    }
  }

  private drawStations(onScreen: (x: number, y: number, pad?: number) => boolean) {
    this.useLane(-1);
    const r = this.renderer;
    for (const s of this.actors.stations) {
      if (!onScreen(s.x, s.y, 70)) continue;
      const pulse = 0.5 + 0.5 * Math.sin(s.phase * 1.7);
      // Dormant stations are cold and dim; a docked one is unmistakable.
      const live = s.active ? 1 : 0.35;
      const warm = s.near;
      compose(this.m, s.x, s.y, laneZ(s.lane) - 1, 0, 0, 0, 1);
      this.set(
        0.26, 0.30, 0.32, 1,
        (0.3 + warm * 1.2) * live, (1.8 + pulse * 1.2 + warm * 2) * live,
        (2.2 + pulse * 1.4 + warm * 2) * live, 0.9,
      );
      r.queue('station', this.m, this.tint, this.emis);
      r.addLight(
        s.x, s.y + 4.5, 1, 34 + warm * 14,
        0.35, 0.95, 1.0, (0.7 + pulse * 0.5 + warm * 1.4) * live,
      );
    }
  }

  /**
   * How many enemies may light the water at once. Scenery has been budgeted
   * since the parallax work — 16 anemones, 18 vents, 12 wrecks, 22 lanterns —
   * and enemies never were: every mechanical on screen added a light
   * unconditionally.
   *
   * In the campaign that costs nothing, because 411 enemies spread over 7.9 km
   * put four or five in frame at a time. Survival puts its entire population
   * inside 260 m *by design*, so the identical code path lit 22 at once — and
   * `points()` in scene.wgsl is the loop every shaded fragment in the frame
   * runs, whose cheap out-of-range reject stops rejecting precisely when the
   * lights are bunched around the player. A mode that is fill-bound on a phone
   * was paying for the campaign's assumption about spacing.
   */
  private static readonly ACTOR_LIGHTS = 12;

  /**
   * How many canisters may light the water at once. Same reasoning as
   * `ACTOR_LIGHTS`, and the same measurement behind it — see the call site.
   */
  private static readonly PICKUP_LIGHTS = 6;
  private litPickupCutoff = Infinity;
  private pickupDists: number[] = [];

  private budgetPickupLights(onScreen: (x: number, y: number, pad?: number) => boolean) {
    const d = this.pickupDists;
    d.length = 0;
    const cx = this.camera.viewX(), cy = this.camera.viewY();
    for (const pk of this.actors.pickups) {
      if (!pk.alive || pk.mass > 0 || !pk.revealed || !onScreen(pk.x, pk.y)) continue;
      const dx = pk.x - cx, dy = pk.y - cy;
      d.push(dx * dx + dy * dy);
    }
    if (d.length <= World.PICKUP_LIGHTS) { this.litPickupCutoff = Infinity; return; }
    d.sort((a, b) => a - b);
    this.litPickupCutoff = d[World.PICKUP_LIGHTS - 1];
  }
  /** Squared camera distance beyond which an enemy is drawn but does not light. */
  private litCutoff = Infinity;
  private readonly litDists: number[] = [];

  /**
   * Nearest-first, so the cutoff is stable frame to frame and what goes dark is
   * always the furthest thing away rather than whichever enemy happens to sit
   * late in the pool. Bosses are exempt: they are the reason you are looking.
   */
  private budgetActorLights(onScreen: (x: number, y: number, pad?: number) => boolean) {
    const d = this.litDists;
    d.length = 0;
    const cx = this.camera.x, cy = this.camera.y;
    for (const e of this.actors.enemies) {
      if (!e.alive || e.boss || !onScreen(e.x, e.y, 30)) continue;
      const dx = e.x - cx, dy = e.y - cy;
      d.push(dx * dx + dy * dy);
    }
    if (d.length <= World.ACTOR_LIGHTS) { this.litCutoff = Infinity; return; }
    d.sort((a, b) => a - b);
    this.litCutoff = d[World.ACTOR_LIGHTS - 1];
  }

  private drawActors(onScreen: (x: number, y: number, pad?: number) => boolean) {
    const r = this.renderer;
    this.budgetActorLights(onScreen);
    const cx = this.camera.x, cy = this.camera.y;

    const camZ = this.camera.eye().z;
    for (const e of this.actors.enemies) {
      if (!e.alive || !onScreen(e.x, e.y, e.boss ? 140 : 30)) continue;
      // Drawn in its own lane, and gone with that lane's ground when the camera
      // has flown past it — otherwise a creature hangs in open water in front
      // of a slab that is no longer being drawn.
      const eL = LAYERS[Math.max(0, Math.min(LAYERS.length - 1, e.lane))]!;
      if (layerVisibility(camZ, eL.zFront) <= 0) continue;
      // AND EVERY DRAW BELOW USES IT. Five of the ten species did; `bloom`,
      // `jelly`, `swarmer` and `lurker` composed at a hard 0 and the
      // dreadnought at a hard -2, so a creature belonging to the second lane
      // was drawn at the player's own depth — full size, no parallax, and
      // co-planar with the boat in the depth buffer.
      //
      // They were tinted for their lane and positioned for ours, which is the
      // worst of both: dim enough to look wrong, near enough to look reachable.
      // Same bug as the loot loop had, in five more places.
      // A LANE IS A SLAB, NOT A PANE OF GLASS.
      //
      // Every actor in a lane was drawn at exactly that lane's z, so the whole
      // population of the player's own water was coplanar with the boat. A
      // medusa never passed in FRONT of the hull, never occluded it, never
      // slid across the lamp — it could only ever sit beside it. The only
      // things in the frame with any depth to them were the decorative props
      // behind, which is why the water reads as scenery with the player pinned
      // flat in the middle of it however many animals are actually in the lane.
      //
      // Lane 0's slab runs z 3.2 to -16, and the boat sits at 0 in the middle
      // of it. Drifting fauna gets a stable spread inside that, from three
      // metres in front of the boat to twelve behind. Visual only: contact,
      // aim and every hit test are x, y and lane, and none of them read this.
      //
      // Stable per animal, from `phase`, which is seeded once at spawn — a
      // value recomputed per frame would swim the creature through the depth
      // buffer, which is the same mistake `rate` was fixed for.
      const drifter = e.type === 'jelly' || e.type === 'bloom';
      const eZ = laneZ(e.lane)
        + (drifter ? -12 + (e.phase / 6.2831853) * 15 : 0);
      this.useLane(e.lane);
      const flash = e.hitFlash;
      // Read once per enemy; every `addLight` below this line is guarded by it.
      const lit = e.boss
        || (e.x - cx) * (e.x - cx) + (e.y - cy) * (e.y - cy) <= this.litCutoff;

      switch (e.type) {
        case 'bloom': {
          const at = this.actors.time * e.rate + e.phase;
          const beat = Math.sin(at);
          const hot = e.alert * 0.6 + flash;
          // Tint only shifts the iridescence; the ramp itself lives in the shader.
          this.set(
            0.95 + hot * 0.5, 0.62 - hot * 0.25, 0.85 - hot * 0.2, 1,
            (0.5 + hot * 1.2), 0.35 + hot * 0.2, 0.8, 0,
          );
          compose(
            this.m, e.x, e.y, eZ,
            Math.sin(at * 0.23) * 0.2,
            e.facing > 0 ? 0 : Math.PI,
            Math.sin(at * 0.17) * 0.28,
            e.size,
          );
          r.queue(`bloom${e.variant % BLOOM_SPECIES}`, this.m, this.tint, this.emis,
            [e.phase, e.rate, e.size * 0.15, 0]);
          if (lit) {
            // A luminous membrane, not a pink volume light: retain a local
            // pulse around the ribs without building a fog ball larger than
            // the whole animal.
            r.addLight(e.x, e.y, eZ, 13 * e.size, 1.0, 0.45, 0.8,
              0.16 + Math.max(0, beat) * 0.24, true);
          }
          break;
        }

        case 'jelly': {
          // Both terms are fixed at spawn, so the swim cycle advances smoothly
          // instead of being re-timed every frame.
          const beat = Math.sin(this.actors.time * e.rate + e.phase);
          // Stung red when the sub is close enough to be in danger.
          const hot = e.alert * 0.5 + flash;
          this.set(
            0.25 + hot * 0.8, 0.72 - hot * 0.3, 1.0 - hot * 0.4, 1,
            (0.35 + hot * 1.6) * 1.4, (1.05 - hot * 0.4) * 1.4, 1.5 * 1.4, 0,
          );
          const at = this.actors.time + e.phase;
          compose(
            this.m, e.x, e.y, eZ,
            Math.sin(at * 0.31) * 0.16,
            Math.sin(at * 0.13) * 0.5,
            Math.sin(at * 0.23) * 0.2,
            e.size,
          );
          r.queue(`jelly${e.variant % JELLY_SPECIES}`, this.m, this.tint, this.emis,
            [e.phase, e.rate, e.size * 0.2, 0]);
          if (lit) {
            r.addLight(e.x, e.y, eZ, 13 * e.size, 0.3, 0.75, 1.0,
              0.45 + Math.max(0, beat) * 0.35, true);
          }
          break;
        }

        case 'mine': {
          compose(this.m, e.x, e.y, eZ, e.spin * 0.3, e.spin, e.spin * 0.2, 1);
          // Idle mines blink slowly so they are spottable; arming ramps it hard.
          const blink = 0.35 + 0.65 * Math.pow(Math.max(0, Math.sin(this.time * 1.6 + e.phase)), 6);
          const arm = Math.max(e.alert, 0);
          this.set(0.22 + flash, 0.21 + flash * 0.4, 0.20, 1, 1.4 * blink + arm * 9, 0.22 + arm * 0.7, 0.16, 0.8);
          r.queue('mine', this.m, this.tint, this.emis);
          if (lit) r.addLight(e.x, e.y, 1.4, 12, 1, 0.22, 0.14, 0.25 * blink + arm * 1.6);
          break;
        }

        case 'seeker': {
          const hot = e.charge;
          compose(this.m, e.x, e.y, eZ, e.spin * 0.4, Math.atan2(e.vy, e.vx), 0, 1);
          const blink = 0.4 + 0.6 * Math.pow(Math.max(0, Math.sin(this.time * (2 + hot * 12) + e.phase)), 4);
          this.set(0.24 + flash, 0.20 + flash * 0.4, 0.19, 1, (1.6 + hot * 7) * blink, 0.4 + hot * 0.5, 0.2, 0.85);
          r.queue('seeker', this.m, this.tint, this.emis);
          if (lit) r.addLight(e.x, e.y, 1.2, 14, 1, 0.3, 0.15, 0.4 * blink + hot * 1.4);
          break;
        }

        case 'turret': {
          // The base sits on its mount; only the whole piece rotates to aim.
          compose(this.m, e.x, e.y, eZ, 0, 0, e.aim, 1);
          const hot = e.alert;
          this.set(0.20 + flash, 0.22 + flash * 0.4, 0.23, 1, 0.4 + hot * 6, 0.7 - hot * 0.5, 0.9 - hot * 0.7, 0.9);
          r.queue('turret', this.m, this.tint, this.emis);
          if (lit) r.addLight(
            e.x + Math.cos(e.aim) * 3, e.y + Math.sin(e.aim) * 3, 1, 16,
            hot > 0.5 ? 1 : 0.3, hot > 0.5 ? 0.3 : 0.8, hot > 0.5 ? 0.2 : 1, 0.35 + hot,
          );
          break;
        }

        case 'swarmer': {
          const wag = Math.sin(this.actors.time * e.rate * 6 + e.phase) * 0.12;
          // The mesh points +X, so the heading is expressed the same way the
          // sub's is: yaw picks the side, pitch carries the rest of the angle.
          const local = e.facing > 0 ? e.aim : Math.PI - e.aim;
          compose(
            this.m, e.x, e.y, eZ,
            // Bank into the turn, plus a little idle sway so a drifting shoal
            // still shimmers.
            e.spin * e.facing + Math.sin(this.actors.time * e.rate * 2.2 + e.phase) * 0.12,
            e.facing > 0 ? 0 : Math.PI,
            (local + wag) * e.facing,
            e.size,
          );
          // A shoal is silver first and coloured second — the tints are near
          // neutral on purpose, because what makes a fish read as a fish here
          // is the wet sheen and the backlit translucency, not its hue.
          const base = SHOAL_TINTS[e.group % SHOAL_TINTS.length];
          const vary = 0.88 + ((e.phase * 0.61) % 1) * 0.24;
          const hot = e.charge;
          const sp = e.variant % FISH_SPECIES;
          // Species 1 is the lantern fish — its belly organs carry the glow.
          const lantern = sp === 1 ? 1 : 0;
          this.set(
            base[0] * vary + flash + hot * 0.5,
            base[1] * vary - hot * 0.2,
            base[2] * vary - hot * 0.15,
            1,
            0.16 + hot * 2.2 + lantern * 0.8,
            0.30 + lantern * 1.3,
            0.38 + lantern * 1.2,
            // High spec: scales catch the lamp the way wet things do.
            0.95,
          );
          const fx = FISH_FLEX[sp];
          // `spin` is the damped turn error, so it is already the shape of the
          // arc the body should be holding. Negated because the tail trails
          // behind the turn, and mirrored with the model for a fish facing -X.
          r.queue(
            `fish${sp}`, this.m, this.tint, this.emis,
            [e.phase, e.rate, fx[0], fx[1]],
            [clamp(-e.spin * fx[2] * e.facing, -1.6, 1.6), 0, 0, 0],
          );
          if (lantern && this.propLights < 20) {
            this.propLights++;
            if (lit) r.addLight(e.x, e.y, eZ, 9 * e.size, 0.4, 0.9, 0.85, 0.35);
          }
          break;
        }

        case 'lurker': {
          const open = e.state === 'burst' || e.state === 'rush' ? 1 : 0;
          compose(
            this.m, e.x, e.y, eZ,
            0, e.facing > 0 ? 0 : Math.PI,
            clamp(Math.atan2(e.vy, Math.abs(e.vx) + 0.001) * e.facing, -0.7, 0.7),
            e.size,
          );
          this.set(0.14 + flash, 0.13 + flash * 0.3, 0.16, 1,
            0.1 + open * 1.4 + flash * 2, 0.06, 0.12, 0.35);
          r.queue('lurker', this.m, this.tint, this.emis);
          // The lure is the only thing visible until it commits.
          const lureX = e.x + Math.cos(e.facing > 0 ? 0 : Math.PI) * 2.6 * e.size;
          if (lit) r.addLight(lureX, e.y + 3.2 * e.size, 0, 20 * e.size,
            0.55, 0.9, 0.75, 0.4 + e.charge * 2.4);
          break;
        }

        case 'sentinel': {
          compose(this.m, e.x, e.y, eZ, 0, 0, e.aim, 1);
          const hot = e.alert;
          // The plate lights up when it is actually between you and the core.
          this.set(0.19 + flash, 0.21 + flash * 0.4, 0.24, 1,
            0.4 + hot * 3.5 + e.charge * 2, 0.75 - hot * 0.4, 1.0 - hot * 0.6, 0.95);
          r.queue('sentinel', this.m, this.tint, this.emis);
          if (lit) r.addLight(
            e.x + Math.cos(e.aim) * 3.2, e.y + Math.sin(e.aim) * 3.2, 1, 20,
            hot > 0.5 ? 1 : 0.35, 0.5, hot > 0.5 ? 0.3 : 1, 0.5 + hot,
          );
          break;
        }

        case 'hunter': {
          const bank = clamp(e.vy * 0.04, -0.4, 0.4);
          compose(this.m, e.x, e.y, eZ, 0, e.facing > 0 ? 0 : Math.PI, bank * e.facing, 1);
          const hot = e.state === 'attack' ? 1 : e.state === 'flee' ? 0.4 : e.alert * 0.5;
          this.set(0.18 + flash, 0.20 + flash * 0.4, 0.22, 1,
            0.5 + hot * 5 + e.charge * 2, 0.8 - hot * 0.5, 1.1 - hot * 0.8, 0.95);
          r.queue('hunter', this.m, this.tint, this.emis);
          if (lit) r.addLight(e.x + e.facing * 5, e.y, 1, 22,
            hot > 0.6 ? 1 : 0.3, hot > 0.6 ? 0.25 : 0.8, hot > 0.6 ? 0.2 : 1, 0.5 + hot);
          if (lit) r.addLight(e.x - e.facing * 5.5, e.y, 0, 12, 0.3, 0.7, 1, 0.5);
          break;
        }

        case 'dreadnought': {
          compose(this.m, e.x, e.y, eZ - 2, 0, e.facing > 0 ? 0 : Math.PI, 0, 1);
          const rage = e.podA <= 0 && e.podB <= 0 ? 1 : 0;
          this.set(0.16 + flash, 0.17 + flash * 0.3, 0.19, 1,
            0.5 + rage * 5 + flash * 3, 0.7 - rage * 0.4, 1.0 - rage * 0.7, 1.0);
          r.queue('dread', this.m, this.tint, this.emis);
          if (lit) r.addLight(e.x + e.facing * 20, e.y + 4, 2, 46,
            rage ? 1 : 0.4, rage ? 0.3 : 0.7, rage ? 0.2 : 1, 1.1 + rage);
          if (lit) r.addLight(e.x - e.facing * 22, e.y - 2, 0, 30, 0.3, 0.7, 1, 0.9);
          // Live weapon pods glow; a destroyed one goes dark and smokes.
          for (const [ox, oy, hpv] of [[9, 6.5, e.podA], [-11, -5.5, e.podB]] as const) {
            const px = e.x + ox * e.facing, py = e.y + oy;
            if (hpv > 0) {
              if (lit) r.addLight(px, py, 3, 16, 1, 0.5, 0.25, 0.9 * (hpv / Math.max(1, e.podMax)) + 0.3);
            } else if (Math.random() < 0.4) {
              this.renderer.particles.bubbles(px, py, 1, 1.4, 2.5, 0.3);
            }
          }
          break;
        }

        default: {
          const bank = clamp(e.vy * 0.05, -0.4, 0.4);
          compose(this.m, e.x, e.y, eZ, e.spin * 0.2, e.facing > 0 ? 0 : Math.PI, bank * e.facing, 1.15);
          const hot = e.state === 'attack' ? 1 : e.state === 'investigate' ? 0.5 : 0.12;
          this.set(0.20 + flash, 0.22 + flash * 0.4, 0.24, 1, 0.6 + hot * 5.5, 0.9 - hot * 0.7, 1.1 - hot * 0.9, 0.9);
          r.queue('drone', this.m, this.tint, this.emis);
          if (lit) r.addLight(
            e.x + e.facing * 1.6, e.y, 1, 16,
            hot > 0.6 ? 1 : 0.3, hot > 0.6 ? 0.25 : 0.8, hot > 0.6 ? 0.2 : 1, 0.5 + hot,
          );
          break;
        }
      }
    }

    for (const pr of this.actors.projectiles) {
      if (!pr.alive || !onScreen(pr.x, pr.y)) continue;
      if (pr.kind === 'bullet') {
        const friendly = pr.from === 'player';
        compose(this.m, pr.x, pr.y, pr.z, 0, 0, pr.angle, 1.4, 1, 1);
        this.set(
          0.9, 0.95, 1, 1,
          friendly ? 1.6 : 6.0, friendly ? 4.5 : 1.1, friendly ? 6.0 : 0.5, 0,
        );
        r.queue('bullet', this.m, this.tint, this.emis);
        // A ROUND IN FLIGHT LIGHTS THE ROOM IT IS CROSSING.
        //
        // Incoming fire always gets a light; the player's own volley is capped,
        // because at a 0.14 s cooldown it is the one thing that can flood the
        // budget on its own.
        //
        // The radius was 9 m and the intensity 0.9, which lit about as far as
        // the bolt's own emissive already reached — so it cost a light slot and
        // showed nothing. 18 m and 2.4 puts a moving pool of light on the walls
        // either side, which is the whole point of a tracer in a dark place and
        // is worth most in the ruins, where there is architecture to catch it.
        if (!friendly || this.shotLights < 10) {
          if (friendly) this.shotLights++;
          r.addLight(
            pr.x, pr.y, pr.z, friendly ? 18 : 12,
            friendly ? 0.30 : 1, friendly ? 0.80 : 0.3, friendly ? 1 : 0.2,
            friendly ? 2.4 : 1.1,
          );
        }
      } else if (pr.kind === 'charge') {
        compose(this.m, pr.x, pr.y, pr.z, this.time * 5, 0, 0, 1.1);
        const blink = Math.pow(Math.max(0, Math.sin(pr.life * 14)), 4);
        this.set(0.26, 0.24, 0.22, 1, 1.2 + blink * 8, 0.5, 0.2, 0.9);
        r.queue('mine', this.m, this.tint, this.emis);
        r.addLight(pr.x, pr.y, pr.z, 13, 1, 0.45, 0.2, 0.5 + blink * 1.6);
      } else {
        const hostile = pr.from === 'enemy';
        compose(this.m, pr.x, pr.y, pr.z, this.time * 12, 0, pr.angle, 1);
        this.set(0.30, 0.31, 0.33, 1,
          hostile ? 3.6 : 1.2, hostile ? 0.9 : 2.6, hostile ? 0.5 : 3.4, 1.1);
        r.queue('torpedo', this.m, this.tint, this.emis);
        r.addLight(pr.x, pr.y, pr.z, 13, hostile ? 1 : 0.4, hostile ? 0.4 : 0.8, hostile ? 0.2 : 1, 0.8);
      }
    }

    this.budgetPickupLights(onScreen);
    const pcx = this.camera.viewX(), pcy = this.camera.viewY();

    for (const pk of this.actors.pickups) {
      // Cargo is drawn by `drawCargo`, which is also where the promise that it
      // takes no dynamic light is kept. Without this guard a crate rendered
      // twice — a glowing canister embedded inside the container — and the
      // canister half claimed a light out of the boss's allowance.
      if (!pk.alive || pk.mass > 0 || !pk.revealed || !onScreen(pk.x, pk.y)) continue;
      const def = PICKUP_DEF[pk.type];
      const bob = Math.sin(pk.phase * 1.3) * 0.5;
      // Loot fades out as it expires, so nothing vanishes without warning.
      const fade = pk.life === Infinity ? 1 : clamp(pk.life / 4, 0.15, 1);
      const pop = 0.5 + pk.born * 0.5;
      // IN ITS OWN LANE, AT ITS OWN DEPTH. This drew every piece of loot at
      // z = 0 — the play plane — and never called `useLane`, so a canister
      // belonging to the second lane was rendered at the player's own depth, at
      // full brightness, with no defocus and no distance tint. It looked
      // exactly like something that could be flown into, because it was being
      // drawn as something that could be flown into. The cargo loop twenty
      // lines below has always done both of these correctly.
      //
      // That is the whole of "orphaned objects I cannot pick up". They are not
      // orphaned: they are in the next lane along, and every cue that would
      // have said so was being skipped.
      this.useLane(pk.lane);
      compose(this.m, pk.x, pk.y + bob, laneZ(pk.lane), 0, pk.phase * 0.9, 0.16, pop);
      const g = 1.6 + Math.sin(pk.phase * 3) * 0.4;
      this.set(
        0.32, 0.34, 0.33, 1,
        def.color[0] * g * fade, def.color[1] * g * fade, def.color[2] * g * fade, 1.0,
      );
      r.queue(pk.id === '' ? 'canister' : 'pickup', this.m, this.tint, this.emis);
      this.useLane(-1);
      // ONE LIGHT PER CANISTER WAS THE ONLY UNBUDGETED CATEGORY IN THE FRAME.
      //
      // Enemies are capped at 12, scenery at 22, shots have their own
      // allowance — and pickups had none, while Survival drops several times
      // what the campaign does and they sit on the floor until collected.
      // Telemetry caught it exactly: with ZERO enemies alive and ZERO shots in
      // the water, 48 of 48 lights in use and three dropped, at 36 fps. Nothing
      // was happening; the floor was just covered in canisters, each holding a
      // 15 m light. Collecting them gave the frame rate back, which is the
      // clearest possible statement of what was wrong.
      //
      // The emissive is untouched and it is bright — 1.6 to 2.0 times the
      // pickup's own colour at full alpha — so every canister still glows and
      // is still findable in the dark. What the nearest handful keep is the
      // ability to light the ground they are lying on.
      if ((pk.x - pcx) * (pk.x - pcx) + (pk.y - pcy) * (pk.y - pcy) <= this.litPickupCutoff) {
        // At the loot's own depth, like everything else about it. A light
        // fixed at z = 0 for a canister two lanes back would have lit the play
        // plane instead of the ground the thing is actually lying on.
        r.addLight(pk.x, pk.y + bob, laneZ(pk.lane), 15,
          def.color[0], def.color[1], def.color[2], 0.8 * fade);
      }
    }

    this.drawCargo(onScreen);
  }

  /**
   * Crates and the tow cable.
   *
   * Neither takes a dynamic light. The renderer keeps 48 and silently drops the
   * rest, `drawPlayer` runs first by deliberate design, and a crate that
   * out-ranked the boss's lighting would be a bad trade at any weight.
   */
  private drawCargo(onScreen: (x: number, y: number, pad?: number) => boolean) {
    this.useLane(-1);
    // The line and its hook hang in the sub's own water.
    const ropeZ = this.player.z;
    const r = this.renderer;

    for (const pk of this.actors.pickups) {
      if (!pk.alive || pk.mass <= 0 || !pk.revealed || !onScreen(pk.x, pk.y)) continue;
      // Bigger loads are bigger objects. Cube-root, so a 4 t container is not
      // four times the size of a 1 t one.
      // Sized so weight READS. Cube-root alone turned a 13x mass spread into
      // 2.3x of scale, which is no spread at all — the heaviest object in the
      // world looked like the lightest. Half the cube root, half linear.
      const s = 0.62 * Math.cbrt(pk.mass) + 0.30 * pk.mass;
      // Towed cargo hangs off the line and swings; a crate on the seabed just
      // sits there. The tilt comes from the velocity, which costs nothing and
      // is the only thing that stops it reading as a static prop.
      const lean = pk.hold === 'towed' ? Math.atan2(pk.vy, pk.vx || 1) * 0.18 : 0;
      this.useLane(pk.lane);
      compose(this.m, pk.x, pk.y, laneZ(pk.lane), 0, pk.phase * 0.15, lean, s);
      // A RECOVERY BEACON, because a dark grey box in dark water is not findable.
      //
      // This was drawn with a flat grey tint and zero emissive, on the stated
      // and correct reasoning that a crate must not take one of the 48 dynamic
      // lights. All true — and it left the object the whole cargo system exists
      // for invisible until you were on top of it, which is the complaint.
      //
      // Emissive is not a light. It is per-instance data already being sent,
      // costs nothing, lights nothing else, and cannot crowd the boss out of the
      // light budget. So the crate carries its own strobe: a slow amber pulse
      // that says "salvage" from across a dark room and never touches the thing
      // the original comment was protecting.
      const beat = 0.5 + 0.5 * Math.sin(this.time * 2.2 + pk.phase);
      const strobe = 0.35 + beat * beat * 2.6;
      this.set(0.34, 0.33, 0.30, 1, strobe, strobe * 0.55, strobe * 0.16, 0.55);
      r.queue(CARGO_MESH[cargoShape(pk.label)], this.m, this.tint, this.emis);
    }

    const tow = this.tow;
    if (tow.state === 'idle') return;

    const p = this.player;
    // WHERE THE CHAIN IS DRAWN FROM IS NOT WHERE IT PULLS FROM.
    //
    // `fairlead` is the physics anchor and it sits at 1.15 hull radii below the
    // keel — deliberately OUTSIDE the boat, so the tow torque has a real moment
    // arm. Drawing from that same point starts the first link in open water
    // with a visible gap between the chain and the belly it is supposedly
    // bolted to, which is the "no connection" complaint.
    //
    // So the drawn line starts INSIDE the hull instead, on the same bearing.
    // The top links are then swallowed by the hull's own depth — they are at
    // z = 0, the centreline, and the belly plating is nearer the camera — so
    // the chain visibly disappears into the boat rather than hanging off it.
    // Nothing about the simulation moves; only the first vertex does.
    p.fairlead(this.muzzlePos);
    const fx = this.muzzlePos.x, fy = this.muzzlePos.y;
    // The physics anchor is at 1.15 hull radii; draw from 0.30, well inside.
    const INSIDE = 0.30 / 1.15;
    let ax = p.x + (fx - p.x) * INSIDE;
    let ay = p.y + (fy - p.y) * INSIDE;

    // ON THE MK II THE LINE HANGS OFF THE CLAW.
    //
    // The paragraph above solves "the chain is not attached to anything" by
    // starting it inside the hull. With a manipulator out, that solves the
    // wrong problem: the chain emerges from the belly while the claw hangs a
    // couple of metres below it, so the boat appears to have TWO grabbers —
    // an arm doing nothing and a rope doing the work. Hanging the line off the
    // grip makes them one mechanism, the arm reaches and the winch takes up
    // what it caught.
    //
    // Blended by `armDeploy` so the start walks out with the arm instead of
    // jumping to it, and as before NOTHING about the simulation moves: the
    // physics anchor is still the fairlead. Only the first vertex differs.
    const armed = this.subParts !== null && p.chassis.mesh === 'sub' && this.armDeploy > 0.01;
    if (armed) {
      ax += (this.clawTip.x - ax) * this.armDeploy;
      ay += (this.clawTip.y - ay) * this.armDeploy;
    }
    const bx = tow.state === 'flying' ? tow.hx : tow.load!.x;
    const by = tow.state === 'flying' ? tow.hy : tow.load!.y;
    if (!onScreen((ax + bx) * 0.5, (ay + by) * 0.5, 40)) return;

    // A taut line is straight; a slack one sags. The sag is derived from how
    // much line is out versus how far apart the ends are, so the cable *shows*
    // the strain the HUD bar reports — and a winched-tight tow visibly pulls
    // into a straight line as it comes in.
    //
    // The OUTGOING hook sags too, and used not to. It is a weight on a chain —
    // `driveTow` already arcs it downwards under `GRAPPLE.sink` — but the cable
    // behind it was drawn dead straight to wherever it had got to, so a heavy
    // chain read as a laser sight. The line paid out during flight is the
    // distance the hook has travelled, and the slack is whatever that exceeds
    // the straight-line distance home.
    const span = hyp(bx - ax, by - ay);
    const out = tow.state === 'attached' ? tow.tether : tow.travelled;
    const sag = Math.max(0, Math.min(out - span, out * 0.5)) * 0.5;
    const LINKS = 16;
    const st = tow.strain;
    // Reddens and thins as the line comes under strain, so the warning is on
    // the cable itself and not only in a corner of the screen.
    const thick = 0.30 - st * 0.06;

    // WHICH WAY SLACK BULGES, AND HOW MUCH.
    //
    // Straight down is right only while the chain is roughly horizontal. Hang a
    // crate directly below the boat — the normal resting state, since towed
    // cargo sinks — and a downward bulge on a vertical chord sends the chain
    // looping below the crate and back up to it.
    //
    // So the bow is PERPENDICULAR to the chord, on the lower side. But the
    // direction alone is not enough: rotating a full-size bulge onto a vertical
    // chord swings the whole chain out sideways, which is the strange angle on
    // a line that should simply drop. The magnitude has to fall away too, and
    // `|ux|` does both jobs at once — full sag across a horizontal chord, none
    // at all down a vertical one, which is also the physical truth. A chain
    // hanging straight down under a heavy load has nowhere to sag TO.
    const ux = (bx - ax) / (span || 1);
    let sx = (by - ay) / (span || 1), sy = -ux;
    if (sy > 0) { sx = -sx; sy = -sy; }
    const bowScale = Math.abs(ux);

    let px = ax, py = ay;
    let lastAng = 0;
    for (let i = 1; i <= LINKS; i++) {
      const t = i / LINKS;
      const bow = Math.sin(t * Math.PI) * sag * bowScale;
      const nx = ax + (bx - ax) * t + sx * bow;
      const ny = ay + (by - ay) * t + sy * bow;
      const len = hyp(nx - px, ny - py);
      lastAng = Math.atan2(ny - py, nx - px);
      compose(this.m, (px + nx) * 0.5, (py + ny) * 0.5, ropeZ, 0, 0, lastAng, Math.max(0.2, len * 1.06), thick, thick);
      // Brighter than the hull it hangs off, and carrying a little emissive of
      // its own. A cable is a thin object seen against open water at depth, so
      // at the hull's own albedo it reads as a black thread — it needs to be
      // legible in a dark room without a light of its own, because it takes no
      // dynamic light by design.
      this.set(0.52 + st * 0.42, 0.50 - st * 0.20, 0.46 - st * 0.24,
        1, 0.05, 0.05, 0.045, 0.2);
      r.queue('cable', this.m, this.tint, this.emis);
      px = nx; py = ny;
    }

    // The hook, hanging point-down off the end of the line while it is going
    // out; once something is caught it is buried in the crate and not drawn.
    //
    // NOT DRAWN AT ALL on the MK II, which has an actual manipulator. Two
    // grabbers on one boat is one too many: the arm swings out of the belly on
    // the same keypress and does the same job, so a second steel hook on the
    // end of the line reads as the arm having failed to be the thing that
    // grabs. The line stays — a salvage boat winches a load up, it does not
    // carry it in the claw — but what is on the end of it is the claw now.
    const clawed = this.subParts !== null && this.player.chassis.mesh === 'sub';
    if (tow.state === 'flying' && !clawed) {
      compose(this.m, bx, by, ropeZ, 0, this.time * 1.4, lastAng + Math.PI * 0.5, 1);
      this.set(0.58, 0.56, 0.50, 1, 0.06, 0.06, 0.05, 0.2);
      r.queue('hook', this.m, this.tint, this.emis);
    }
  }

  private drawOctopus(onScreen: (x: number, y: number, pad?: number) => boolean) {
    this.useLane(-1);
    const o = this.octopus;
    if (!o.awake) return;
    const r = this.renderer;
    const dying = o.state === 'dead';

    for (const t of o.tentacles) {
      const dead = t.hp <= 0;
      for (let i = 1; i <= t.seg; i++) {
        const x0 = t.px[i - 1], y0 = t.py[i - 1];
        const x1 = t.px[i], y1 = t.py[i];
        if (!onScreen((x0 + x1) * 0.5, (y0 + y1) * 0.5, 40)) continue;
        const len = hyp(x1 - x0, y1 - y0);
        const ang = Math.atan2(y1 - y0, x1 - x0);
        const k = i / t.seg;
        const rad = 2.6 * (1 - k * 0.62) + 0.35;
        compose(
          this.m, (x0 + x1) * 0.5, (y0 + y1) * 0.5, -2 + Math.sin(k * 3 + t.phase) * 1.5,
          0, 0, ang, Math.max(0.4, len), rad, rad,
        );
        const f = t.hitFlash;
        const base = dead || dying ? 0.16 : 0.30;
        // Faint bioluminescent chromatophores running down the limb — the only
        // reason the creature is legible at 500 m with the lamp pointed away.
        const pulse = 0.5 + 0.5 * Math.sin(this.time * 2.2 - k * 5.5 + t.phase);
        const bio = (dead ? 0.06 : 0.20) * (0.35 + pulse * 0.65) * (0.4 + k * 0.6);
        this.set(
          base + f * 0.9, base * 0.42 + f * 0.2, base * 0.5 + f * 0.2, 1,
          bio * 1.1 + f * 2.5, bio * 0.5, bio * 1.5, 0.25,
        );
        r.queue('tentacle', this.m, this.tint, this.emis);
        if (i === t.seg && !dead) {
          r.addLight(x1, y1, -2, 16, 0.55, 0.25, 0.9, bio * 1.6);
        }
      }
    }

    if (onScreen(o.x, o.y, 60)) {
      compose(
        this.m, o.x, o.y, -3, 0, o.facing > 0 ? 0 : Math.PI,
        Math.sin(o.time * 0.6) * 0.08, o.bodyRadius * 0.78,
      );
      const f = o.hitFlash;
      const glow = dying ? 0.05 : o.arousal;
      this.set(0.34 + f, 0.15 + f * 0.4, 0.19 + f * 0.3, 1, 4.0 * glow, 0.5 * glow, 0.25 * glow, 0.3);
      r.queue('octohead', this.m, this.tint, this.emis);
      r.addLight(o.x + o.facing * 4, o.y + 3, 2, 26, 1.0, 0.28, 0.16, 0.7 * glow);
    }
  }

  /**
   * Lane shading applied to every actor drawn through `set`.
   *
   * A creature in another lane was drawn exactly as brightly and as sharply as
   * one you can actually shoot, so the two were indistinguishable — you aim at
   * it, nothing happens, and the game looks broken. It is not a logic bug: the
   * gating is right and has been for a while. It is that nothing on screen ever
   * said which water the thing was in.
   *
   * The terrain has always said it, with the layer tints — dimmer and bluer
   * with distance, which is what depth does underwater. Actors borrow the same
   * language, so "I cannot shoot that" and "that is far away" become the same
   * observation. Defocus would say it too but cannot be used: the translucent
   * pipeline forces that channel to zero, and jellyfish are translucent.
   */
  /** Coral's bioluminescence, in the channel `o.bio` reads. */
  private static readonly CORAL_BIO: [number, number, number, number] = [0, 0.5, 0, 0];

  /**
   * Where the refit screen wants the boat shown, as FRACTIONS of the viewport.
   *
   * Fractions rather than pixels because the HUD measures in CSS units and the
   * renderer in device ones, and the two do not agree on any machine with a
   * scaled display. A fraction is the same number in both.
   *
   * Null whenever the shop is shut.
   */
  showcase: { u: number; v: number; wf: number; hf: number } | null = null;
  /**
   * An ORE index to display on the turntable instead of the boat, or null.
   *
   * A separate channel from `showcase` rather than a union, because the two
   * answer different questions — WHERE the panel is, and WHAT is standing in
   * it. The rect is set by the rail on every screen that has one; only the
   * hold screen has an opinion about the second.
   */
  showcaseOre: number | null = null;
  private showAngle = 0;

  /**
   * Turn the display hull.
   *
   * Driven from the frame loop rather than from `this.time`, because opening a
   * screen suspends `update` — so the world clock stops the moment the refit
   * appears, and a turntable keyed to it would present a model frozen at
   * whatever angle the player happened to dock at.
   */
  spinShowcase(dt: number) {
    if (this.showcase) this.showAngle += dt * 0.55;
  }
  private aspect = 1.777;

  private laneMod: [number, number, number, number] = [1, 1, 1, 1];
  /** Play-layer weight for the current lane: 1 in the player's, less outside it. */
  private laneFocus = 1;

  /** Shade everything drawn after this as belonging to `lane`. */
  private useLane(lane: number) {
    // NOT `Math.max(0, ...)`. The sub's lane is -1 in the open water between
    // slabs, and clamping that to 0 told the cue the boat was in the near lane
    // when it was in no lane at all — so every lane-0 creature was drawn at
    // full brightness through a window in which nothing at all can be hit.
    // That is the precise failure this whole mechanism exists to prevent, in
    // the one state where the player most needs the answer.
    const here = this.player.lane;
    // A negative `lane` is the other meaning: "this is not a thing that lives
    // in a lane" — the gates, effects, the boat itself — and always draws plain.
    if (lane < 0 || lane === here) {
      this.laneMod[0] = 1; this.laneMod[1] = 1; this.laneMod[2] = 1; this.laneMod[3] = 1;
      this.laneFocus = 1;
      return;
    }
    // INDEXED BY DISTANCE FROM THE PLAYER, NOT BY ABSOLUTE DEPTH.
    //
    // The layer tints are authored as "how a background layer looks from lane
    // 0", and lane 0's own entry is therefore [1, 1, 1] — the identity. Read
    // absolutely, that meant a lane-0 creature seen from lane 1 or 2 was
    // multiplied by one: no dim, no cue, drawn exactly as brightly as something
    // the player could actually shoot, and closer to the camera so BIGGER. That
    // is the whole complaint about untargetable things appearing to be in the
    // way, and the mechanism built to prevent it was silently inert for two of
    // the three lanes the player can fly.
    //
    // The cue is about reachability, which is relative: one lane away is one
    // lane away whichever direction it lies in. So the table is indexed by the
    // GAP. From lane 0 that is the identity mapping and nothing changes, which
    // is why this went unnoticed — lane 0 is where most of the game is played.
    //
    // A lane in FRONT gets the same treatment as one behind, deliberately. It
    // is just as unreachable, and being nearer the camera it is the one that
    // fills the screen.
    const gap = here < 0 ? Math.max(1, lane) : Math.abs(lane - here);
    const near = LAYERS[Math.max(0, Math.min(LAYERS.length - 1, gap))]!;
    const t = near.tint;
    // AND THE SAME DEFOCUS ITS OWN GROUND IS DRAWN WITH.
    //
    // `drawScenery` has always defocused props by layer while actors were
    // pinned sharp, so a lane-2 creature stood in perfect focus against the
    // lane-2 rock it was floating over. One of the two has to be lying, and it
    // was the one the player was trying to shoot.
    //
    // Opaque actors can carry it: `tint.a` is the play-layer weight the depth
    // of field reads, and nothing blends against the scene target so it is not
    // being used as opacity. Jellyfish are the exception — they are translucent
    // and that channel is their alpha — but they take the colour cue above,
    // which is the half that works with depth of field switched off anyway.
    this.laneFocus = 1 - near.defocus * 0.8;
    // ONE APPLICATION, NOT SQUARED.
    //
    // Squaring was right while the tint was the only cue there was: it takes
    // lane 1 to [0.21, 0.30, 0.44], which is unmistakable. It is also most of
    // the way to black, and a black shape against lit water does not read as
    // "far away" — it reads as a hole, or as something switched off. Real
    // distance washes a thing toward the colour of the water between you, and
    // the fog already does that part correctly, by metres.
    //
    // There is a second cue now — the defocus below — so the colour no longer
    // has to carry the whole message on its own, and it can afford to say
    // "dimmer" instead of "gone".
    this.laneMod[0] = t[0];
    this.laneMod[1] = t[1];
    this.laneMod[2] = t[2];
    this.laneMod[3] = t[1] * 0.45;
  }

  private set(
    // `ta` is not opacity: nothing blends against the scene target. It is how
    // much this instance belongs to the play layer, which is what depth of
    // field reads. Everything drawn through here is an actor, so it is 1.
    tr: number, tg: number, tb: number, ta: number,
    er: number, eg: number, eb: number, ea: number,
  ) {
    const m = this.laneMod;
    this.tint[0] = tr * m[0]; this.tint[1] = tg * m[1]; this.tint[2] = tb * m[2];
    this.tint[3] = ta * this.laneFocus;
    this.emis[0] = er * m[3]; this.emis[1] = eg * m[3]; this.emis[2] = eb * m[3];
    this.emis[3] = ea;
  }

  // ==========================================================================
  // queries for the HUD
  // ==========================================================================

  visibleChunks() {
    const ext = this.camera.halfExtent();
    const cx = this.camera.viewX();
    return this.terrain.visible(cx - ext.w - 40, cx + ext.w + 40);
  }

  /** Terrain profile for the HUD sonar strip (PRD §14). */
  sonarProfile(samples: number, span: number, out: Float32Array): Float32Array {
    const L = LAYERS[Math.max(0, this.player.lane)]!;
    for (let i = 0; i < samples; i++) {
      const x = this.player.x + (i / (samples - 1) - 0.5) * span;
      // Drawn y of that lane's seabed — the profile is a picture of the ground
      // under the boat, not of the ground under lane 0.
      out[i] = seabedY(x + L.warp) + layerLift(x, L);
    }
    return out;
  }

  /** World → normalised screen (0..1, y down). False if behind the camera. */
  /**
   * `z` defaults to the play plane, which is where everything that asked for
   * this before happens to live. An ore deposit does not: it sits anywhere
   * from -7 to +1.5 and further on a background lane, and at sixty metres of
   * camera standoff that is hundreds of pixels of disagreement between where
   * the label thinks it is and where the rock is drawn.
   */
  project(x: number, y: number, out: { x: number; y: number }, z = 0): boolean {
    const vp = this.camera.viewProj;
    const w = vp[3] * x + vp[7] * y + vp[11] * z + vp[15];
    if (w <= 0.0001) return false;
    out.x = ((vp[0] * x + vp[4] * y + vp[8] * z + vp[12]) / w) * 0.5 + 0.5;
    out.y = 0.5 - ((vp[1] * x + vp[5] * y + vp[9] * z + vp[13]) / w) * 0.5;
    return true;
  }

  /**
   * The boss the HUD should show a bar for, if any. Both this and
   * `nextObjective` fill a record they own: the HUD calls them once a frame on
   * the play path, and a fresh object per frame is garbage the game does not
   * need to make.
   */
  private bossRec = { name: '', health: 0, sub: '' };

  /**
   * Where the boss is, for an off-screen marker. Null when there is no bar.
   *
   * A health bar with no way to find the thing it belongs to is worse than no
   * bar: it says something is happening and refuses to say where.
   */
  bossMarker(): { x: number; y: number } | null {
    const o = this.octopus;
    if (o.awake && o.state !== 'dead' && this.bossNear(o.x, o.y)) return o;
    const e = this.actors.activeBoss();
    return e ?? null;
  }

  /**
   * Close enough for the contact to be worth a bar. The octopus stays awake
   * while it swims home to sleep, and during those seconds it is behind you and
   * irrelevant; `BOSS_BAR_RANGE` is a little beyond the range at which it gives
   * up, so the bar goes out at roughly the moment the encounter is over.
   */
  private bossNear(x: number, y: number): boolean {
    return hyp(x - this.player.x, y - this.player.y) < 330;
  }

  bossState(): { name: string; health: number; sub: string } | null {
    const r = this.bossRec;
    const o = this.octopus;
    if (o.awake && o.state !== 'dead' && this.bossNear(o.x, o.y)) {
      r.name = 'UNIDENTIFIED BIOLOGICAL CONTACT';
      r.health = clamp(o.hp / o.maxHp, 0, 1);
      r.sub = '';
      return r;
    }
    const e = this.actors.activeBoss();
    if (e) {
      const pods = (e.podA > 0 ? 1 : 0) + (e.podB > 0 ? 1 : 0);
      r.name = 'CAPITAL SUBMARINE — HOSTILE';
      r.health = clamp(e.hp / e.maxHp, 0, 1);
      r.sub = pods ? `${pods} WEAPON POD${pods > 1 ? 'S' : ''} ACTIVE` : 'WEAPONS DESTROYED — IT IS CHARGING';
      return r;
    }
    return null;
  }

  get bossBannerTime() {
    return this.bossBanner;
  }

  get bossBannerLabel() {
    return this.bossBannerText;
  }

  /** The next station the player has not reached, for the objective marker. */
  private objRec = { x: 0, y: 0, label: '' };

  nextObjective(): { x: number; y: number; label: string } | null {
    // A mode that has its own idea of "where next" says so. The campaign's
    // answer — the next station you have not switched on — is meaningless in a
    // mode whose whole content is a job somewhere specific.
    const own = this.rules.objective?.(this);
    if (own) return own;
    const p = this.player;
    let best: typeof STATIONS[number] | null = null;
    let bestD = Infinity;
    for (const s of STATIONS) {
      if (this.activatedStations.has(s.id)) continue;
      if (s.x < p.x - 60) continue;
      const d = s.x - p.x;
      if (d < bestD) { bestD = d; best = s; }
    }
    if (!best) return null;
    this.objRec.x = best.x;
    this.objRec.y = best.y;
    this.objRec.label = best.name;
    return this.objRec;
  }

  cameraViewHeight() {
    return CAMERA.viewHeight * this.camera.viewZoom();
  }

  /** Fraction of the world traversed, for the map strip. */
  get progress() {
    return clamp(this.player.x / WORLD.endX, 0, 1);
  }

  get lootLife() {
    return LOOT.life;
  }

  get nitroFrac() {
    return clamp(this.player.nitro / this.player.stats.nitroMax, 0, 1);
  }

  get nitroReady() {
    return this.player.nitro > NITRO.minToStart;
  }
}
